import NiceModal from '@ebay/nice-modal-react';
import dayjs from 'dayjs';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import ConfirmationModal from 'components/global/ConfirmationModal';
import { FilterAction, FilterType } from 'components/list/DynamicListFilterUtils';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import useSwitchSupportClient from 'hooks/useSwitchSupportClient';

import ReturnRequestUrlModal from 'modals/ReturnRequestUrlModal';

import ChangeMultipleModuleModal from 'pages/Admin/AccessControl/ChangeMultipleModuleModal';
import ChangeMultipleRoleModal from 'pages/Admin/AccessControl/ChangeMultipleRoleModal';
import ClientForm from 'pages/Admin/ClientListing/ClientForm';
import CountriesManagementForm from 'pages/Admin/CountriesManagement/CountriesManagementForm';
import AddDeliveryCompanySettingsForm from 'pages/Admin/DeliveryCompanyModule/DeliveryCompanySettings/AddDeliveryCompanySettingsForm';
import PolygonCoverageForm from 'pages/Admin/DeliveryCompanyModule/PolygonCoverage/PolygonCoverageForm';
import EnumManagementModal from 'pages/Admin/EnumManagement/EnumModal';
import HomePageAnnouncementForm from 'pages/Admin/HomePageAnnouncementsManagement/HomePageAnnouncementForm';
import LanguageResourceManagementModal from 'pages/Admin/LanguageResourceManagement/LanguageResourceManagementModal';
import AddModuleModal from 'pages/Admin/Modules/AddModuleModal';
import OTOAIClientConfigurationForm from 'pages/Admin/OTOAIClientConfigurations/OTOAIClientConfigurationForm';
import OtherIntegrationModal from 'pages/Admin/OtherIntegrations/OtherIntegrationModal';
import AddPackageModal from 'pages/Admin/Packages/AddPackageModal';
import SalesChannelForm from 'pages/Admin/SalesChannels/SalesChannelForm';
import StoryManagementModal from 'pages/Admin/StoryManagement/StoryManagementModal';
import EditSubscriptionInvoiceModal from 'pages/Admin/Subcriptions/SubscriptionInvoiceModal';
import AssingToDriverForm from 'pages/BranchUser/ActionForms/AssignToDriverForm';
import BranchOTPForm from 'pages/Branches/BranchOTPForm';
import UpdateRadiusForm from 'pages/Branches/UpdateRadiusForm';
import ViewReturnDetails from 'pages/CSModule/CustomerService/FailedDeliveryOrders/ViewReturnDetails';
import BankTransferModal from 'pages/FinanceUser/BankTransfer/BankTransferModal';
import AddBankModal from 'pages/FinanceUser/Banks/AddBankModal';
import CODReportModal from 'pages/FinanceUser/CODPayable/CODPayable/CODReportModal';
import UpdateCODStatusModal from 'pages/FinanceUser/CODPayable/CODPayable/UpdateCODStatusModal';
import CODReceivableModal from 'pages/FinanceUser/CODReceivables/CODReceivableModal';
import PaymentSlipModal from 'pages/FinanceUser/CODReceivables/PaymentSlipModal';
import EditClientTransactionInvoiceModal from 'pages/FinanceUser/ClientTransactions/EditClientTransactionInvoiceModal';
import ExtraCreditForEnterpriseForm from 'pages/FinanceUser/ExtraCredits/ExtraCreditForEnterpriseForm';
import UpdateStatusModal from 'pages/FinanceUser/InvoiceLogs/UpdateStatusModal';
import AddTransactionForm from 'pages/OTOFlexMain/Drivers/AddTransactionForm';
import ViewTransactionHistory from 'pages/OTOFlexMain/Drivers/ViewTransactionHistory';
import ViewPOD from 'pages/OTOFlexMain/HistoryOrders/ViewPOD';
import CreateOtoLink from 'pages/OTOLink/CreateOTOLink/CreateOtoLink';
import BulletDistrictSLAForm from 'pages/PriceConfigurations/BulletDistrictSLA/BulletDistrictSLAForm';
import BulletKMSLAForm from 'pages/PriceConfigurations/BulletKMSLA/BulletKMSLAForm';
import CityBasedPriceForm from 'pages/PriceConfigurations/CityBasedPrices/CityBasedPriceForm';
import CityBasedSLAForm from 'pages/PriceConfigurations/CityBasedSLA/CityBasedSLAForm';
import CountryBasedSLAForm from 'pages/PriceConfigurations/CountryBasedSLA/CountryBasedSLAForm';
import DCWorkingHourForm from 'pages/PriceConfigurations/DCWorkingHours/DCWorkingHourForm';
import DistrictBasedSLAForm from 'pages/PriceConfigurations/DistrictBasedSLA/DistrictBasedSLAForm';
import KMBasedPriceForm from 'pages/PriceConfigurations/KMBasedPrices/KMBasedPriceForm';
import KMBasedSLAForm from 'pages/PriceConfigurations/KMBasedSLA/KMBasedSLAForm';
import OTODeliveryPriceForm from 'pages/PriceConfigurations/OTODeliveryPrices/OTODeliveryPriceForm';
import OTOMarginForm from 'pages/PriceConfigurations/OTOMargin/OTOMarginForm';
import ChargeCorrectionCreditCardModal from 'pages/PriceConfigurations/Reconciliation/ChargeCorrectionCreditCardModal';
import ChargeCorrectionModal from 'pages/PriceConfigurations/Reconciliation/ChargeCorrectionModal';
import ShippingContractForm from 'pages/PriceConfigurations/ShippingContracts/ShippingContractForm';
import ShippingMigrateConfigurationModal from 'pages/PriceConfigurations/ShippingContracts/ShippingMigrateConfigurationModal';
import SpecialContractTermForm from 'pages/PriceConfigurations/SpecialContractTerms/SpecialContractTermForm';
import SuccessInstructionsForm from 'pages/PriceConfigurations/SuccessInstructions/SuccessInstructionsForm';
import TierBasedPriceForm from 'pages/PriceConfigurations/TierBasedPrices/TierBasedPriceForm';
import TierBasedSLAForm from 'pages/PriceConfigurations/TierBasedSLA/TierBasedSLAForm';
import WeightBasedPriceForm from 'pages/PriceConfigurations/WeightBasedPrices/WeightBasedPriceForm';
import WeightCountryBasedPriceForm from 'pages/PriceConfigurations/WeightCountryBasedPrices/WeightCountryBasedPriceForm';
import AttributesGroupAssignForm from 'pages/Products/AttributesGroups/AttributesGroupAssignForm';
import BarcodeGeneratorModal from 'pages/Products/BarcodeGeneratorForm';
import CategoryForm from 'pages/Products/Categories/CategoryForm';
import UpdateProductCategory from 'pages/Products/Categories/UpdateProductCategory';
import PaidCODDetails from 'pages/Reports/ShipmentReports/PaidCODReports/PaidCODDetailsModal';
import { fetchSallaStores } from 'pages/SalesChannels/ConnectedStores/Salla.slice';
import useSallaSync from 'pages/SalesChannels/useSallaSync';
import AddSenderLocationForm from 'pages/SenderLocations/AddSenderLocationForm';
import SendTestEmailModal from 'pages/Settings/Email/SendTestEmailModal';
import RecordHistory from 'pages/Settings/Invoices/InvoiceDetails/RecordHistoryForm';
import AddPriceManuallyModal from 'pages/Settings/Invoices/PriceNotFoundOrders/AddPriceManuallyModal';
import CODDetailsModal from 'pages/Settings/Payment/CODWallet/CODWalletList/Modals/CODDetailsModal';
import RevertCreditForm from 'pages/Settings/Payment/Transactions/CreditTransactions/RevertCreditForm';
import SendTestMessageForm from 'pages/Settings/Whatsapp/forms/SendTestMessageForm';
import AddSerialNumber from 'pages/Shipments/ActionForms/AddSerialNumberForm';
import AssignPickerOrder from 'pages/Shipments/ActionForms/AssignPickerOrder';
import CancelOTOLinkOrderForm from 'pages/Shipments/ActionForms/CancelOTOLinkOrderForm';
import CancelOrderForm from 'pages/Shipments/ActionForms/CancelOrderForm';
import ChangeStatusForm from 'pages/Shipments/ActionForms/ChangeStatusForm';
import CollectedByCustomerForm from 'pages/Shipments/ActionForms/CollectedByCustomerForm';
import CreateShipmentWithMyRatesModal from 'pages/Shipments/ActionForms/CreateShipment/CreateShipmentWithMyRatesModal';
import {
	removeMultipleShipmentResults,
	removeShipmentResult
} from 'pages/Shipments/ActionForms/CreateShipment/shipmentResultsSlice';
import CreateWave from 'pages/Shipments/ActionForms/CreateWave';
import CreateZoneWave from 'pages/Shipments/ActionForms/CreateZoneWave';
import DeliverySlotForm from 'pages/Shipments/ActionForms/DeliverySlotForm';
import EditReturnReasonForm from 'pages/Shipments/ActionForms/EditReturnReasonForm';
import GeneratedPickupOrderModal from 'pages/Shipments/ActionForms/GeneratedPickupOrderModal';
import InterDepotTransfer from 'pages/Shipments/ActionForms/InterDepotTransfer';
import OnHoldForm from 'pages/Shipments/ActionForms/OnHoldForm';
import OrderAvailableForm from 'pages/Shipments/ActionForms/OrderAvailableForm';
import OrderCommentModal from 'pages/Shipments/ActionForms/OrderCommentModal';
import OrderDocumentsModal from 'pages/Shipments/ActionForms/OrderDocumentsModal';
import OrderItems from 'pages/Shipments/ActionForms/OrderItems';
import OrderSplitForm from 'pages/Shipments/ActionForms/OrderSplitForm';
import OrderStatusesModal from 'pages/Shipments/ActionForms/OrderStatusesModal';
import PCPriceMatcher from 'pages/Shipments/ActionForms/PCPriceMatcher';
import PaymentMethodConfirmationModal from 'pages/Shipments/ActionForms/PaymentMethodConfirmationModal';
import Reallocate from 'pages/Shipments/ActionForms/ReallocateForm';
import ReturnConfirmation from 'pages/Shipments/ActionForms/ReturnConfirmationForm';
import ReviewPickingForm from 'pages/Shipments/ActionForms/ReviewPickingForm';
import SMSForm from 'pages/Shipments/ActionForms/SMSForm';
import SendEmailForm from 'pages/Shipments/ActionForms/SendEmailForm';
import SendTestSMSForm from 'pages/Shipments/ActionForms/SendTestSMSForm';
import SynchronizeForm from 'pages/Shipments/ActionForms/SynchronizeForm';
import WhatsappMessageForm from 'pages/Shipments/ActionForms/WhatsappMessageForm';
import SendEmailToClientForm from 'pages/Shipments/Operation/AllOrders/SendEmailToClientForm';
import UpdateSettings from 'pages/Shipments/Operation/CreateShipmentErrorMapping/UpdateSettings';
import UpdateTranslationModal from 'pages/Shipments/Operation/CreateShipmentErrorMapping/UpdateTranslationModal';
import PromoCodeForm from 'pages/Shipments/Operation/Promotions/PromoCodeForm';
import ZeroDcResultDetailedModal from 'pages/Shipments/Operation/ZeroDCResults/ZeroDcResultDetailedModal.jsx';
import DCPickList from 'pages/Shipments/PrintOrders/DCPickList';
import InternationalProforma from 'pages/Shipments/PrintOrders/InternationalProforma';
import PickListPDF from 'pages/Shipments/PrintOrders/PickListPDF';
import PrintAWB from 'pages/Shipments/PrintOrders/PrintAWB';
import PrintBranchLabel from 'pages/Shipments/PrintOrders/PrintBranchLabel';
import PrintOrders from 'pages/Shipments/PrintOrders/PrintOrders';
import PrintOrdersWithAWB from 'pages/Shipments/PrintOrders/PrintOrdersWithAWB';
import ReturnShipmentDetails from 'pages/Shipments/PrintOrders/ReturnShipmentDetails';
import CancelShipmentConfirmationModal from 'pages/Shipments/Shipments/CancelShipmentConfirmationModal';
import AdminEditIntegratorOrderForm from 'pages/Shipments/Shipments/OrderForm/AdminEditIntegratorOrderForm';
import EditOrderForm from 'pages/Shipments/Shipments/OrderForm/EditOrderForm';
import ViewOrderForm from 'pages/Shipments/Shipments/OrderForm/ViewOrderForm';
import DeliveryCompanyFeasibilitiesForm from 'pages/ShippingOtomation/DeliveryCompanyFeasibilities/DeliveryCompanyFeasibilitiesForm';
import OtomationRuleForm from 'pages/ShippingOtomation/OtomationRules/OtomationRuleForm';
import CapacityModal from 'pages/ShippingPartners/ConnectedPartners/CapacityModal';
import DeliverySlotConfigurationForm from 'pages/ShippingPartners/ConnectedPartners/DeliverySlotConfigurationForm';
import CityForm from 'pages/SupportAdmin/AddressManagement/Forms/CityForm';
import CountryForm from 'pages/SupportAdmin/AddressManagement/Forms/CountryForm';
import DistrictForm from 'pages/SupportAdmin/AddressManagement/Forms/DistrictForm';
import GovernorateForm from 'pages/SupportAdmin/AddressManagement/Forms/GovernorateForm';
import NeighbourhoodForm from 'pages/SupportAdmin/AddressManagement/Forms/NeighbourhoodForm';
import StateForm from 'pages/SupportAdmin/AddressManagement/Forms/StateForm';
import Send2FACodeModal from 'pages/SupportAdmin/ClientUsers/Send2FACodeModal';
import UserSelectionModal from 'pages/SupportAdmin/ClientUsers/UserSelectionModal';
import DCPickupLocationForm from 'pages/SupportAdmin/DCPickupLocationList/DCPickupLocationForm';
import SendDCPickupLocationEmailForm from 'pages/SupportAdmin/DCPickupLocationList/SendDCPickupLocationEmailForm';
import StatusChangeHistoryModal from 'pages/SupportAdmin/DCStatusManagement/MissingMappingStatuses/StatusChangeHistory';
import DCStatusMappingForm from 'pages/SupportAdmin/DCStatusManagement/StatusMapping/DCStatusMappingForm';
import OtoOrderStatusForm from 'pages/SupportAdmin/DCStatusManagement/Statuses/OtoOrderStatusForm';
import DeliveryCompanyForm from 'pages/SupportAdmin/DeliveryCompanies/DeliveryCompanyForm.jsx';
import ApplyRoleTemplateModal from 'pages/Users/ApplyRoleTemplateModal';
import ResetPasswordForm from 'pages/Users/ResetPasswordForm';
import BinItems from 'pages/WMSModule/Configuration/Bins/BinItems';
import UpdateBinZone from 'pages/WMSModule/Configuration/Bins/UpdateBinZone';
import UserGroupForm from 'pages/WMSModule/Configuration/UserGroups/UserGroupForm';
import VendorForm from 'pages/WMSModule/Configuration/Vendors/VendorForm';
import UpdateMinInventoryForm from 'pages/WMSModule/Inventory/Inventory/UpdateMinInventoryForm';
import AssignResponsible from 'pages/WMSModule/Operations/InventoryOrders/AssignResponsible';
import AssignPickerForm from 'pages/WMSModule/Operations/Waves/AssignPickerForm';
import FullDayWorkingHoursModal from 'pages/Warehouses/FullDayWorkingHoursModal';
import AddWebhookModal from 'pages/Webhooks/AddWebhookModal';

import fetchRemainingAmount from 'services/common/fetchRemainingAmount';
import {
	ADDRESS,
	ADMIN,
	ADVANCED_OMS,
	ADVANCE_WMS_MODULE,
	BLACKLISTED_CUSTOMER,
	BOXES,
	BRANCHES,
	BRAND,
	CATEGORY,
	COMPANY,
	CUSTOM_ROLE,
	FINANCE,
	IMPORT_EXPORT,
	ORDER_ACTIONS,
	ORDER_AUTOMATION,
	OTOFLEX,
	OTO_AI_CLIENT,
	OTO_LINK,
	OTO_STORE,
	PRICE_CONFIGURATIONS,
	PRODUCTS,
	PURCHASE_ORDER,
	REPORT,
	RETURNS,
	SHIPPING_AUTOMATION,
	SHIPPING_PARTNERS,
	SHIPPING_PRICES,
	SMS,
	USERS,
	WAREHOUSES,
	WEBHOOK,
	WMS_MODULE
} from 'services/consts';

import { setLoading } from 'stores/layoutSlice';
import { getPickupLocationCountries } from 'stores/userSlice';

import AppLogger from 'utils/AppLogger';
import PATHS, { mergePaths } from 'utils/paths';

import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faBoxes } from '@fortawesome/pro-light-svg-icons';
import {
	faArrowRotateLeft,
	faArrowUpFromGroundWater,
	faArrowsRotate,
	faBackward,
	faBallotCheck,
	faBan,
	faBarcode,
	faBarcodeScan,
	faBolt,
	faBoltSlash,
	faBookmark,
	faBookmarkSlash,
	faBox,
	faBoxesPacking,
	faBoxesStacked,
	faBroadcastTower,
	faCartShopping,
	faCheck,
	faCheckDouble,
	faCircleCheck,
	faCircleXmark,
	faClock,
	faClockRotateLeft,
	faCommentPlus,
	faCopy,
	faDollyEmpty,
	faDollyFlatbedAlt,
	faEnvelope,
	faEquals,
	faExpandAlt,
	faEye,
	faFileAlt,
	faFileCircleCheck,
	faFileDoc,
	faFileInvoice,
	faFilePen,
	faFilePowerpoint,
	faFileSignature,
	faGauge,
	faHourglassEmpty,
	faImages,
	faLessThanEqual,
	faLink,
	faListCheck,
	faMoneyCheckDollarPen,
	faPen,
	faPenToSquare,
	faPeopleCarryBox,
	faPlay,
	faPlus,
	faPrint,
	faRectangleList,
	faRepeat,
	faRightLeft,
	faRoad,
	faRotate,
	faRotateLeft,
	faRotateRight,
	faScannerGun,
	faSend,
	faSendBackward,
	faShareNodes,
	faShelves,
	faShop,
	faShopSlash,
	faSms,
	faTimes,
	faToggleOn,
	faTrashCan,
	faTruck,
	faTruckBolt,
	faTruckContainer,
	faTruckFast,
	faTruckLoading,
	faUpload,
	faUser,
	faUserCheck,
	faUserGear,
	faUserPen,
	faUserXmark,
	faWineGlassCrack,
	faXmark
} from '@fortawesome/pro-regular-svg-icons';

export const ACTION_OBJECT_TYPES = {
	ORDER: 'order',
	WAREHOUSE: 'warehouse',
	BRANCH: 'branch',
	PRODUCT: 'product',
	CATEGORY: 'category',
	PRODUCT_ATTRIBUTES: 'product-attributes',
	ATTRIBUTES_GROUPS: 'attributes-groups',
	OTOFLEX: 'otoflex',
	USER: 'user',
	BOX: 'box',
	ADMIN: 'admin',
	INBOUND: 'inbound',
	BRAND: 'brand',
	WMS: 'wms',
	SUPPORT: 'support',
	CUSTOM_ROLE: 'custom-role',
	PURCHASE_ORDER: 'purchaseOrder',
	SHIPPING_PARTNERS: 'shippingPartners',
	USER_GROUP: 'user-groups',
	DELIVERY_SLOT: 'delivery-slot',
	OTHER: 'other',
	CLIENT: 'client',
	OTO_AI_CLIENT_CONFIGURATION: 'oto-ai-client-configuration'
};

const BEFORE_SHIPMENT_STATUSES = [
	'assignedToWarehouse',
	'shipmentCanceled',
	'confirmationCanceled',
	'confirmationFailed',
	'scheduledShipmentFailed',
	'branchAssigned',
	'addressConfirmed',
	'shipmentOnHoldWarehouse'
];

const CANCEL_SHIPMENT_STATUSES = [
	'arrivedPickup',
	'goingToPickup',
	'reverseGoingToPickup',
	'searchingDriver',
	'shipmentCreated',
	'returnShipmentProcessing'
];

const DELETE_ORDER_STATUSES = [
	'assignedToWarehouse',
	'shipmentCanceled',
	'confirmationCanceled',
	'confirmationFailed',
	'scheduledShipmentFailed',
	'branchAssigned',
	'addressConfirmed',
	'shipmentOnHoldWarehouse',
	'canceled',
	'pickupFromStore'
];

const PRINT_AWB_DISABLED_STATUSES = [
	'assignedToWarehouse',
	'shipmentCanceled',
	'confirmationCanceled',
	'confirmationFailed',
	'scheduledShipmentFailed',
	'branchAssigned',
	'addressConfirmed',
	'shipmentOnHoldWarehouse',
	'canceled',
	'pickupFromStore',
	'returned',
	'confirmedReturn',
	'reverseReturned',
	'reverseConfirmReturn',
	'shipmentOnHoldToCancel'
];

const useActionsLibrary = () => {
	const { t } = useTranslation();
	const axiosPrivate = useAxiosPrivate();
	const switchSupportClient = useSwitchSupportClient();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { state, pathname } = useLocation();

	const { showError, showSuccess } = useAlert();
	const { showModal } = useModalRouter();
	const isAuthorized = useAccessControl();
	const { updateSallaOrderList } = useSallaSync();

	const handleSelectOnlyOneItem = (row) => Array.isArray(row) && 'errorMsg.selectOnlyOneItem';

	const { userType } = useSelector((state) => state?.user);

	const isThereAnyWithStatus = (rows, status) => {
		if (Array.isArray(rows)) {
			if (rows.filter((row) => row.status === status).length !== 0) {
				return true;
			}
		} else {
			if (rows.status === status) return true;
		}
		return false;
	};

	const isAllWithStatus = (rows, status) => {
		if (Array.isArray(rows)) {
			if (rows.filter((row) => row.status !== status).length !== 0) {
				return false;
			}
		} else {
			if (rows.status !== status) return false;
		}
		return true;
	};

	const isExpired = (expiryDate) => {
		const today = dayjs().startOf('day');
		if (today.isAfter(expiryDate)) {
			return 'errorMsg.expiredCantEdit';
		}
		return false;
	};

	/**
	 * @param {object} data - data to be validated
	 * @param {string} type - 'enabled' or 'disabled'
	 * @param {array} statusArray - array of statuses to be checked
	 * @param {string} errorMessage - error message to be returned
	 */
	const handleStatusCheck = (data, type, statusArray, errorMessage) => {
		const dataArr = Array.isArray(data) ? data : [data];

		if (dataArr.length === 0) return 'errorMsg.selectAtLeastOneItem';

		if (type === 'enabled') {
			const statusCheck = dataArr.filter((obj) => statusArray.includes(obj.status));
			if (statusCheck.length !== dataArr.length) {
				return errorMessage;
			}
		} else if (type === 'disabled') {
			const statusCheck = dataArr.filter((obj) => statusArray.includes(obj.status));
			if (statusCheck.length > 0) {
				return errorMessage;
			}
		} else {
			return 'Handle status check type error!!';
		}
	};

	const actionsLibrary = {
		[ACTION_OBJECT_TYPES.ORDER]: [
			// CREATE-SHIPMENT START
			{
				icon: faPlus,
				label: 'newShipment',
				role: 'createNewShipment',
				group: 'createShipment',
				showAlways: true,
				disabled: (selectedOrders) => {
					if (
						(Array.isArray(selectedOrders) && selectedOrders.length !== 0) ||
						(typeof selectedOrders === 'object' && !Array.isArray(selectedOrders))
					) {
						return 'errorMsg.youMustClearSelectionBeforeCreatingNewShipment';
					}
				},
				action: () => {
					navigate(PATHS.CREATE_SHIPMENT);
				}
			},
			{
				icon: faTruckFast,
				label: 'createShipmentSingleAggregator',
				role: 'createShipmentSingleAggregator',
				group: 'createShipment',
				requiredFields: ['ID', 'status', 'paymentMethod', 'totalDue', 'orderID', 'orderType'],
				disabled: (selectedOrders) =>
					handleSelectOnlyOneItem(selectedOrders) ||
					handleStatusCheck(
						selectedOrders,
						'enabled',
						BEFORE_SHIPMENT_STATUSES,
						'errorMsg.cannotCreateShipmentForThisStatus'
					),
				render: (selectedOrders) => !Array.isArray(selectedOrders),
				action: (selectedOrder) => {
					const error = selectedOrder.paymentMethod.toLowerCase() === 'cod' && !selectedOrder.totalDue;
					if (!error) {
						const path =
							selectedOrder?.orderType === 'import'
								? mergePaths(PATHS.IMPORT_ORDERS, PATHS.IMPORT_CALCULATOR)
								: mergePaths(PATHS.SHIPMENTS, PATHS.PRICE_CALCULATOR);

						navigate(path, {
							state: {
								orderID: selectedOrder.ID,
								external: true
							}
						});
					} else {
						showError({
							header: selectedOrder.orderID,
							message: 'error.codTotalDueNotExists'
						});
					}
				}
			},
			{
				icon: faTruckFast,
				label: 'createShipmentBulkAggregator',
				role: 'createShipmentBulkAggregator',
				group: 'createShipment',
				requiredFields: ['ID', 'status', 'paymentMethod', 'totalDue', 'orderID', 'isInternationalShipment'],
				render: (selectedOrders) => Array.isArray(selectedOrders),
				disabled: (selectedOrders) => {
					const importOrderCheck = selectedOrders.some((order) => order.orderType === 'import');
					if (importOrderCheck) return 'errorMsg.cannotCreateBulkShipmentForImportOrder';

					const orderCountCheck = selectedOrders.length > 50 ? 'errorMsg.max50Orders' : null;
					if (orderCountCheck) return orderCountCheck;

					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						BEFORE_SHIPMENT_STATUSES,
						'errorMsg.cannotCreateShipmentForThisStatus'
					);
					if (statusCheck) return statusCheck;

					const isInternationalShipmentCheck = selectedOrders.some((order) => order.isInternationalShipment);
					if (isInternationalShipmentCheck)
						return 'errorMsg.cannotCreateBulkShipmentWithInternationalShipment';
				},
				action: (selectedOrders) => {
					const selected = Array.isArray(selectedOrders) ? selectedOrders : [selectedOrders];
					const error = selected.find(
						(selectedOrder) =>
							selectedOrder.paymentMethod.toLowerCase() === 'cod' && !selectedOrder.totalDue
					);

					if (!error) {
						showModal('create-shipment-bulk-aggregator', {
							ids: selectedOrders.map((order) => order.ID)
						});
					} else {
						showError({
							header: error.orderID,
							message: 'error.codTotalDueNotExists'
						});
					}
				}
			},
			{
				icon: faTruckContainer,
				label: 'createShipmentAutomatically',
				role: 'createShipmentAutomatically',
				group: 'createShipment',
				requiredFields: ['ID', 'status', 'paymentMethod', 'totalDue'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						BEFORE_SHIPMENT_STATUSES,
						'errorMsg.cannotCreateShipmentForThisStatus'
					),
				action: (selectedOrders) => {
					dispatch(setLoading(true));
					const selected = Array.isArray(selectedOrders) ? selectedOrders : [selectedOrders];
					const error = selected.find(
						(selectedOrder) =>
							selectedOrder.paymentMethod.toLowerCase() === 'cod' && !selectedOrder.totalDue
					);
					if (!error) {
						axiosPrivate
							.post(ORDER_ACTIONS.CREATE_SHIPMENT_AUTOMATICALLY, {
								IDs: Array.isArray(selectedOrders)
									? selectedOrders.map((order) => order.ID)
									: [selectedOrders.ID]
							})
							.then((res) => {
								NiceModal.show(CreateShipmentWithMyRatesModal, {
									ids: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID],
									mode: 'automaticShipment',
									lastIndexOfErrorLogs: res.data.lastIndexOfErrorLogs ?? null,
									minimized: true
								});
								showSuccess({
									header: 'asyncShipmentCreated',
									message: 'asyncShipmentCreatedMessage'
								});
							})
							.catch((error) => {
								AppLogger.error(error);
								showError();
							})
							.finally(() => {
								dispatch(setLoading(false));
							});
					} else {
						showError({
							header: error.orderID,
							message: 'error.codTotalDueNotExists'
						});
						dispatch(setLoading(false));
					}
				}
			},
			{
				icon: faTruckBolt,
				label: 'createShipmentAndPrint',
				role: 'createShipmentAndPrint',
				group: 'createShipment',
				showAlways: true,
				action: () => {
					showModal('create-shipment-and-print');
				}
			},
			{
				icon: faTruck,
				label: 'createShipmentWithMyRates',
				role: 'createShipmentWithMyRates',
				group: 'createShipment',
				requiredFields: ['ID', 'status', 'paymentMethod', 'totalDue', 'orderID'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						BEFORE_SHIPMENT_STATUSES,
						'errorMsg.cannotCreateShipmentForThisStatus'
					),
				action: (selectedOrders) => {
					const selected = Array.isArray(selectedOrders) ? selectedOrders : [selectedOrders];
					const error = selected.find(
						(selectedOrder) =>
							selectedOrder.paymentMethod.toLowerCase() === 'cod' && !selectedOrder.totalDue
					);
					if (!error) {
						showModal('create-shipment-my-rates', {
							ids: Array.isArray(selectedOrders)
								? selectedOrders.map((order) => order.ID)
								: [selectedOrders.ID]
						});
					} else {
						showError({
							header: error.orderID,
							message: 'error.codTotalDueNotExists'
						});
					}
				}
			},
			{
				icon: faArrowRotateLeft,
				label: 'createReturnShipmentWithOtoRates',
				role: 'createReturnShipmentWithOtoRates',
				group: 'createShipment',
				requiredFields: ['ID', 'status'],
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					handleStatusCheck(row, 'enabled', ['delivered'], 'errorMsg.notAllowedForThisStatus'),
				action: (selectedOrder) => {
					showModal('create-return-shipment-aggregator', {
						id: selectedOrder?.ID
					});
				}
			},
			{
				icon: faArrowRotateLeft,
				label: 'createReturnShipmentWithMyRates',
				role: 'createReturnShipmentWithMyRates',
				group: 'createShipment',
				requiredFields: ['ID', 'status'],
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					handleStatusCheck(row, 'enabled', ['delivered'], 'errorMsg.notAllowedForThisStatus'),
				action: (selectedOrder) => {
					showModal('create-return-shipment-with-my-rates', {
						id: selectedOrder?.ID
					});
				}
			},
			// CREATE-SHIPMENT END
			// ORDER START
			{
				icon: faPenToSquare,
				label: 'editOrderAdmin',
				role: 'editOrderAdmin',
				group: 'order',
				requiredFields: ['ID', 'status'],
				disabled: (selectedOrders) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(selectedOrders);
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						[...BEFORE_SHIPMENT_STATUSES, 'pickupFromStore', 'missingData'],
						'errorMsg.notAllowedForThisStatus'
					);
					if (selectOnlyOneItem) return selectOnlyOneItem;
					if (statusCheck) return statusCheck;
				},
				action: (row) => {
					NiceModal.show(EditOrderForm, {
						id: row?.ID,
						adminMode: true
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOrderAdminIntegratorShipment',
				role: 'editOrderAdminIntegratorShipment',
				group: 'order',
				requiredFields: ['ID', 'isAggregator'],
				disabled: (selectedOrders) => {
					const aggregatorCheck = Array.isArray(selectedOrders)
						? selectedOrders.some((order) => order?.isAggregator)
						: selectedOrders?.isAggregator;
					const isTechSupportManager = isAuthorized('editAdminAggregatorOrders');
					return aggregatorCheck && !isTechSupportManager ? 'errorMsg.notAllowedForThisStatus' : null;
				},
				action: (row) => {
					NiceModal.show(AdminEditIntegratorOrderForm, {
						id: row?.ID
					});
				}
			},
			{
				icon: faTruck,
				label: 'reallocateOrder',
				role: 'reallocateOrder',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						[
							'branchAssigned',
							'returned',
							'assignedToWarehouse',
							'shipmentCanceled',
							'confirmationCanceled',
							'confirmationFailed',
							'scheduledShipmentFailed',
							'shipmentOnHoldWarehouse',
							'addressConfirmed',
							'waitingAddressConfirmation',
							'pickupFromStore',
							'readyForCollection',
							'canceled',
							'needConfirmation',
							'new',
							'confirmedReturn',
							'reverseReturned',
							'reverseConfirmReturn',
							'missingData',
							'orderConfirmed',
							'codOrderConfirmed'
						],
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(Reallocate, {
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},

			{
				icon: faArrowRotateLeft,
				label: 'markAsReturned',
				role: 'markAsReturned',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'isReverseShipment'],
				disabled: (selectedOrders) => {
					const aggregatorCheck = Array.isArray(selectedOrders)
						? selectedOrders.some((order) => order?.isAggregator)
						: selectedOrders?.isAggregator;

					const isSupportUser = isAuthorized('SupportUserActions');

					if (!isSupportUser && aggregatorCheck) return 'errorMsg.notAllowedForThisStatus';

					const isAllReverse = Array.isArray(selectedOrders)
						? selectedOrders.every((order) => order.isReverseShipment === 'yes')
						: selectedOrders.isReverseShipment === 'yes';
					const isAllNotReverse = Array.isArray(selectedOrders)
						? selectedOrders.every((order) => order.isReverseShipment !== 'yes')
						: selectedOrders.isReverseShipment !== 'yes';

					if (!isAllReverse && !isAllNotReverse) {
						return 'errorMsg.notAllowedForThisStatus';
					} else {
						return handleStatusCheck(
							selectedOrders,
							'enabled',
							[
								'searchingDriver',
								'shipmentCreated',
								'goingToPickup',
								'arrivedPickup',
								'pickedUp',
								'arrivedOriginTerminal',
								'arrivedTerminal',
								'reverseArrivedTerminal',
								'shipmentInProgress',
								'arrivedDestinationTerminal',
								'shipmentOnHold',
								'outForDelivery',
								'undeliveredAttempt',
								'arrivedDestination',
								'inTransit',
								'departedTerminal',
								'reverseDepartedTerminal',
								'shipmentOnHoldWarehouse',
								'delivered',
								'reverseOutForDelivery',
								'reversePickedUp',
								'reverseUndeliveredAttempt',
								'returnShipmentProcessing',
								'returnProcessing',
								'heldAtCustomsClearance',
								'reverseHeldAtCustomsClearance',
								'moreDocumentsRequired',
								'reverseMoreDocumentsRequired'
							],
							'errorMsg.notAllowedForThisStatus'
						);
					}
				},
				action: (selectedOrders) => {
					const isAnyReverse = Array.isArray(selectedOrders)
						? selectedOrders.some((order) => order.isReverseShipment === 'yes')
						: selectedOrders.isReverseShipment === 'yes';
					NiceModal.show(ChangeStatusForm, {
						status: isAnyReverse ? 'reverseReturned' : 'returned',
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editReturnReason',
				role: 'editReturnReason',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status', 'returnReasonOptions', 'returnReason'],
				action: (selectedOrders) => {
					NiceModal.show(EditReturnReasonForm, {
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID],
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						returnReason: Array.isArray(selectedOrders) ? null : selectedOrders.returnReasonOptions,
						returnComment: Array.isArray(selectedOrders) ? null : selectedOrders.returnReason
					});
				}
			},
			{
				icon: faCheck,
				label: 'confirmReturned',
				role: 'confirmReturned',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status', 'returnType'],
				disabled: (selectedOrders) => {
					if (handleSelectOnlyOneItem(selectedOrders)) return handleSelectOnlyOneItem(selectedOrders);
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						['returned', 'reverseReturned'],
						'errorMsg.notAllowedForThisStatus'
					);
					if (
						statusCheck &&
						(selectedOrders.status !== 'confirmedReturn' || selectedOrders.returnType !== 'partially') &&
						(selectedOrders.status !== 'reverseConfirmReturn' || selectedOrders.returnType !== 'partially')
					)
						return statusCheck;
				},
				action: (selectedOrder) => {
					NiceModal.show(ReturnConfirmation, { selectedOrder });
				}
			},
			{
				icon: faCheckDouble,
				label: 'confirmReturnedMultiple',
				role: 'confirmReturnedMultiple',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						['returned', 'reverseReturned'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'confirmReturnedMultiple',
						subTitle: 'confirmation.confirmReturnedMultiple',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(
									ORDER_ACTIONS.UPDATE_RETURN_CONFIRMATION_MULTIPLE,
									{
										otoOrderIDs: Array.isArray(selectedOrders)
											? selectedOrders.map((order) => order.ID)
											: [selectedOrders.ID]
									}
								);
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								if (['error.O022', 'error.O021', 'error.O010'].includes(error.response?.data.code)) {
									showError({
										message: error.response?.data.code
									});
								} else {
									showError();
								}
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCopy,
				label: 'duplicateOrder',
				role: 'duplicateOrder',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) => {
					showModal('add-order-form', {
						mode: 'duplicate',
						id: selectedOrder?.ID
					});
				}
			},
			{
				icon: faHourglassEmpty,
				label: 'onHoldOrder',
				role: 'onHoldOrder',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						[...BEFORE_SHIPMENT_STATUSES, 'pickupFromStore'],
						'errorMsg.cannotOnHoldOrderForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(OnHoldForm, {
						status: 'shipmentOnHoldWarehouse',
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOnholdReason',
				role: 'editOnholdReason',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status', 'onHoldReason', 'onHoldComment'],
				disabled: (selectedOrders) => {
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						[...BEFORE_SHIPMENT_STATUSES, 'pickupFromStore'],
						'errorMsg.cannotOnHoldOrderForThisStatus'
					);
					if (statusCheck) return statusCheck;
				},
				action: (selectedOrders) => {
					NiceModal.show(OnHoldForm, {
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID],
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						onHoldReason: Array.isArray(selectedOrders) ? null : selectedOrders.onHoldReason,
						onHoldComment: Array.isArray(selectedOrders) ? null : selectedOrders.onHoldComment,
						status: 'shipmentOnHoldWarehouse'
					});
				}
			},
			{
				icon: faCircleCheck,
				label: 'markAsDelivered',
				role: 'markAsDelivered',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status', 'isAggregator', 'totalDue'],
				disabled: (selectedOrders) => {
					const aggregatorCheck = Array.isArray(selectedOrders)
						? selectedOrders.some((order) => order?.isAggregator)
						: selectedOrders?.isAggregator;

					const isSupportUser = isAuthorized('SupportUserActions');

					if (!isSupportUser && aggregatorCheck) return 'errorMsg.notAllowedForThisStatus';

					const isCod = Array.isArray(selectedOrders)
						? selectedOrders.every((order) => !isNaN(order.totalDue) && order.totalDue > 0)
						: !isNaN(selectedOrders.totalDue) && selectedOrders.totalDue > 0;

					if (isSupportUser && isCod) return 'errorMsg.notAllowedForCodOrderForSupportUser';

					return handleStatusCheck(
						selectedOrders,
						'enabled',
						[
							...BEFORE_SHIPMENT_STATUSES,
							'arrivedPickup',
							'goingToPickup',
							'searchingDriver',
							'shipmentCreated',
							'pickedUp',
							'arrivedTerminal',
							'departedTerminal',
							'inTransit',
							'outForDelivery',
							'arrivedDestination',
							'shipmentInProgress',
							'returnProcessing',
							'arrivedOriginTerminal',
							'arrivedDestinationTerminal',
							'heldAtCustomsClearance',
							'moreDocumentsRequired',
							'undeliveredAttempt',
							'shipmentOnHold',
							'pickupAttempted',
							'heldForPickup',
							'arrivedPickup',
							'driverAssigned'
						],
						'errorMsg.cannotMarkasDeliveredForThisStatus'
					);
				},
				action: async (selectedOrders) => {
					const aggregatorOrders = [];

					try {
						const response = await axiosPrivate.post(ORDER_ACTIONS.CHECK_AGGREGATOR_FOR_MARK_AS_DELIVERED, {
							otoOrderIDs: Array.isArray(selectedOrders)
								? selectedOrders.map((order) => order.ID)
								: [selectedOrders.ID]
						});
						if (response.status === 200) {
							response.data.map((order) => {
								if (order.aggregator === true) {
									aggregatorOrders.push(order.orderID);
								}
							});

							if (aggregatorOrders.length === 0 || isAuthorized('SupportUserActions')) {
								NiceModal.show(ChangeStatusForm, {
									status: 'delivered',
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID],
									orderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.orderID)
										: [selectedOrders.orderID]
								});
							} else if (aggregatorOrders.length > 0) {
								showError({
									header: 'errorMsg.markAsDeliveredFailedForTheseOrders',
									persist: true,
									message: aggregatorOrders.join(', ')
								});
							}
						} else {
							AppLogger.error(response);
						}
					} catch (error) {
						AppLogger.error(error);
						showError();
					}
				}
			},
			{
				icon: faWineGlassCrack,
				label: 'markAsLostOrDamaged',
				role: 'markAsLostOrDamaged',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (selectedOrders) => {
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'disabled',
						['destroyed', 'returnDestroyed'],
						'errorMsg.cannotOnHoldOrderForThisStatus'
					);
					if (statusCheck) return statusCheck;
				},
				action: (selectedOrders) => {
					NiceModal.show(ChangeStatusForm, {
						status: 'lostOrDamaged',
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faCircleCheck,
				label: 'markAsDestroyed',
				role: 'markAsDestroyed',
				group: 'order',
				requiredFields: ['ID', 'status', 'aggrementType', 'serviceType'],
				disabled: (selectedOrders) => {
					const notAggregatorCheck = Array.isArray(selectedOrders)
						? selectedOrders.some((order) => order.aggrementType !== 'AGGREGATOR')
						: selectedOrders.aggrementType !== 'AGGREGATOR';

					if (notAggregatorCheck) return 'errorMsg.cannotMarkAsDestroyedForNotAggregatorOrders';

					return handleStatusCheck(
						selectedOrders,
						'enabled',
						[
							'pickedUp',
							'arrivedTerminal',
							'departedTerminal',
							'inTransit',
							'outForDelivery',
							'arrivedDestination',
							'shipmentInProgress',
							'returnProcessing',
							'arrivedOriginTerminal',
							'arrivedDestinationTerminal',
							'heldAtCustomsClearance',
							'moreDocumentsRequired',
							'undeliveredAttempt',
							'shipmentOnHold'
						],
						'errorMsg.cannotMarkasDestroyedForThisStatus'
					);
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'destroyOrderTitle',
						subTitle: 'destroyOrderSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.MARK_AS_DESTROYED, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								showError();
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faShelves,
				label: 'markAsPicked',
				role: 'markAsPicked',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsPicked',
						subTitle: 'pickedConfirmationText',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.MARK_AS_PICKED, {
									otoOrderIds: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									const successfullIDs = response.data.successfulOtoOrderIds;
									const failedIDs = response.data.failedOtoOrderIds;
									if (failedIDs?.length > 0) {
										showError({
											header: 'error',
											message: failedIDs.join(', ')
										});
										if (successfullIDs?.length > 0) {
											showSuccess({
												header: 'success',
												message: successfullIDs.join(', ')
											});
										}
										setLoading(false);
									} else {
										showSuccess();
									}
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError({
									message: error?.message
								});
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBarcode,
				label: 'markAsPacked',
				role: 'markAsPacked',
				group: 'order',
				requiredFields: ['ID', 'status'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsPacked',
						subTitle: 'packedConfirmationText',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.MARK_AS_PACKED, {
									otoOrderIds: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									const successfullIDs = response.data.successfulOtoOrderIds;
									const failedIDs = response.data.failedOtoOrderIds;
									if (failedIDs?.length > 0) {
										showError({
											header: 'error',
											message: failedIDs.join(', ')
										});
										if (successfullIDs?.length > 0) {
											showSuccess({
												header: 'success',
												message: successfullIDs.join(', ')
											});
										}
										setLoading(false);
									} else {
										showSuccess();
									}
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError({
									message: error?.message
								});
							}

							dispatch(setLoading(false));
						}
					});
				}
			},

			{
				icon: faListCheck,
				label: 'reviewPickingForm',
				role: 'reviewPickingForm',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) => {
					NiceModal.show(ReviewPickingForm, { ID: selectedOrder.ID });
				}
			},
			{
				icon: faDollyEmpty,
				label: 'markAsNotAvailable',
				role: 'markAsNotAvailable',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) => {
					NiceModal.show(OrderAvailableForm, {
						orderID: selectedOrder?.ID,
						status: selectedOrder?.status
					});
				}
			},
			{
				icon: faCheck,
				label: 'acceptOrder',
				role: 'acceptOrder',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) => {
					NiceModal.show(ConfirmationModal, {
						title: 'acceptOrder',
						subTitle: 'confirmation.acceptOrder',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.ACCEPT_ORDER, {
									orderID: selectedOrder.ID
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faXmark,
				label: 'rejectOrder',
				role: 'rejectOrder',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) => {
					NiceModal.show(ConfirmationModal, {
						title: 'rejectOrder',
						subTitle: 'confirmation.rejectOrder',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.REJECT_ORDER, {
									orderID: selectedOrder.ID
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBarcodeScan,
				label: 'addSerialNumber',
				role: 'addSerialNumber',
				group: 'tools',
				requiredFields: ['ID', 'orderID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) => {
					NiceModal.show(AddSerialNumber, { ID: selectedOrder.ID, orderID: selectedOrder.orderID });
				}
			},
			{
				icon: faTruck,
				label: 'sendBackToPending',
				role: 'sendBackToPending',
				group: 'order',
				requiredFields: ['ID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						['shipmentOnHoldWarehouse', 'shipmentOnHoldToCancel', 'returned', 'confirmedReturn'],
						'errorMsg.cannotOnHoldOrderForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'sendBackToPending',
						subTitle: 'confirmation.sendBackProcessing',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.SEND_BACK_TO_PENDING, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								showError({
									message: error.response?.data.code || error.response?.data.message
								});
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faRotate,
				label: 'synchronizeOrder',
				role: 'synchronizeOrder',
				group: 'order',
				requiredFields: ['orderID', 'storeName'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SynchronizeForm, {
						orderID: row.orderID,
						storeName: row.storeName
					});
				}
			},
			{
				icon: faExpandAlt,
				label: 'splitOrder',
				role: 'splitOrder',
				group: 'order',
				requiredFields: [
					'ID',
					'orderID',
					'bundleCheck',
					'createdAt',
					'paymentMethod',
					'totalDue',
					'grandTotal',
					'currency'
				],
				disabled: (selectedOrders) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(selectedOrders);
					if (selectOnlyOneItem) return selectOnlyOneItem;
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						[
							'assignedToWarehouse',
							'branchAssigned',
							'shipmentCanceled',
							'confirmationCanceled',
							'confirmationFailed',
							'scheduledShipmentFailed',
							'shipmentOnHoldWarehouse',
							'shipmentOnHoldToCancel'
						],
						'errorMsg.cannotSplitOrderForThisStatus'
					);
					if (statusCheck) return statusCheck;
					if (selectedOrders.bundleCheck) return 'errorMsg.cannotSplitOrdersWithBundleItems';
				},
				action: (selectedOrders) => {
					NiceModal.show(OrderSplitForm, {
						selectedOrders
					});
				}
			},
			{
				icon: faSms,
				label: 'sendSMS',
				role: 'sendSMS',
				group: 'notify',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (row) =>
					handleStatusCheck(row, 'disabled', ['canceled'], 'errorMsg.cannotSendSMSForThisStatus'),
				action: (selectedOrders) => {
					NiceModal.show(SMSForm, {
						selectedOrders
					});
				}
			},
			{
				icon: faWhatsapp,
				label: 'sendWhatsappMessage',
				role: 'sendWhatsappMessage',
				group: 'notify',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (row) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(row);
					const statusCheck = handleStatusCheck(
						row,
						'disabled',
						['canceled'],
						'errorMsg.cannotSendWPMessageForThisStatus'
					);
					return selectOnlyOneItem || statusCheck;
				},
				action: (selectedOrders) => {
					NiceModal.show(WhatsappMessageForm, {
						selectedOrders
					});
				}
			},
			{
				icon: faEnvelope,
				label: 'sendEmail',
				role: 'sendEmail',
				group: 'notify',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (row) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(row);
					const statusCheck = handleStatusCheck(
						row,
						'disabled',
						['canceled'],
						'errorMsg.cannotSendEmailForThisStatus'
					);
					return selectOnlyOneItem || statusCheck;
				},
				action: (selectedOrders) => {
					NiceModal.show(SendEmailForm, {
						selectedOrders
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOrder',
				role: 'editOrder',
				group: 'order',
				requiredFields: ['ID', 'status'],
				headerItem: true,
				disabled: (selectedOrders) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(selectedOrders);
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						[...BEFORE_SHIPMENT_STATUSES, 'pickupFromStore', 'missingData'],
						'errorMsg.notAllowedForThisStatus'
					);
					if (selectOnlyOneItem) return selectOnlyOneItem;
					if (statusCheck) return statusCheck;
				},
				action: (row) => {
					showModal(row?.orderType === 'import' ? 'edit-import-order-form' : 'edit-order-form', {
						id: row?.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewOrder',
				role: 'viewOrder',
				group: 'order',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (selectedOrders) => handleSelectOnlyOneItem(selectedOrders),
				action: (row) => {
					showModal(row?.orderType === 'import' ? 'view-import-order-form' : 'view-order-form', {
						id: row?.ID
					});
				}
			},
			{
				icon: faBan,
				label: 'cancelOrder',
				role: 'cancelOrder',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						[
							...BEFORE_SHIPMENT_STATUSES,
							'pickupFromStore',
							'needConfirmation',
							'new',
							'readyForCollection'
						],
						'errorMsg.cannotCancelOrderForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(CancelOrderForm, {
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faBan,
				label: 'cancelOrder',
				role: 'cancelOTOLinkOrder',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						['shipmentCreated', 'searchingDriver', 'assignedToWarehouse'],
						'errorMsg.cannotCancelOrderForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(CancelOTOLinkOrderForm, {
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'assignPicker',
				role: 'assignPicker',
				group: 'tools',
				requiredFields: ['ID', 'orderID', 'warehouseID'],
				disabled: (orders) => {
					const warehouseCheck =
						Array.isArray(orders) && orders.some((order) => order?.warehouseID !== orders[0]?.warehouseID);
					if (!Array.isArray(orders) && !orders.warehouseID) return 'errorMsg.notAllowedForBranches';
					if (warehouseCheck) return 'errorMsg.selectOrdersFromSameWarehouse';
				},
				action: (orders) => {
					const warehouseIDs = Array.isArray(orders)
						? orders.map((order) => order?.warehouseID)
						: [orders.warehouseID];
					NiceModal.show(AssignPickerOrder, {
						ids: Array.isArray(orders) ? orders.map((order) => order.ID) : [orders.ID],
						orderIDs: Array.isArray(orders) ? orders.map((order) => order.orderID) : [orders.orderID],
						whID: warehouseIDs[0]?.toString()
					});
				}
			},
			{
				icon: faCommentPlus,
				label: 'addComment',
				role: 'addComment',
				group: 'tools',
				requiredFields: ['ID', 'orderID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => NiceModal.show(OrderCommentModal, { ID: row.ID, orderID: row.orderID })
			},
			{
				icon: faClockRotateLeft,
				label: 'showStatusHistory',
				role: 'showStatusHistory',
				group: 'tools',
				requiredFields: ['ID', 'orderID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => NiceModal.show(OrderStatusesModal, { ID: row.ID, orderID: row.orderID })
			},
			{
				icon: faCartShopping,
				label: 'showOrderItems',
				role: 'showOrderItems',
				group: 'tools',
				requiredFields: ['ID', 'orderID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => NiceModal.show(OrderItems, { data: row, formMode: 'view' })
			},
			{
				icon: faLink,
				label: 'updateTransactionReport',
				role: 'updateTransactionReport',
				group: 'tools',
				requiredFields: ['ID'],
				action: (rows) => {
					const otoIds = Array.isArray(rows) ? rows.map((order) => order.ID) : [rows.ID];
					dispatch(setLoading(true));
					axiosPrivate
						.post(WMS_MODULE.UPDATE_ORDER_BASED_TRANSACTIONS, {
							otoIds
						})
						.then((response) => {
							if (response.status === 200) {
								showSuccess({
									message: 'updatedTransactionReportInfoMessage'
								});
							} else {
								AppLogger.error(response);
								showError();
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(setLoading(false));
						});
				}
			},
			{
				icon: faClock,
				label: 'changeDeliverySlot',
				role: 'changeDeliverySlot',
				group: 'tools',
				requiredFields: ['ID', 'orderID'],
				action: (selectedOrders) => {
					NiceModal.show(DeliverySlotForm, {
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faListCheck,
				label: 'viewReturnDetails',
				role: 'viewReturnDetails',
				group: 'order',
				requiredFields: ['ID'],
				action: (selectedOrder) => {
					NiceModal.show(ViewReturnDetails, { ID: selectedOrder.ID });
				}
			},

			{
				icon: faBoxesPacking,
				label: 'quickPack',
				role: 'quickPack',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (selectedOrders) => handleSelectOnlyOneItem(selectedOrders),
				action: (selectedOrder) => showModal('packing-form', { id: selectedOrder?.ID, mode: 'quickPack' })
			},
			{
				icon: faLessThanEqual,
				label: 'pcPriceMatcher',
				role: 'pcPriceMatcher',
				group: 'order',
				requiredFields: ['ID'],
				action: (selectedOrders) =>
					NiceModal.show(PCPriceMatcher, {
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID]
					})
			},
			{
				icon: faFilePen,
				label: 'editOrderDocuments',
				role: 'editOrderDocuments',
				group: 'order',
				requiredFields: ['ID', 'orderId'],
				action: (selectedOrder) =>
					NiceModal.show(OrderDocumentsModal, {
						ID: selectedOrder.orderId,
						orderID: selectedOrder.ID
					})
			},
			{
				icon: faListCheck,
				label: 'undeliveredAttempt',
				role: 'undeliveredAttempt',
				group: 'order',
				requiredFields: ['ID', 'orderId'],
				action: (selectedOrders) => {
					NiceModal.show(ChangeStatusForm, {
						status: 'undeliveredAttempt',
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						orderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faEye,
				label: 'markAsHeldForPickup',
				role: 'heldForPickup',
				group: 'order',
				requiredFields: ['ID', 'orderId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrders) => {
					NiceModal.show(ChangeStatusForm, {
						status: selectedOrders?.isReverseShipment === 'yes' ? 'reverseHeldForPickup' : 'heldForPickup',
						otoOrderIDs: [selectedOrders.ID],
						orderIDs: [selectedOrders.orderID]
					});
				}
			},
			{
				icon: faUpload,
				label: 'uploadDocument',
				role: 'uploadDocument',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedOrder) =>
					NiceModal.show(OrderDocumentsModal, {
						ID: selectedOrder.orderID,
						orderID: selectedOrder.ID
					})
			},
			{
				icon: faEye,
				label: 'sendEmailToClient',
				role: 'sendEmailToClient',
				group: 'order',
				headerItem: true,
				requiredFields: ['companyId'],
				action: (selectedRows) =>
					NiceModal.show(SendEmailToClientForm, {
						selectedRows: Array.isArray(selectedRows) ? selectedRows : [selectedRows]
					})
			},
			{
				icon: faArrowRotateLeft,
				label: 'resendEcommerceUpdate',
				role: 'resendEcommerceUpdate',
				group: 'order',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					const isDesktop = import.meta.env.VITE_PLATFORM === 'desktop';
					const apiUrl = isDesktop
						? ORDER_ACTIONS.RESEND_ECOM_UPDATE_DEKSTOP
						: ORDER_ACTIONS.RESEND_ECOM_UPDATE;
					const otoOrderIDs = Array.isArray(selectedOrders)
						? selectedOrders.map((order) => order.ID)
						: [selectedOrders.ID];

					dispatch(setLoading(true));
					axiosPrivate
						.post(apiUrl, {
							otoOrderIDs
						})
						.then(async (response) => {
							if (response.status === 200) {
								if (isDesktop && response?.data?.orderUpdates?.length > 0) {
									const sallaStoreList = await dispatch(
										fetchSallaStores({ forceSync: false })
									).unwrap();
									response.data.orderUpdates.forEach((order) => {
										const { merchantId } = order;
										const merchant = sallaStoreList?.find((item) => item.id === Number(merchantId));
										updateSallaOrderList([order], merchant, merchantId);
									});
								}
								showSuccess({
									message: 'ecommerceUpdateInfoMessage'
								});
							}
						})
						.catch(() => {
							showError();
						})
						.finally(() => {
							dispatch(setLoading(false));
						});
				}
			},
			{
				icon: faFileCircleCheck,
				label: 'paymentMethodConfirmation',
				role: 'paymentMethodConfirmation',
				group: 'tools',
				requiredFields: [
					'ID',
					'orderID',
					'currency',
					'grandTotal',
					'paymentMethod',
					'paymentNeedConfirm',
					'totalDue',
					'status'
				],
				disabled: (selectedOrders) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(selectedOrders);
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						['needConfirmation'],
						'errorMsg.notAllowedForThisStatus'
					);
					if (selectOnlyOneItem) return selectOnlyOneItem;
					if (statusCheck) return statusCheck;

					if (selectedOrders.paymentNeedConfirm !== true) {
						return 'errorMsg.paymentMethodAlreadyConfirmed';
					}
				},
				action: (selectedOrder) => {
					NiceModal.show(PaymentMethodConfirmationModal, {
						ID: selectedOrder?.ID,
						orderID: selectedOrder?.orderID,
						currency: selectedOrder?.currency,
						grandTotal: selectedOrder?.grandTotal,
						paymentMethod: selectedOrder?.paymentMethod,
						totalDue: selectedOrder?.totalDue
					});
				}
			},
			{
				icon: faMoneyCheckDollarPen,
				label: 'codOrderConfirmation',
				role: 'codOrderConfirmation',
				group: 'tools',
				requiredFields: ['ID', 'orderConfirmation', 'status', 'orderID'],
				disabled: (selectedOrders) => {
					const statusCheck = handleStatusCheck(
						selectedOrders,
						'enabled',
						['needConfirmation'],
						'errorMsg.notAllowedForThisStatus'
					);

					if (statusCheck) return statusCheck;
				},
				action: (selectedOrders) => {
					const ConfirmedOrders = [];

					if (Array.isArray(selectedOrders)) {
						selectedOrders.forEach((order) => {
							if (order.orderConfirmation === true) {
								ConfirmedOrders.push(order.orderID);
							}
						});
					} else {
						if (selectedOrders.orderConfirmation === true) {
							ConfirmedOrders.push(selectedOrders.orderID);
						}
					}

					if (ConfirmedOrders && ConfirmedOrders.length === 0) {
						NiceModal.show(ConfirmationModal, {
							title: 'codOrderConfirmation',
							subTitle: 'codOrderConfirmationText',
							yesAction: async () => {
								dispatch(setLoading(true));

								try {
									const response = await axiosPrivate.post(ORDER_ACTIONS.COD_ORDER_CONFIRMATION, {
										otoOrderIDs: Array.isArray(selectedOrders)
											? selectedOrders.map((order) => order.ID)
											: [selectedOrders.ID]
									});
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								} catch (error) {
									AppLogger.error(error);
									showError({
										message: error?.message
									});
								}

								dispatch(setLoading(false));
							}
						});
					} else {
						showError({
							header: 'errorMsg.codOrderAlreadyConfirmed',
							persist: true,
							message: ConfirmedOrders.join(', ')
						});
					}
				}
			},
			{
				icon: faBarcode,
				label: 'packOrder',
				role: 'packOrder',
				group: 'order',
				requiredFields: ['ID', 'status', 'packingStatus', 'warehouseID', 'shipmentNumber'],
				disabled: (selecteds) => {
					const selectOnlyOneItem = handleSelectOnlyOneItem(selecteds);

					if (selectOnlyOneItem) return selectOnlyOneItem;
					if (!selecteds.warehouseID) return 'errorMsg.notAllowedForBranches';
					if (!selecteds.shipmentNumber) return 'errorMsg.cannotPackOrderWithoutShipmentNumber';

					const statusCheck = handleStatusCheck(
						selecteds,
						'enabled',
						[
							'assignedToWarehouse',
							'shipmentCreated',
							'shipmentCanceled',
							'confirmationCanceled',
							'confirmationFailed',
							'scheduledShipmentFailed',
							'searchingDriver',
							'goingToPickup'
						],
						'errorMsg.notAllowedForThisStatus'
					);
					if (statusCheck) return statusCheck;

					const packCheck = selecteds?.packingStatus !== 'picked';
					if (packCheck) return 'errorMsg.notAllowedForThisStatus';
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'packOrderTitle',
						subTitle: 'packOrderSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ADVANCE_WMS_MODULE.COMPLETE_PACKING, {
									otoID: selectedOrders.ID.toString()
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
									AppLogger.error(response);
								}
							} catch (error) {
								showError();
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTruckLoading,
				label: 'clearWave',
				role: 'clearWave',
				group: 'tools',
				requiredFields: ['ID', 'status', 'packingStatus'],
				disabled: (selecteds) => {
					const statusCheck = handleStatusCheck(
						selecteds,
						'enabled',
						[
							'assignedToWarehouse',
							'shipmentCreated',
							'shipmentCanceled',
							'confirmationCanceled',
							'confirmationFailed',
							'scheduledShipmentFailed',
							'searchingDriver',
							'goingToPickup'
						],
						'errorMsg.notAllowedForThisStatus'
					);
					if (statusCheck) return statusCheck;

					const packCheck = Array.isArray(selecteds)
						? selecteds.some((order) => order?.packingStatus === 'packed')
						: selecteds?.packingStatus === 'packed';
					if (packCheck) return 'errorMsg.notAllowedForThisStatus';
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'clearWaveTitle',
						subTitle: 'clearWaveSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ADVANCE_WMS_MODULE.CLEAR_WAVE_BY_ID, {
									otoIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								showError({
									message: error.response?.data.code || error.response?.data.message
								});
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			// ORDER END
			// SHIPMENT START
			{
				icon: faPeopleCarryBox,
				label: 'markAsPickedUp',
				role: 'markAsPickedUp',
				group: 'order',
				requiredFields: ['ID', 'status', 'OTPRequirement', 'isAggregator'],
				disabled: (selectedOrders) => {
					const dataArr = Array.isArray(selectedOrders) ? selectedOrders : [selectedOrders];

					if (Array.isArray(selectedOrders) && dataArr.some((obj) => obj.OTPRequirement))
						return 'errorMsg.cannotSelectMultipleWithOTP';

					const aggregatorCheck = Array.isArray(selectedOrders)
						? selectedOrders.some((order) => order?.isAggregator)
						: selectedOrders?.isAggregator;
					if (aggregatorCheck && !isAuthorized('SupportUserActions'))
						return 'errorMsg.notAllowedForThisStatus';
					return dataArr.some((obj) => obj.status === 'pickedUp')
						? 'errorMsg.alreadyMarkedAsPickedUp'
						: dataArr.some((obj) =>
									['readyForCollection', 'returnDestroyed', 'destroyed'].includes(obj.status)
							  )
							? 'errorMsg.notAllowedForThisStatus'
							: undefined;
				},
				action: (selectedOrders) => {
					if (!Array.isArray(selectedOrders) && selectedOrders.OTPRequirement) {
						NiceModal.show(BranchOTPForm, {
							id: selectedOrders.ID,
							onSubmitResult: (result) => {
								if (result) {
									dispatch(updateDynamicListsInPage());
								}
							}
						});
					} else {
						NiceModal.show(ConfirmationModal, {
							title: 'markAsPickedUp',
							subTitle: 'pickedUpConfirmationText',
							yesAction: async () => {
								dispatch(setLoading(true));

								try {
									const response = await axiosPrivate.post(ORDER_ACTIONS.MARK_AS_PICKED_UP, {
										otoOrderIDs: Array.isArray(selectedOrders)
											? selectedOrders.map((order) => order.ID)
											: [selectedOrders.ID]
									});
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								} catch (error) {
									AppLogger.error(error);
									showError({
										message: error?.response?.data?.code
									});
								}

								dispatch(setLoading(false));
							}
						});
					}
				}
			},
			{
				icon: faBan,
				label: 'cancelShipment',
				role: 'cancelShipment',
				group: 'order',
				requiredFields: ['ID', 'status', 'orderId'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						CANCEL_SHIPMENT_STATUSES,
						'errorMsg.cannotCancelShipmentForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(CancelShipmentConfirmationModal, {
						otoOrderIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.CANCEL_SHIPMENT, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									await fetchRemainingAmount();
									const successOrderIDs = [];

									const successOrders = [];
									const failedOrders = [];

									response.data.orders?.map((order) => {
										if (order.success === true) {
											successOrders.push(order.orderId);
											successOrderIDs.push(order.ID);
										}
										if (order.success === false) {
											failedOrders.push(order.orderId);
										}
									});

									dispatch(removeMultipleShipmentResults(successOrderIDs));
									dispatch(updateDynamicListsInPage());
									showSuccess({
										header: 'successMsg.cancelShipmentSuccessForTheseOrders',
										message: successOrders.join(', ')
									});
									if (failedOrders.length > 0) {
										showError({
											header: 'errorMsg.cancelShipmentFailedForTheseOrders',
											persist: true,
											message: failedOrders.join(', ')
										});
									}
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								error.response?.data.orders.map((order) => {
									showError({
										message: order.errorMsg || order.error?.code || order.error?.message
									});
								});

								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBan,
				label: 'cancelShipmentAdmin',
				role: 'cancelShipmentAdmin',
				group: 'order',
				requiredFields: ['ID', 'status', 'orderId'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'disabled',
						['destroyed', 'returnDestroyed'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrder) => {
					NiceModal.show(ConfirmationModal, {
						title: 'cancelShipmentTitle',
						subTitle: 'cancelShipmentConfirmationText',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.CANCEL_SHIPMENT_ADMIN, {
									IDs: Array.isArray(selectedOrder)
										? selectedOrder.map((order) => order.ID)
										: [selectedOrder.ID],
									reverse: false
								});
								if (response.status === 200) {
									if (response.data.failed?.length > 0) {
										response.data.failed.map((failedReason) => {
											showError({
												header: failedReason.otoOrderId,
												message: failedReason.error,
												persist: true
											});
										});
									} else {
										showSuccess();
									}
									await fetchRemainingAmount();
									dispatch(removeShipmentResult(selectedOrder.ID));
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								showError();
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBan,
				label: 'cancelReverseShipmentAdmin',
				role: 'cancelReverseShipmentAdmin',
				group: 'order',
				requiredFields: ['ID', 'status', 'orderId'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'disabled',
						['destroyed', 'returnDestroyed'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrder) => {
					NiceModal.show(ConfirmationModal, {
						title: 'cancelShipmentTitle',
						subTitle: 'cancelShipmentConfirmationText',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.CANCEL_SHIPMENT_ADMIN, {
									IDs: Array.isArray(selectedOrder)
										? selectedOrder.map((order) => order.ID)
										: [selectedOrder.ID],
									reverse: true
								});
								if (response.status === 200) {
									if (response.data.failed?.length > 0) {
										response.data.failed.map((failedReason) => {
											showError({
												header: failedReason.otoOrderId,
												message: failedReason.error,
												persist: true
											});
										});
									} else {
										showSuccess();
									}
									await fetchRemainingAmount();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								showError();
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBan,
				label: 'deleteOrder',
				role: 'deleteOrder',
				group: 'order',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						DELETE_ORDER_STATUSES,
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteOrderTitle',
						subTitle: 'deleteOrderConfirmationText',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.DELETE_ORDER, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									dispatch(updateDynamicListsInPage());
									showSuccess();
								} else {
									showError();
								}
							} catch (error) {
								showError();
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faLink,
				label: 'getReturnRequestUrl',
				role: 'getReturnRequestUrl',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'phone', 'email'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ReturnRequestUrlModal, {
						order: row
					});
				}
			},
			// SHIPMENT END
			// PRINT START
			{
				icon: faPrint,
				label: 'airWaybill',
				role: 'printAwb',
				group: 'print',
				requiredFields: ['ID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'disabled',
						PRINT_AWB_DISABLED_STATUSES,
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(PrintAWB, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID]
					}).then((resolved) => {
						const { internationalOrders = null } = resolved;
						if (internationalOrders?.length > 0) {
							NiceModal.show(InternationalProforma, {
								ids: internationalOrders
							});
						}
					});
				}
			},
			{
				icon: faPrint,
				label: 'returnAWB',
				role: 'printReturnAWB',
				group: 'print',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(PrintAWB, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						reverse: true
					});
				}
			},
			{
				icon: faFileInvoice,
				label: 'orderDetailsWithAWB',
				role: 'printOrderWithAWB',
				group: 'print',
				requiredFields: ['ID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'disabled',
						BEFORE_SHIPMENT_STATUSES,
						'errorMsg.notAllowedForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(PrintOrdersWithAWB, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID]
					});
				}
			},
			{
				icon: faFileSignature,
				label: 'dcPickupList',
				role: 'printPickupList',
				group: 'print',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(DCPickList, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID]
					});
				}
			},
			{
				icon: faRectangleList,
				label: 'orderDetails',
				role: 'printOrders',
				group: 'print',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					if (userType === 'branchUser') {
						NiceModal.show(PrintBranchLabel, {
							ids: Array.isArray(selectedOrders)
								? selectedOrders.map((order) => order.ID)
								: [selectedOrders.ID]
						});
					} else {
						NiceModal.show(PrintOrders, {
							ids: Array.isArray(selectedOrders)
								? selectedOrders.map((order) => order.ID)
								: [selectedOrders.ID]
						});
					}
				}
			},
			{
				icon: faFileDoc,
				label: 'pickListExport',
				role: 'pickListExport',
				group: 'print',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					const orderIDs = Array.isArray(selectedOrders)
						? selectedOrders.map((order) => order.ID)
						: [selectedOrders.ID];

					axiosPrivate
						.post(IMPORT_EXPORT.EXPORT_PICK_LIST_EXCEL, {
							orderIDs,
							isAdvancedWMS: isAuthorized('WMS_BinTracking')
						})
						.then((response) => {
							if (response.status === 200) {
								showSuccess({
									message: 'exportRequestTakenSuccess'
								});
							}
						})
						.catch((error) => {
							if (error?.response?.data?.code) {
								showError({
									message: error.response.data.code
								});
							} else {
								showError();
								AppLogger.error(error);
							}
						});
				}
			},
			{
				icon: faListCheck,
				label: 'pickListPDF',
				role: 'pickListPDF',
				group: 'print',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(PickListPDF, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						isAdvancedWMS: isAuthorized('WMS_BinTracking')
					});
				}
			},
			{
				icon: faFilePowerpoint,
				label: 'internationalProforma',
				role: 'printInternationalProforma',
				group: 'print',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(InternationalProforma, {
						ids: [row.ID]
					});
				}
			},
			{
				icon: faFilePowerpoint,
				label: 'returnShipmentDetails',
				role: 'printReturnShipmentDetails',
				group: 'print',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ReturnShipmentDetails, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID]
					});
				}
			},
			// PRINT END
			// TOOLS START
			{
				icon: faBoxes,
				label: 'waveCreationPilot',
				role: 'waveCreationPilot',
				group: 'otoPack',
				requiredFields: [],
				showAlways: true,
				action: () => {
					showModal('wave-pilot-form', { mode: 'add' });
				}
			},
			{
				icon: faBoxes,
				label: 'interDepotTransfer',
				role: 'interDepotTransfer',
				group: 'otoPack',
				requiredFields: ['ID', 'orderID', 'packageCount', 'name', 'cityName'],
				action: (orders) => {
					const ordersArray = Array.isArray(orders) ? orders : [orders];
					NiceModal.show(InterDepotTransfer, { orders: ordersArray });
				}
			},
			{
				icon: faBoxesPacking,
				label: 'createZonePickingWave',
				role: 'createZoneWave',
				group: 'otoPack',
				requiredFields: ['ID', 'warehouseID', 'waveID'],
				disabled: (orders) => {
					const warehouseCheck =
						Array.isArray(orders) && orders.some((order) => order?.warehouseID !== orders[0]?.warehouseID);

					const waveCheck = Array.isArray(orders) ? orders.some((order) => order?.waveID) : orders.waveID;

					if (waveCheck) return 'errorMsg.selectNotWavedOrders';
					if (warehouseCheck) return 'errorMsg.selectOrdersFromSameWarehouse';
					if (!Array.isArray(orders) && !orders.warehouseID) return 'errorMsg.notAllowedForBranches';

					return handleStatusCheck(
						orders,
						'enabled',
						[
							'assignedToWarehouse',
							'branchAssigned',
							'searchingDriver',
							'shipmentCreated',
							'shipmentCanceled',
							'confirmationCanceled',
							'confirmationFailed',
							'scheduledShipmentFailed'
						],
						'errorMsg.notAllowedForThisStatus'
					);
				},
				action: (orders) => {
					const ids = Array.isArray(orders) ? orders.map((order) => order.ID) : [orders.ID];
					const warehouseIDs = Array.isArray(orders)
						? orders.map((order) => order?.warehouseID)
						: [orders.warehouseID];
					NiceModal.show(CreateZoneWave, {
						ids: ids.join(','),
						whID: warehouseIDs[0]?.toString()
					});
				}
			},
			{
				icon: faBroadcastTower,
				label: 'createWaveWithSelectedOrders',
				role: 'createWave',
				group: 'otoPack',
				requiredFields: ['ID', 'warehouseID', 'status', 'waveID'],
				disabled: (orders) => {
					const warehouseCheck =
						Array.isArray(orders) && orders.some((order) => order?.warehouseID !== orders[0]?.warehouseID);

					const waveCheck = Array.isArray(orders) ? orders.some((order) => order?.waveID) : orders.waveID;

					if (waveCheck) return 'errorMsg.selectNotWavedOrders';
					if (warehouseCheck) return 'errorMsg.selectOrdersFromSameWarehouse';
					if (!Array.isArray(orders) && !orders.warehouseID) return 'errorMsg.notAllowedForBranches';

					return handleStatusCheck(
						orders,
						'enabled',
						[
							'assignedToWarehouse',
							'branchAssigned',
							'searchingDriver',
							'shipmentCreated',
							'shipmentCanceled',
							'confirmationCanceled',
							'confirmationFailed',
							'scheduledShipmentFailed'
						],
						'errorMsg.notAllowedForThisStatus'
					);
				},
				action: (orders) => {
					const ids = Array.isArray(orders) ? orders.map((order) => order.ID) : [orders.ID];
					const warehouseIDs = Array.isArray(orders)
						? orders.map((order) => order?.warehouseID)
						: [orders.warehouseID];

					NiceModal.show(CreateWave, {
						ids,
						whID: warehouseIDs[0]?.toString()
					});
				}
			},
			{
				icon: faBarcode,
				label: 'pack',
				role: 'startPacking',
				group: 'tools',
				requiredFields: ['id', 'packingStatus'],
				disabled: (selectedOrders) => {
					const selectOne = handleSelectOnlyOneItem(selectedOrders);
					if (selectOne) return selectOne;
					if (selectedOrders?.packingStatus === 'packed') return 'errorMsg.alreadyPacked';
				},
				action: (selectedOrder) => showModal('packing-form', { id: selectedOrder?.id })
			},
			// RMS START
			{
				icon: faCheck,
				label: 'approveReturn',
				role: 'approveReturn',
				group: 'order',
				requiredFields: ['ID', 'returnApprovalStatus'],
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((order) => order?.returnApprovalStatus === 'approved')
						: selecteds?.returnApprovalStatus === 'approved') && 'errorMsg.approvedAlready',
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'approveReturn',
						subTitle: 'approveReturnSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(RETURNS.RETURN_STATUS_CHANGE, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID],
									status: 'approved'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTimes,
				label: 'rejectReturn',
				role: 'rejectReturn',
				group: 'order',
				requiredFields: ['ID', 'returnApprovalStatus'],
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((order) => order?.returnApprovalStatus === 'rejected')
						: selecteds?.returnApprovalStatus === 'rejected') && 'errorMsg.rejectedAlready',
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'rejectReturn',
						subTitle: 'rejectReturnSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(RETURNS.RETURN_STATUS_CHANGE, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID],
									status: 'rejected'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			// RMS END
			// BRANCH START
			{
				icon: faBox,
				label: 'clickAndCollect',
				role: 'clickAndCollect',
				group: 'order',
				requiredFields: ['ID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						['pickupFromStore'],
						'errorMsg.cannotClickAndCollectForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'clickAndCollect',
						subTitle: 'clickAndCollectSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.CLICK_AND_COLLECT_UPDATE, {
									ids: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCircleCheck,
				label: 'collectedByCustomer',
				role: 'collectedByCustomer',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'status'],
				disabled: (selectedOrders) =>
					handleStatusCheck(
						selectedOrders,
						'enabled',
						['readyForCollection'],
						'errorMsg.cannotCollectedByCustomerForThisStatus'
					),
				action: (selectedOrders) => {
					NiceModal.show(CollectedByCustomerForm, {
						status: 'delivered',
						otoOrderID: selectedOrders.ID,
						orderID: selectedOrders.orderID
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'assignToDriver',
				role: 'assignToDriver',
				group: 'order',
				requiredFields: ['ID', 'orderID', 'integrationName'],
				disabled: (selectedOrders) => {
					if (selectedOrders.length)
						return (
							selectedOrders.some((order) => order.integrationName !== 'otoDriverApp') &&
							'errorMsg.selectOtoFlexOrder'
						);
					else return selectedOrders?.integrationName !== 'otoDriverApp' && 'errorMsg.selectOtoFlexOrder';
				},
				action: (selectedOrders) => {
					NiceModal.show(AssingToDriverForm, {
						ids: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.ID)
							: [selectedOrders.ID],
						customIDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.orderID)
							: [selectedOrders.orderID],
						mode: 'order'
					});
				}
			},
			{
				icon: faXmark,
				label: 'cancelDriver',
				role: 'cancelDriver',
				group: 'order',
				requiredFields: ['ID', 'integrationName'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedOrders) => {
					if (selectedOrders.length)
						return (
							selectedOrders.some((order) => order.integrationName !== 'otoDriverApp') &&
							'errorMsg.selectOtoFlexOrder'
						);
					else return selectedOrders?.integrationName !== 'otoDriverApp' && 'errorMsg.selectOtoFlexOrder';
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'cancelDriver',
						subTitle: 'cancelDriverSubtitle.confirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(OTOFLEX.CANCEL_DRIVER, {
									orderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order?.ID)
										: [selectedOrders?.ID]
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
									dispatch(setLoading(false));
									return;
								}
								showError();
							} catch (error) {
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBroadcastTower,
				label: 'broadcastToFleet',
				role: 'broadcastToFleet',
				group: 'order',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'broadcastToFleet',
						subTitle: 'broadcastToFleetSubtitle.confirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(OTOFLEX.BULK_BROADCAST_TO_FLEET, {
									orderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order?.ID)
										: [selectedOrders?.ID]
								});
								if (response.status === 200) {
									showSuccess();
									return;
								}
								showError();
							} catch (error) {
								AppLogger.error(error);
							} finally {
								dispatch(setLoading(false));
							}
						}
					});
				}
			},
			{
				icon: faImages,
				label: 'viewProofs',
				role: 'viewPOD',
				group: 'order',
				requiredFields: ['ID', 'driverName', 'integrationName'],
				headerItem: true,
				disabled: (selectedOrders) =>
					handleSelectOnlyOneItem(selectedOrders) ||
					(!selectedOrders?.driverName &&
						selectedOrders?.integrationName !== 'otoDriverApp' &&
						'errorMsg.selectOtoFlexOrder'),
				action: (selectedOrder) => {
					NiceModal.show(ViewPOD, { ID: selectedOrder.ID });
				}
			},
			{
				icon: faArrowsRotate,
				label: 'checkShipmentStatus',
				role: 'checkShipmentStatus',
				group: 'order',
				requiredFields: ['ID'],
				disabled: (selectedOrders) =>
					selectedOrders.length > 20 ? 'errorMsg.max20OrdersForCheckShipmentStatus' : undefined,
				action: async (selectedOrders) => {
					dispatch(setLoading(true));

					try {
						const response = await axiosPrivate.post(ORDER_ACTIONS.CHECK_SHIPMENT_STATUS, {
							ids: Array.isArray(selectedOrders)
								? selectedOrders.map((order) => order?.ID)
								: [selectedOrders?.ID]
						});

						if (response.status === 200) {
							showSuccess();
							dispatch(updateDynamicListsInPage());
						} else {
							showError();
						}
					} catch (error) {
						AppLogger.error(error);
					}

					dispatch(setLoading(false));
				}
			},
			{
				icon: faEye,
				label: 'viewOrder',
				role: 'viewOTOStoreOrder',
				group: 'order',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (selectedOrders) => handleSelectOnlyOneItem(selectedOrders),
				action: (row) => NiceModal.show(ViewOrderForm, { id: row?.ID, apiSource: OTO_STORE.GET_ORDER_BY_ID })
			}
		],
		[ACTION_OBJECT_TYPES.WAREHOUSE]: [
			{
				icon: faShop,
				label: 'activateWarehouse',
				role: 'activateWarehouse',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return selectedRows.some((row) => row.status === 'active') && 'errorMsg.alreadyActive';
					else return selectedRows?.status === 'active' && 'errorMsg.alreadyActive';
				},
				action: (selectedWarehouses) => {
					axiosPrivate
						.post(WAREHOUSES.UPDATE_WAREHOUSE_STATUS, {
							IDs: Array.isArray(selectedWarehouses)
								? selectedWarehouses.map((warehouse) => warehouse.ID)
								: [selectedWarehouses.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(getPickupLocationCountries({ invalidate: true }));
						});
				}
			},
			{
				icon: faShopSlash,
				label: 'deactivateWarehouse',
				role: 'deactivateWarehouse',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return selectedRows.some((row) => row.status === 'inactive') && 'errorMsg.alreadyInActive';
					else return selectedRows?.status === 'inactive' && 'errorMsg.alreadyInActive';
				},
				action: (selectedWarehouses) => {
					axiosPrivate
						.post(WAREHOUSES.UPDATE_WAREHOUSE_STATUS, {
							IDs: Array.isArray(selectedWarehouses)
								? selectedWarehouses.map((warehouse) => warehouse.ID)
								: [selectedWarehouses.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(getPickupLocationCountries({ invalidate: true }));
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editWarehouse',
				role: 'editWarehouse',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('warehouse-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'viewWarehouse',
				role: 'viewWarehouse',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('warehouse-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faEye,
				label: 'setWarehouseWorkingHoursAllDay',
				role: 'setWarehouseWorkingHoursAllDay',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(FullDayWorkingHoursModal, {
						ids: Array.isArray(row) ? row.map((warehouse) => warehouse.ID) : [row.ID],
						endpointUrl: WAREHOUSES.SET_WAREHOUSE_WORKING_HOURS_ALL_DAY,
						type: 'warehouse'
					});
				}
			},
			{
				icon: faEye,
				label: 'updateServingRadiusForWarehouse',
				role: 'updateServingRadiusForWarehouse',
				group: 'tools',
				requiredFields: ['ID'],
				action: (row) => {
					NiceModal.show(UpdateRadiusForm, {
						ids: Array.isArray(row) ? row.map((warehouse) => warehouse.ID) : [row.ID],
						mode: 'warehouse',
						onSubmitResult: (result) => {
							if (result) dispatch(updateDynamicListsInPage());
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editSenderLocation',
				role: 'editSenderLocation',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddSenderLocationForm, { id: row.ID, mode: 'edit' });
				}
			},
			{
				icon: faShop,
				label: 'activateSenderLocation',
				role: 'activateSenderLocation',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return selectedRows.some((row) => row.status === 'active') && 'errorMsg.alreadyActive';
					else return selectedRows?.status === 'active' && 'errorMsg.alreadyActive';
				},
				action: (selectedItems) => {
					axiosPrivate
						.post(WAREHOUSES.UPDATE_SENDER_LOCATION_STATUS, {
							IDs: Array.isArray(selectedItems)
								? selectedItems.map((item) => item.ID)
								: [selectedItems.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(getPickupLocationCountries({ invalidate: true }));
						});
				}
			},
			{
				icon: faShopSlash,
				label: 'deactivateSenderLocation',
				role: 'deactivateSenderLocation',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return selectedRows.some((row) => row.status === 'inactive') && 'errorMsg.alreadyInActive';
					else return selectedRows?.status === 'inactive' && 'errorMsg.alreadyInActive';
				},
				action: (selectedItems) => {
					axiosPrivate
						.post(WAREHOUSES.UPDATE_SENDER_LOCATION_STATUS, {
							IDs: Array.isArray(selectedItems)
								? selectedItems.map((item) => item.ID)
								: [selectedItems.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(getPickupLocationCountries({ invalidate: true }));
						});
				}
			}
		],
		[ACTION_OBJECT_TYPES.BRANCH]: [
			{
				icon: faShop,
				label: 'activateBranch',
				role: 'activateBranch',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'green-item',
				disabled: (rows) =>
					(Array.isArray(rows) ? rows.some((row) => row?.status === 'active') : rows?.status === 'active') &&
					'errorMsg.userCannotBeDeactivated',
				action: (selectedBranches) => {
					axiosPrivate
						.post(BRANCHES.UPDATE_BRANCH_STATUS, {
							IDs: Array.isArray(selectedBranches)
								? selectedBranches.map((warehouse) => warehouse.ID)
								: [selectedBranches.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(getPickupLocationCountries({ invalidate: true }));
						});
				}
			},
			{
				icon: faShopSlash,
				label: 'deactivateBranch',
				role: 'deactivateBranch',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (rows) =>
					(Array.isArray(rows)
						? rows.some((row) => row?.status === 'inactive')
						: rows?.status === 'inactive') && 'errorMsg.userCannotBeDeactivated',
				action: (selectedBranches) => {
					axiosPrivate
						.post(BRANCHES.UPDATE_BRANCH_STATUS, {
							IDs: Array.isArray(selectedBranches)
								? selectedBranches.map((warehouse) => warehouse.ID)
								: [selectedBranches.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(getPickupLocationCountries({ invalidate: true }));
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBranch',
				role: 'editBranch',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('branch-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'viewBranch',
				role: 'viewBranch',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('branch-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faEye,
				label: 'setBranchWorkingHoursAllDay',
				role: 'setBranchWorkingHoursAllDay',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(FullDayWorkingHoursModal, {
						ids: Array.isArray(row) ? row.map((branch) => branch.ID) : [row.ID],
						endpointUrl: BRANCHES.SET_BRANCH_WORKING_HOURS_ALL_DAY,
						type: 'branch'
					});
				}
			},
			{
				icon: faEye,
				label: 'updateServingRadius',
				role: 'updateServingRadius',
				group: 'tools',
				requiredFields: ['ID'],
				action: (row) => {
					NiceModal.show(UpdateRadiusForm, {
						ids: Array.isArray(row) ? row.map((branch) => branch.ID) : [row.ID],
						mode: 'branch',
						onSubmitResult: (result) => {
							if (result) dispatch(updateDynamicListsInPage());
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.ATTRIBUTES_GROUPS]: [
			{
				icon: faTrashCan,
				label: 'deleteAttributesGroup',
				role: 'deleteAttributesGroup',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteAttributesGroup',
						subTitle: 'deleteAttributesGroupConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(WMS_MODULE.DELETE_ATTRIBUTES_GROUP, {
									groupIDs: Array.isArray(rows) ? rows.map((row) => row.ID) : [rows.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.PRODUCT_ATTRIBUTES]: [
			{
				icon: faPenToSquare,
				label: 'editProductAttribute',
				role: 'editProductAttribute',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedProduct) => {
					showModal('product-attribute-form', {
						mode: 'edit',
						id: selectedProduct?.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewProductAttribute',
				role: 'viewProductAttribute',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedProduct) => {
					showModal('product-attribute-form', {
						mode: 'view',
						id: selectedProduct?.ID
					});
				}
			},
			{
				icon: faBookmark,
				label: 'assignGroup',
				role: 'assignGroup',
				group: 'tools',
				requiredFields: ['ID'],
				action: (rows) =>
					NiceModal.show(AttributesGroupAssignForm, {
						ids: Array.isArray(rows) ? rows.map((row) => row.ID) : [rows.ID]
					})
			},
			{
				icon: faBookmarkSlash,
				label: 'unassignGroup',
				role: 'unassignGroup',
				group: 'tools',
				requiredFields: ['ID'],
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'unassignProductAttributeGroup',
						subTitle: 'unassignProductAttributeGroupConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(WMS_MODULE.UNASSIGN_PRODUCT_GROUP, {
									attributeIDs: Array.isArray(rows) ? rows.map((row) => row.ID) : [rows.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteProductAttribute',
				role: 'deleteProductAttribute',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteProductAttribute',
						subTitle: 'deleteProductAttributeConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(WMS_MODULE.DELETE_PRODUCT_ATTRIBUTE, {
									attributeIDs: Array.isArray(rows) ? rows.map((row) => row.ID) : [rows.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.PRODUCT]: [
			{
				icon: faScannerGun,
				label: 'productSpecs',
				role: 'productSpecs',
				group: 'tools',
				showAlways: true,
				requiredFields: ['ID'],
				action: () => showModal('product-specs-modal')
			},
			{
				icon: faBarcode,
				label: 'barcodeGenerator',
				role: 'barcodeGenerate',
				group: 'tools',
				showAlways: true,
				requiredFields: ['ID'],
				action: () => NiceModal.show(BarcodeGeneratorModal)
			},
			{
				icon: faPenToSquare,
				label: 'editProduct',
				role: 'editProduct',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedProduct) => {
					showModal('product-form', {
						mode: 'edit',
						id: selectedProduct?.ID
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCategory',
				role: 'editCategory',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(CategoryForm, {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'viewCategory',
				role: 'viewCategory',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CategoryForm, {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCategory',
				role: 'deleteCategory',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				className: 'red-item',
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCategory',
						subTitle: 'deleteCategoryConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(CATEGORY.DELETE_CATEGORY, {
									id: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response?.data?.code || error?.response?.data?.message
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateProductCategory',
				role: 'updateProductCategory',
				group: 'tools',
				requiredFields: ['ID', 'sku'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(UpdateProductCategory, {
						ids: Array.isArray(row) ? row.map((row) => row.ID) : row.ID,
						skus: Array.isArray(row) ? row.map((row) => row.sku) : row.sku
					});
				}
			},
			{
				icon: faEye,
				label: 'viewProduct',
				role: 'viewProduct',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedProduct) => {
					showModal('product-form', {
						mode: 'view',
						id: selectedProduct?.ID
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteProduct',
				role: 'deleteProduct',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteProduct',
						subTitle: 'deleteProductConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRODUCTS.DELETE_PRODUCT, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.OTOFLEX]: [
			{
				icon: faUserCheck,
				label: 'activateDriver',
				role: 'activateDriver',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selecteds) =>
					(
						Array.isArray(selecteds)
							? selecteds.some((user) => user?.status === 'active')
							: selecteds?.status === 'active'
					)
						? 'errorMsg.userCannotBeActivated'
						: Array.isArray(selecteds)
							? selecteds.some((user) => user?.status === 'invited')
							: selecteds?.status === 'invited' && 'errorMsg.statusInvited',
				action: (selectedUsers) => {
					axiosPrivate
						.post(OTOFLEX.UPDATE_DRIVER_STATUS, {
							IDs: Array.isArray(selectedUsers)
								? selectedUsers.map((user) => user.ID)
								: [selectedUsers.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch(() => {
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivateDriver',
				role: 'deactivateDriver',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((user) => user?.status === 'inactive')
						: selecteds?.status === 'inactive') && 'errorMsg.userCannotBeDeactivated',
				action: (selectedUsers) => {
					axiosPrivate
						.post(OTOFLEX.UPDATE_DRIVER_STATUS, {
							IDs: Array.isArray(selectedUsers)
								? selectedUsers.map((user) => user.ID)
								: [selectedUsers.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch(() => {
							showError();
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDriver',
				role: 'editDriver',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('driver-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'viewDriver',
				role: 'viewDriver',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('driver-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'addTransaction',
				role: 'addTransaction',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddTransactionForm, {
						id: row.ID,
						mode: 'view',
						fullName: row.fullName,
						balance: row.cashBalance
					});
				}
			},
			{
				icon: faListCheck,
				label: 'viewTransactionHistory',
				role: 'viewTransactionHistory',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ViewTransactionHistory, {
						id: row.ID,
						fullName: row.fullName,
						balance: row.cashBalance
					});
				}
			},
			{
				icon: faRoad,
				label: 'trackDriver',
				role: 'trackDriver',
				group: 'tools',
				requiredFields: ['ID', 'lat', 'lng'],
				disabled: (selecteds) => {
					if (handleSelectOnlyOneItem(selecteds)) return handleSelectOnlyOneItem(selecteds);
					if (!selecteds?.lat || !selecteds?.lng) return 'errorMsg.driverTrackingNotAvailable';
				},
				action: (row) => {
					showModal('driver-track-form', {
						id: row.ID
					});
				}
			},
			{
				icon: faShareNodes,
				label: 'requestResetPasswordLink',
				role: 'requestResetPasswordLink',
				group: 'tools',
				requiredFields: ['ID', 'email'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ResetPasswordForm, {
						id: row.ID,
						email: row.email
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.USER]: [
			{
				icon: faUserCheck,
				label: 'activateUser',
				role: 'activateUser',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selecteds) =>
					(
						Array.isArray(selecteds)
							? selecteds.some((user) => user?.status === 'active')
							: selecteds?.status === 'active'
					)
						? 'errorMsg.userCannotBeActivated'
						: Array.isArray(selecteds)
							? selecteds.some((user) => user?.status === 'invited')
							: selecteds?.status === 'invited' && 'errorMsg.statusInvited',
				action: (selectedUsers) => {
					axiosPrivate
						.post(USERS.UPDATE_USER_STATUS, {
							IDs: Array.isArray(selectedUsers)
								? selectedUsers.map((user) => user.ID)
								: [selectedUsers.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							showError({
								message: error.response?.data.code || error.response?.data.message
							});
							AppLogger.error(error);
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivateUser',
				role: 'deactivateUser',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selecteds) =>
					(
						Array.isArray(selecteds)
							? selecteds.some((user) => user?.status === 'inactive')
							: selecteds?.status === 'inactive'
					)
						? 'errorMsg.userCannotBeDeactivated'
						: Array.isArray(selecteds)
							? selecteds.some((user) => user?.status === 'invited')
							: selecteds?.status === 'invited' && 'errorMsg.statusInvited',
				action: (selectedUsers) => {
					axiosPrivate
						.post(USERS.UPDATE_USER_STATUS, {
							IDs: Array.isArray(selectedUsers)
								? selectedUsers.map((user) => user.ID)
								: [selectedUsers.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							showError({
								message: error.response?.data.code || error.response?.data.message
							});
							AppLogger.error(error);
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editUser',
				role: 'editUser',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					const isWmsForm =
						location.pathname === mergePaths(PATHS.WMS_CONFIGURATION, PATHS.WMS_CONFIGURATION_USERS);

					showModal(isWmsForm ? 'wms-user-form' : 'user-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'viewUser',
				role: 'viewUser',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('user-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faShareNodes,
				label: 'requestResetPasswordLink',
				role: 'requestResetPasswordLink',
				group: 'tools',
				requiredFields: ['ID', 'email'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ResetPasswordForm, {
						id: row.ID,
						email: row.email
					});
				}
			},
			{
				icon: faUserGear,
				label: 'modifyRoles',
				role: 'modifyRoles',
				group: 'roles',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('modify-roles-modal', {
						id: row.ID
					});
				}
			},
			{
				icon: faRotateLeft,
				label: 'restoreRoles',
				role: 'restoreRoles',
				group: 'roles',
				requiredFields: ['ID'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'restoreRolesTitle',
						subTitle: 'restoreRolesDescription',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(USERS.RESTORE_CUSTOM_ROLES, {
									IDs: Array.isArray(row) ? row.map((user) => user.ID) : [row.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'applyRoleTemplate',
				role: 'applyRoleTemplate',
				group: 'roles',
				requiredFields: ['ID', 'roleTemplateID'],
				action: (selectedRows) => {
					NiceModal.show(ApplyRoleTemplateModal, {
						ids: Array.isArray(selectedRows) ? selectedRows.map((row) => row.ID) : [selectedRows.ID],
						roleTemplateIDs: Array.isArray(selectedRows)
							? selectedRows.map((row) => row.roleTemplateID)
							: [selectedRows.roleTemplateID]
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.DELIVERY_SLOT]: [
			{
				icon: faPenToSquare,
				label: 'editDeliverySlot',
				role: 'editDeliverySlot',
				group: 'tools',
				requiredFields: ['ID', 'deliveryCompanySettingsId'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DeliverySlotConfigurationForm, {
						mode: 'edit',
						id: row.ID,
						deliveryCompanySettingsId: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faEye,
				label: 'viewDeliverySlot',
				role: 'viewDeliverySlot',
				group: 'tools',
				requiredFields: ['ID', 'deliveryCompanySettingsId'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DeliverySlotConfigurationForm, {
						id: row.ID,
						deliveryCompanySettingsId: row.deliveryCompanySettingsId,
						mode: 'view'
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDeliverySlot',
				role: 'deleteDeliverySlot',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDeliverySlot',
						subTitle: 'deleteDeliverySlotConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_DELIVERY_SLOT_METHOD, {
									deliverySlotGroupId: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.BOX]: [
			{
				icon: faPenToSquare,
				label: 'editBox',
				role: 'editBox',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('box-form', { id: row.ID, mode: 'edit' });
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBox',
				role: 'deleteBox',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBox',
						subTitle: 'deleteBoxConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(BOXES.DELETE_BOX, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.WMS]: [
			{
				icon: faPenToSquare,
				label: 'editInventory',
				role: 'editInventory',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('inventory-form', { id: row.id, mode: 'edit' });
				}
			},
			{
				icon: faEye,
				label: 'viewInventory',
				role: 'viewInventory',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('inventory-form', { id: row.id, mode: 'view' });
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBin',
				role: 'editBin',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => showModal('bin-form', { id: row.id, mode: 'edit' })
			},
			{
				icon: faEye,
				label: 'viewBin',
				role: 'viewBin',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => showModal('bin-form', { id: row.id, mode: 'view' })
			},
			{
				icon: faTrashCan,
				label: 'deleteBin',
				role: 'deleteBin',
				group: 'tools',
				requiredFields: ['id', 'isItemInInventory'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedBins) =>
					(Array.isArray(selectedBins)
						? selectedBins.every((bin) => !!bin.isItemInInventory)
						: !!selectedBins.isItemInInventory) && 'errorMsg.canNotDeleteBin',
				action: (selectedBins) => {
					const body = Array.isArray(selectedBins)
						? { binIDs: selectedBins.map((bin) => bin.id) }
						: { binIDs: [selectedBins.id] };
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBin',
						subTitle: 'confirmation.deleteBin',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_BIN, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPen,
				label: 'updateZone',
				role: 'updateBinZone',
				group: 'tools',
				requiredFields: ['id', 'warehouseID', 'binName'],
				disabled: (selectedBins) => {
					if (!Array.isArray(selectedBins)) return;
					const wID = selectedBins[0].warehouseID;
					if (selectedBins.find((bin) => bin.warehouseID !== wID)) {
						return 'errorMsg.selectBinsFromSameWarehouse';
					}
				},
				action: (selectedBins) => {
					const wID = Array.isArray(selectedBins) ? selectedBins[0].warehouseID : selectedBins.warehouseID;
					const ids = Array.isArray(selectedBins) ? selectedBins.map((bin) => bin.id) : [selectedBins.id];
					const binNames = Array.isArray(selectedBins)
						? selectedBins.map((bin) => bin.binName)
						: [selectedBins.binName];
					NiceModal.show(UpdateBinZone, { ids, binNames, wID });
				}
			},
			{
				icon: faLink,
				label: 'wms.showBinTransactions',
				role: 'showBinTransactions',
				group: 'tools',
				requiredFields: ['id', 'sku', 'binName'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					navigate(PATHS.BIN_TRANSACTIONS, {
						state: {
							defaultFilters: [
								...(pathname.includes(mergePaths(PATHS.OPERATIONS, PATHS.WAVES))
									? [
											{
												column: {
													Header: 'waveId',
													accessor: 'waveID',
													filterType: FilterType.number,
													roleName: 'WMS_BinTracking',
													width: 30
												},
												action: FilterAction.is,
												value: row.id
											}
										]
									: pathname.includes(mergePaths(PATHS.INVENTORY, PATHS.BIN_INVENTORY))
										? [
												{
													column: {
														Header: 'sku',
														accessor: 'sku',
														filterType: FilterType.string,
														width: 30
													},
													action: FilterAction.is,
													value: row.sku
												},
												{
													column: {
														Header: 'binName',
														accessor: 'binName',
														filterType: FilterType.multiSelection,
														width: 30
													},
													action: FilterAction.is,
													value: [row.binName]
												}
											]
										: pathname.includes(mergePaths(PATHS.WMS_CONFIGURATION, PATHS.BINS))
											? [
													{
														column: {
															Header: 'binName',
															accessor: 'binName',
															filterType: FilterType.multiSelection,
															width: 30
														},
														action: FilterAction.is,
														value: [row.binName]
													}
												]
											: [
													{
														column: {
															Header: 'orderID',
															accessor: 'incrementId',
															filterType: FilterType.string,
															width: 30
														},
														action: FilterAction.is,
														value: row.id
													}
												])
							]
						}
					});
				}
			},
			{
				icon: faLink,
				label: 'wms.showInventoryTransactions',
				role: 'showInventoryTransactions',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					navigate(PATHS.INVENTORY_TRANSACTIONS, {
						state: {
							defaultFilters: [
								{
									column: {
										Header: 'orderID',
										accessor: 'orderNumber',
										filterType: FilterType.string,
										width: 30
									},
									action: FilterAction.is,
									value: row.id
								}
							]
						}
					});
				}
			},
			{
				icon: faLink,
				label: 'updateTransactionReport',
				role: 'updateTransactionReport',
				group: 'tools',
				requiredFields: ['id'],
				action: (rows) => {
					const otoIds = Array.isArray(rows) ? rows.map((order) => order.id) : [rows.id];
					dispatch(setLoading(true));
					axiosPrivate
						.post(WMS_MODULE.UPDATE_ORDER_BASED_TRANSACTIONS, {
							otoIds,
							page: 'operations'
						})
						.then((response) => {
							if (response.status === 200) {
								showSuccess({
									message: 'updatedTransactionReportInfoMessage'
								});
							} else {
								AppLogger.error(response);
								showError();
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(setLoading(false));
						});
				}
			},
			{
				icon: faEye,
				label: 'viewWave',
				role: 'viewWave',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					showModal('wave-form', { id: row.id, mode: 'view' });
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteWave',
				role: 'deleteWave',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedWaves) =>
					handleStatusCheck(selectedWaves, 'enabled', ['new'], 'errorMsg.notAllowedForThisStatus'),
				action: (selectedWaves) => {
					const body = Array.isArray(selectedWaves)
						? { waveIDs: selectedWaves.map((wave) => wave.id) }
						: { waveIDs: [selectedWaves.id] };
					NiceModal.show(ConfirmationModal, {
						title: 'deleteWave',
						subTitle: 'confirmation.deleteWave',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_WAVE, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess({
											header: 'success',
											message: `${t('successMessages.waveDeleted')} ${response.data?.ids.join(
												', '
											)}`
										});
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									error?.data?.unsuccessful?.forEach(({ reason, ids }) => {
										showError({
											header: 'error',
											message: `${t(reason)} ${ids}`
										});
									});
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPlay,
				label: 'startWavePicking',
				role: 'startWavePicking',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (selectedWaves) =>
					handleSelectOnlyOneItem(selectedWaves) ||
					handleStatusCheck(
						selectedWaves,
						'enabled',
						['new', 'partiallyCompleted'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: ({ id }) => {
					const body = { id };
					NiceModal.show(ConfirmationModal, {
						title: 'startWavePicking',
						subTitle: 'confirmation.startWavePicking',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.START_WAVE_PICKING, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBallotCheck,
				label: 'completeWavePicking',
				role: 'completePicking',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (selectedWaves) =>
					handleSelectOnlyOneItem(selectedWaves) ||
					handleStatusCheck(
						selectedWaves,
						'enabled',
						['new', 'partiallyCompleted', 'inprogress'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: ({ id }) => showModal('wave-picking-form', { id })
			},
			{
				icon: faTrashCan,
				label: 'deleteInventory',
				role: 'deleteInventory',
				group: 'tools',
				requiredFields: ['id', 'quantityOnHand', 'quantityForecasted'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedInventories) => {
					const errorMsg = 'errorMsg.canNotDeleteInventory';
					try {
						if (selectedInventories?.length) {
							return (
								selectedInventories.some(
									({ quantityOnHand, quantityForecasted }) => quantityOnHand || quantityForecasted
								) && errorMsg
							);
						}
						return (
							!!(selectedInventories?.quantityOnHand || selectedInventories?.quantityForecasted) &&
							errorMsg
						);
					} catch (error) {
						AppLogger.error(error);
					}
				},
				action: (selectedInventories) => {
					const body = Array.isArray(selectedInventories)
						? { inventoryIDs: selectedInventories.map((inventory) => inventory.id) }
						: { inventoryIDs: [selectedInventories.id] };
					NiceModal.show(ConfirmationModal, {
						title: 'deleteInventory',
						subTitle: 'confirmation.deleteInventory',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_INVENTORY, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteInventoryOrder',
				role: 'deleteInventoryOrder',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedInventoryOrders) => {
					if (Array.isArray(selectedInventoryOrders)) {
						const found = selectedInventoryOrders.every(
							({ status }) => status === 'new' || status === 'assigned'
						);

						if (!found) return 'errorMsg.canNotDeleteInventoryOrder';
					} else {
						if (selectedInventoryOrders.status !== 'new' && selectedInventoryOrders.status !== 'assigned')
							return 'errorMsg.canNotDeleteInventoryOrder';
					}
				},
				action: (selectedInventoryOrders) => {
					const inventoryOrderIDs = Array.isArray(selectedInventoryOrders)
						? selectedInventoryOrders.map((selectedInventoryItem) => selectedInventoryItem.id)
						: [selectedInventoryOrders.id];

					NiceModal.show(ConfirmationModal, {
						title: 'deleteInventoryOrder',
						subTitle: 'confirmation.deleteInventoryOrder',
						yesAction: async () => {
							try {
								dispatch(setLoading(true));

								await axiosPrivate
									.post(ADVANCE_WMS_MODULE.DELETE_INVENTORY_ORDER, {
										inventoryOrderIDs
									})
									.then((response) => {
										if (response.status === 200) {
											showSuccess();

											dispatch(updateDynamicListsInPage());
										} else {
											AppLogger.error(response);
										}
									});
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(setLoading(false));
							}
						}
					});
				}
			},
			{
				icon: faEquals,
				label: 'equalQuantityOnHandToBinInventoryAction',
				role: 'equalQuantityOnHandToBinInventoryAction',
				group: 'tools',
				requiredFields: ['id'],
				action: (selectedInventories) => {
					dispatch(setLoading(true));

					axiosPrivate
						.post(ADVANCE_WMS_MODULE.EQUAL_QUANTITY_ONHAND, {
							ids: Array.isArray(selectedInventories)
								? selectedInventories.map((inventory) => inventory.id)
								: [selectedInventories.id]
						})
						.then((response) => {
							const { data, status } = response;
							if (status === 200) {
								if (data?.updatedIds?.length > 0) {
									showSuccess({
										header: data?.message ?? 'quantityUpdate.success.message',
										message: data?.updatedIds?.join(', ')
									});
								}
								if (data?.notUpdatedIds?.length > 0) {
									showError({
										header: 'quantityUpdate.error.message',
										message: data?.notUpdatedIds?.join(', ')
									});
								}
								dispatch(updateDynamicListsInPage());
							} else {
								AppLogger.error(response);
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(setLoading(false));
						});
				}
			},
			{
				icon: faArrowUpFromGroundWater,
				label: 'updateQuantityAvailableAction',
				role: 'updateQuantityAvailableAction',
				group: 'tools',
				requiredFields: ['id'],
				action: async (selectedInventories) => {
					dispatch(setLoading(true));

					await axiosPrivate
						.post(ADVANCE_WMS_MODULE.UPDATE_QUANTITY_AVAILABLE, {
							ids: Array.isArray(selectedInventories)
								? selectedInventories.map((inventory) => inventory.id)
								: [selectedInventories.id]
						})
						.then((response) => {
							const { data, status } = response;
							if (status === 200) {
								if (data?.updatedIds?.length > 0) {
									showSuccess({
										header: data?.message ?? 'quantityUpdate.success.message',
										message: data?.updatedIds?.join(', ')
									});
								}
								if (data?.notUpdatedIds?.length > 0) {
									showError({
										header: 'quantityUpdate.error.message',
										message: data?.notUpdatedIds?.join(', ')
									});
								}

								dispatch(updateDynamicListsInPage());
							} else {
								AppLogger.error(response);
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});

					dispatch(setLoading(false));
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateMinInventory',
				role: 'editInventory',
				group: 'tools',
				requiredFields: ['id'],
				action: (selectedInventories) => {
					const ids = Array.isArray(selectedInventories)
						? selectedInventories.map((inventoryItem) => inventoryItem.id)
						: [selectedInventories.id];
					NiceModal.show(UpdateMinInventoryForm, {
						ids
					});
				}
			},
			{
				icon: faCheck,
				label: 'activateZone',
				role: 'activateZone',
				group: 'tools',
				requiredFields: ['id', 'active'],
				footerItem: true,
				className: 'green-item',
				disabled: (selectedZones) =>
					(
						Array.isArray(selectedZones)
							? selectedZones.some((zone) => zone.active === 'active')
							: selectedZones.active === 'active'
					)
						? 'errorMsg.canNotActivateZone'
						: false,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'activateZone',
						subTitle: 'confirmation.activateZone',
						yesAction: () => {
							dispatch(setLoading(true));

							axiosPrivate
								.post(ADVANCE_WMS_MODULE.CHANGE_ZONE_STATUS, {
									ids: Array.isArray(row) ? row.map(({ id }) => id) : [row.id],
									activate: true
								})
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => dispatch(setLoading(false)));
						}
					});
				}
			},
			{
				icon: faXmark,
				label: 'deactivateZone',
				role: 'deactivateZone',
				group: 'tools',
				requiredFields: ['id', 'active'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedZones) =>
					(
						Array.isArray(selectedZones)
							? selectedZones.some((zone) => zone.active === 'inactive')
							: selectedZones.active === 'inactive'
					)
						? 'errorMsg.canNotDeactivateZone'
						: false,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deactivateZone',
						subTitle: 'confirmation.deactivateZone',
						yesAction: () => {
							dispatch(setLoading(true));

							axiosPrivate
								.post(ADVANCE_WMS_MODULE.CHANGE_ZONE_STATUS, {
									ids: Array.isArray(row) ? row.map(({ id }) => id) : [row.id],
									activate: false
								})
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => dispatch(setLoading(false)));
						}
					});
				}
			},
			{
				icon: faPen,
				label: 'editVendor',
				role: 'editVendor',
				group: 'tools',
				requiredFields: ['ID', 'vendorName', 'email', 'accountNumber'],
				headerItem: true,
				action: (row) =>
					NiceModal.show(VendorForm, {
						mode: 'edit',
						data: row
					})
			},
			{
				icon: faEye,
				label: 'viewVendor',
				role: 'viewVendor',
				group: 'tools',
				requiredFields: ['ID', 'vendorName', 'email', 'accountNumber'],
				headerItem: true,
				action: (row) =>
					NiceModal.show(VendorForm, {
						mode: 'view',
						data: row
					})
			},
			{
				icon: faTrashCan,
				label: 'deleteVendor',
				role: 'deleteVendor',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedZones) => handleSelectOnlyOneItem(selectedZones),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteVendor',
						subTitle: 'confirmation.deleteVendor',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_VENDOR, { ID: row.ID })
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPen,
				label: 'editZone',
				role: 'editZone',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => showModal('zone-form', { mode: 'edit', id: row.id })
			},
			{
				icon: faEye,
				label: 'viewZone',
				role: 'viewZone',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => showModal('zone-form', { mode: 'view', id: row.id })
			},
			{
				icon: faTrashCan,
				label: 'deleteZone',
				role: 'deleteZone',
				group: 'tools',
				requiredFields: ['id', 'isAssignedToBins'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedZones) =>
					Array.isArray(selectedZones) &&
					selectedZones.some((zone) => !!zone.isAssignedToBins) &&
					'errorMsg.canNotDeleteZoneAssignedToBin',
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteZone',
						subTitle: 'confirmation.deleteZone',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_ZONE, {
									zoneIDs: Array.isArray(row) ? row.map(({ id }) => id) : [row.id]
								})
								.then((response) => {
									if (response.status === 200) {
										const successfullIDs = response.data.successfulZoneIds;
										const failedIDs = response.data.failedZoneIds;
										if (failedIDs?.length > 0) {
											showError({
												header: 'error.deleteZone',
												message: failedIDs.join(', ')
											});
											setLoading(false);
										}
										if (successfullIDs?.length > 0) {
											showSuccess({
												header: 'success.deleteZone',
												message: successfullIDs.join(', ')
											});
											setLoading(false);
										}
										dispatch(updateDynamicListsInPage());
									} else {
										showError();
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBinInventory',
				role: 'deleteBinInventory',
				group: 'tools',
				requiredFields: ['id', 'quantity'],
				disabled: (selectedBinInventories) => {
					if (Array.isArray(selectedBinInventories)) {
						const found = selectedBinInventories.every(({ quantity }) => quantity);
						if (found) return 'errorMsg.canNotDeleteBinInventory';
					} else {
						if (selectedBinInventories.quantity) return 'errorMsg.canNotDeleteBinInventory';
					}
				},
				action: (selectedBinInventories) => {
					const body = Array.isArray(selectedBinInventories)
						? { biIDs: selectedBinInventories.map((binInventory) => binInventory.id) }
						: { biIDs: [selectedBinInventories.id] };
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBinInventory',
						subTitle: 'confirmation.deleteBinInventory',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_BIN_INVENTORY, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteTransaction',
				role: 'deleteInventoryTransaction',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				action: (selectedInventoryTransactions) => {
					const body = Array.isArray(selectedInventoryTransactions)
						? {
								inventoryTransactionIDs: selectedInventoryTransactions.map(
									(inventoryTransaction) => inventoryTransaction.id
								)
							}
						: { inventoryTransactionIDs: [selectedInventoryTransactions.id] };
					NiceModal.show(ConfirmationModal, {
						title: 'deleteInventoryTransaction',
						subTitle: 'confirmation.deleteInventoryTransaction',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_INVENTORY_TRANSACTION, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBackward,
				label: 'revertInventory',
				role: 'revertTransaction',
				group: 'tools',
				requiredFields: ['id'],
				action: (selectedInventoryTransactions) => {
					const body = Array.isArray(selectedInventoryTransactions)
						? {
								inventoryTransactionIDs: selectedInventoryTransactions.map(
									(inventoryTransaction) => inventoryTransaction.id
								)
							}
						: { inventoryTransactionIDs: [selectedInventoryTransactions.id] };
					NiceModal.show(ConfirmationModal, {
						title: 'revertInventory',
						subTitle: 'confirmation.revertInventory',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.REVERTE_INVENTORY, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'assignPicker',
				role: 'assignPicker',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) => handleSelectOnlyOneItem(rows) || rows.status !== 'new',
				action: (row) => {
					NiceModal.show(AssignPickerForm, {
						id: row.id
					});
				}
			},
			// putaway
			{
				icon: faPenToSquare,
				label: 'editPutAway',
				role: 'editPutaway',
				group: 'tools',
				requiredFields: ['id', 'status'],
				headerItem: true,
				disabled: (rows) =>
					handleSelectOnlyOneItem(rows) ||
					handleStatusCheck(
						rows,
						'disabled',
						['completed', 'inprogress'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: ({ id }) => showModal('putaway-form', { id, mode: 'edit' })
			},
			{
				icon: faEye,
				label: 'viewPutAway',
				role: 'viewPutaway',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: ({ id }) => showModal('putaway-form', { id, mode: 'view' })
			},
			{
				icon: faPlay,
				label: 'startPutAway',
				role: 'startPutaway',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleSelectOnlyOneItem(rows) ||
					handleStatusCheck(
						rows,
						'disabled',
						['inprogress', 'completed'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: ({ id }) => {
					const body = { id };
					NiceModal.show(ConfirmationModal, {
						title: 'startPutAway',
						subTitle: 'confirmation.startPutAway',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.START_PUTAWAY, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'completePutAway',
				role: 'completePutaway',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleSelectOnlyOneItem(rows) ||
					handleStatusCheck(rows, 'disabled', ['new', 'completed'], 'errorMsg.notAllowedForThisStatus'),
				action: ({ id }) => showModal('putaway-form', { id, mode: 'complete' })
			},
			{
				icon: faTrashCan,
				label: 'deletePutAway',
				role: 'deletePutaway',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (rows) =>
					handleSelectOnlyOneItem(rows) ||
					handleStatusCheck(
						rows,
						'disabled',
						['inprogress', 'completed'],
						'errorMsg.notAllowedForThisStatus'
					),
				action: ({ id }) => {
					const body = { ID: id };
					NiceModal.show(ConfirmationModal, {
						title: 'deletePutAway',
						subTitle: 'confirmation.deletePutAway',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.DELETE_PUTAWAY, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPlay,
				label: 'startInbound',
				role: 'startInbound',
				group: 'tools',
				requiredFields: ['id', 'status', 'fullName'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus') ??
					((Array.isArray(rows) ? rows.some((row) => !row.fullName) : !rows.fullName)
						? 'errorMsg.canNotStartOutboundWithNotResponsible'
						: false),
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'startInbound',
						subTitle: 'confirmation.startInbound',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.START_INBOUND, {
									ids: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id]
								})
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'assignResponsible',
				role: 'assignInboundResponsible',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus'),
				action: (rows) => {
					NiceModal.show(AssignResponsible, {
						ids: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id],
						mode: 'inbound'
					});
				}
			},
			{
				icon: faEye,
				label: 'showInventoryBin',
				role: 'showInventoryBin',
				group: 'tools',
				requiredFields: ['id', 'binName'],
				disabled: (rows) => handleSelectOnlyOneItem(rows),
				action: (rows) => NiceModal.show(BinItems, { id: rows.id, binName: rows.binName })
			},
			{
				icon: faCheck,
				label: 'completeInbound',
				role: 'completeInbound',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus'),
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'completeInbound',
						subTitle: 'confirmation.completeInbound',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.COMPLETE_INBOUND, {
									ids: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id]
								})
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPlay,
				label: 'startOutbound',
				role: 'startOutbound',
				group: 'tools',
				requiredFields: ['id', 'status', 'fullName'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus') ??
					((Array.isArray(rows) ? rows.some((row) => !row.fullName) : !rows.fullName)
						? 'errorMsg.canNotStartOutboundWithNotResponsible'
						: false),
				action: (rows) =>
					NiceModal.show(ConfirmationModal, {
						title: 'startOutbound',
						subTitle: 'confirmation.startOutbound',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.START_OUTBOUND, {
									ids: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id]
								})
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					})
			},
			{
				icon: faUserCheck,
				label: 'assignResponsible',
				role: 'assignOutboundResponsible',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus'),
				action: (rows) => {
					NiceModal.show(AssignResponsible, {
						ids: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id],
						mode: 'outbound'
					});
				}
			},
			{
				icon: faCheck,
				label: 'completeOutbound',
				role: 'completeOutboundOrder',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus'),
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'completeOutboundOrder',
						subTitle: 'confirmation.completeOutboundOrder',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCE_WMS_MODULE.COMPLETE_OUTBOUND, {
									ids: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id]
								})
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faDollyFlatbedAlt,
				label: 'updateInventoryAndBin',
				role: 'updateInventoryAndBin',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (rows) =>
					handleStatusCheck(rows, 'disabled', ['completed'], 'errorMsg.completedInventoryTransaction'),
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'updateInventoryAndBinTitle',
						subTitle: 'updateInventoryAndBinSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							const inventoryOrderIDs = [];

							if (selectedOrders.length) {
								selectedOrders.forEach((order) => {
									inventoryOrderIDs.push(order.id);
								});
							} else {
								inventoryOrderIDs.push(selectedOrders.id);
							}

							try {
								const response = await axiosPrivate.post(WMS_MODULE.UPDATE_INVENTORY_AND_BIN, {
									inventoryOrderIDs,
									type: pathname.includes('inbound') ? 'inbound' : 'outbound'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faDollyFlatbedAlt,
				label: 'updateInventory',
				role: 'updateInventory',
				group: 'tools',
				requiredFields: ['id', 'status'],
				action: (selectedOrders) => {
					if (selectedOrders?.status !== 'completed') {
						NiceModal.show(ConfirmationModal, {
							title: 'updateInventoryTitle',
							subTitle: 'updateInventorySubTitle',
							yesAction: async () => {
								dispatch(setLoading(true));

								const orderIds = [];

								if (selectedOrders.length) {
									selectedOrders.forEach((order) => {
										orderIds.push(order.id);
									});
								} else {
									orderIds.push(selectedOrders.id);
								}

								try {
									const response = await axiosPrivate.post(ADVANCE_WMS_MODULE.UPDATE_INVENTORY, {
										ids: orderIds
									});
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								} catch (error) {
									AppLogger.error(error);
									showError();
								}

								dispatch(setLoading(false));
							}
						});
					} else {
						showError({ header: 'error', message: 'errorMsg.completedInventoryTransaction' });
					}
				}
			},
			{
				icon: faFileAlt,
				label: 'createPutList',
				role: 'createPutList',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (rows) => handleSelectOnlyOneItem(rows),
				action: (row) => showModal('putaway-form', { id: row?.id, mode: 'createPutaway' })
			},
			{
				icon: faPenToSquare,
				label: 'editOrder',
				role: 'editInventoryOrder',
				group: 'tools',
				requiredFields: ['id', 'status'],
				headerItem: true,
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					handleStatusCheck(row, 'disabled', ['completed'], 'errorMsg.notAllowedForThisStatus'),
				action: (row) => {
					showModal('inventory-order-form', { id: row.id, mode: 'edit' });
				}
			},
			{
				icon: faEye,
				label: 'viewOrder',
				role: 'viewInventoryOrder',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('inventory-order-form', { id: row.id, mode: 'view' });
				}
			},
			{
				icon: faBookmarkSlash,
				label: 'unassignOrder',
				role: 'unassignOrder',
				group: 'tools',
				requiredFields: ['orderID', 'sortingStatus'],
				disabled: (sortingItem) => {
					if (sortingItem.sortingStatus !== 'readyForSorting')
						return 'errorMsg.notAllowedForThisSortingStatus';
				},
				action: (row) => {
					const body = { orderID: row.ID };
					NiceModal.show(ConfirmationModal, {
						title: 'confirmation.unassignOrder.title',
						subTitle: 'confirmation.unassignOrder.description',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(WMS_MODULE.UNASSIGN_ORDER, body)
								.then((response) => {
									if (response.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.ADMIN]: [
			{
				icon: faPenToSquare,
				label: 'editDeliveryCompany',
				role: 'editDeliveryCompany',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DeliveryCompanyForm, {
						mode: 'edit',
						id: row.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewDeliveryCompany',
				role: 'viewDeliveryCompany',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DeliveryCompanyForm, {
						mode: 'view',
						id: row.ID
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'updateRole',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (row) =>
					showModal('role-modal', {
						id: row.ID
					})
			},
			{
				icon: faTrashCan,
				label: 'delete',
				role: 'deleteRole',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteRole',
						subTitle: 'deleteRoleConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.ROLE.DELETE_ROLES, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserPen,
				label: 'updateMultipleRole',
				role: 'updateMultipleRole',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				action: (selectedRoles) => {
					showModal('update-multiple-role-modal', {
						ids: Array.isArray(selectedRoles) ? selectedRoles.map((role) => role.ID) : [selectedRoles.ID]
					});
				}
			},
			{
				icon: faFilePen,
				label: 'updateMultipleModule',
				role: 'updateMultipleModule',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				action: (selectedRoles) => {
					showModal('update-multiple-module-modal', {
						ids: Array.isArray(selectedRoles) ? selectedRoles.map((role) => role.ID) : [selectedRoles.ID]
					});
				}
			},
			{
				icon: faFilePen,
				label: 'updateMultipleCountries',
				role: 'updateMultipleCountries',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				action: (selectedRoles) => {
					showModal('update-multiple-countries-modal', {
						ids: Array.isArray(selectedRoles) ? selectedRoles.map((role) => role.ID) : [selectedRoles.ID]
					});
				}
			},
			{
				icon: faRightLeft,
				label: 'changeMultipleRole',
				role: 'changeMultipleRole',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				action: (selectedRoles) => {
					NiceModal.show(ChangeMultipleRoleModal, { selectedRoles });
				}
			},
			{
				icon: faArrowsRotate,
				label: 'changeMultipleModule',
				role: 'changeMultipleModule',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				action: (selectedRoles) => {
					NiceModal.show(ChangeMultipleModuleModal, { selectedRoles });
				}
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'updateLanguageResource',
				group: 'tools',
				requiredFields: ['ID', 'langKey', 'engValue', 'trValue', 'arabicValue'],
				headerItem: true,
				action: (row) =>
					NiceModal.show(LanguageResourceManagementModal, {
						row
					})
			},
			{
				icon: faCopy,
				label: 'duplicate',
				role: 'duplicateLanguageResource',
				group: 'tools',
				requiredFields: ['langKey', 'engValue', 'trValue', 'arabicValue'],
				headerItem: true,
				action: (row) =>
					NiceModal.show(LanguageResourceManagementModal, {
						row: {
							key: row?.langKey,
							engValue: row?.engValue,
							trValue: row?.trValue,
							arabicValue: row?.arabicValue
						}
					})
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'updateEnum',
				group: 'tools',
				requiredFields: ['ID', 'enumName', 'enumType', 'value'],
				headerItem: true,
				action: (row) =>
					NiceModal.show(EnumManagementModal, {
						row
					})
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'editStory',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (row) =>
					NiceModal.show(StoryManagementModal, {
						ID: row.ID
					})
			},
			{
				icon: faSendBackward,
				label: 'publishStory',
				role: 'publishStory',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				disabled: (row) => row.status === 'published' && 'errorMsg.storyAlreadyPublished',
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'publishStory',
						subTitle: 'publishStoryConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(ADMIN.STORY.PUBLISH_STORY, {
									ID: row.ID
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'delete',
				role: 'deleteStory',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteStory',
						subTitle: 'deleteStoryConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(ADMIN.STORY.DELETE_STORY, {
									ID: row.ID
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editModule',
				role: 'editModule',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) =>
					NiceModal.show(AddModuleModal, {
						id: row.ID
					})
			},
			{
				icon: faPenToSquare,
				label: 'editPackage',
				role: 'editPackage',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) =>
					NiceModal.show(AddPackageModal, {
						id: row.ID
					})
			},
			{
				icon: faPenToSquare,
				label: 'editSalesChannel',
				role: 'editSalesChannel',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SalesChannelForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faEye,
				label: 'viewSalesChannel',
				role: 'viewSalesChannel',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SalesChannelForm, {
						mode: 'view',
						id: row.id
					});
				}
			},

			{
				icon: faEye,
				label: 'viewCountry',
				role: 'viewCountry',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CountriesManagementForm, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCountry',
				role: 'editCountry',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CountriesManagementForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'editOtherIntegrations',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) =>
					NiceModal.show(OtherIntegrationModal, {
						id: row.ID
					})
			},
			{
				icon: faTrashCan,
				label: 'delete',
				role: 'deleteOtherIntegrations',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteOtherIntegrations',
						subTitle: 'deleteOtherIntegrationsConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(
									ADMIN.OTHER_INTEGRATIONS.DELETE_OTHER_INTEGRATIONS,
									{
										ID: row.ID
									}
								);
								if (response?.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(setLoading(false));
							}
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDeliveryCompanySettings',
				role: 'editDeliveryCompanySettings',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddDeliveryCompanySettingsForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faEye,
				label: 'viewDeliveryCompanySettings',
				role: 'viewDeliveryCompanySettings',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddDeliveryCompanySettingsForm, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'pickupCities',
				role: 'viewPickupCities',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					navigate(`${PATHS.DELIVERY_COMPANY_SETTINGS_PICKUP_CITIES}?id=${row.id}`);
				}
			},
			{
				icon: faCircleCheck,
				label: 'activateDeliveryCompanySettings',
				role: 'activateDeliveryCompanySettings',
				group: 'tools',
				requiredFields: ['id', 'status'],
				className: 'green-item',
				disabled: (row) => handleSelectOnlyOneItem(row) || row.status === 'active',
				action: (selectedRows) => {
					axiosPrivate
						.post(ADMIN.DELIVERY_COMPANY_SETTINGS.UPDATE_DCS_STATUS, {
							id: selectedRows.id,
							status: true
						})
						.then((response) => {
							if (response?.status === 200) {
								dispatch(updateDynamicListsInPage());
								showSuccess();
							} else {
								AppLogger.error(response);
								showError();
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faCircleXmark,
				label: 'deactivateDeliveryCompanySettings',
				role: 'deactivateDeliveryCompanySettings',
				group: 'tools',
				requiredFields: ['id', 'status'],
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row) || row.status === 'inactive',
				action: (selectedRows) => {
					axiosPrivate
						.post(ADMIN.DELIVERY_COMPANY_SETTINGS.UPDATE_DCS_STATUS, {
							id: selectedRows.id,
							status: false
						})
						.then((response) => {
							if (response?.status === 200) {
								dispatch(updateDynamicListsInPage());
								showSuccess();
							} else {
								AppLogger.error(response);
								showError();
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editPolygonCoverage',
				role: 'editPolygonCoverage',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(PolygonCoverageForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faEye,
				label: 'viewPolygonCoverage',
				role: 'viewPolygonCoverage',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(PolygonCoverageForm, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deletePolygonCoverage',
				role: 'deletePolygonCoverage',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deletePolygonCoverage',
						subTitle: 'deletePolygonCoverageConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.DELIVERY_COMPANY_SETTINGS.DELETE_POLYGON_COVERAGE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
										showError();
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faShop,
				label: 'activateDCSPickupCity',
				role: 'activateDCSPickupCity',
				group: 'tools',
				requiredFields: ['id', 'active', 'dcSettingsID', 'country', 'cityName'],
				footerItem: true,
				className: 'green-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return selectedRows.some((row) => row.active === 'active') && 'errorMsg.alreadyActive';
					else return selectedRows?.active === 'active' && 'errorMsg.alreadyActive';
				},
				action: (selectedRows) => {
					axiosPrivate
						.post(ADMIN.DELIVERY_COMPANY_SETTINGS.UPDATE_PICKUP_CITITES_STATUS, {
							cities: Array.isArray(selectedRows)
								? selectedRows.map((pickupCities) => ({
										country: pickupCities.country,
										city: pickupCities.cityName
									}))
								: [
										{
											country: selectedRows.country,
											city: selectedRows.cityName
										}
									],
							dcSettingsID: Array.isArray(selectedRows)
								? selectedRows?.[0]?.dcSettingsID
								: selectedRows.dcSettingsID,
							active: true
						})
						.then((response) => {
							if (response?.status === 200) {
								dispatch(updateDynamicListsInPage());
								showSuccess();
							} else {
								AppLogger.error(response);
								showError();
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faShopSlash,
				label: 'deactivateDCSPickupCity',
				role: 'deactivateDCSPickupCity',
				group: 'tools',
				requiredFields: ['id', 'active', 'dcSettingsID', 'dcsPickupCitiesID'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return selectedRows.some((row) => row.active === 'inactive') && 'errorMsg.alreadyInActive';
					else return selectedRows?.active === 'inactive' && 'errorMsg.alreadyInActive';
				},
				action: (selectedRows) => {
					axiosPrivate
						.post(ADMIN.DELIVERY_COMPANY_SETTINGS.UPDATE_PICKUP_CITITES_STATUS, {
							IDs: Array.isArray(selectedRows)
								? selectedRows.map((pickupCities) => pickupCities.dcsPickupCitiesID)
								: [selectedRows.dcsPickupCitiesID],
							dcSettingsID: Array.isArray(selectedRows)
								? selectedRows?.[0]?.dcSettingsID
								: selectedRows.dcSettingsID,
							active: false
						})
						.then((response) => {
							if (response?.status === 200) {
								dispatch(updateDynamicListsInPage());
								showSuccess();
							} else {
								AppLogger.error(response);
								showError();
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faEye,
				label: 'viewOtoOrderStatus',
				role: 'viewOtoOrderStatus',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(OtoOrderStatusForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOtoOrderStatus',
				role: 'editOtoOrderStatus',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(OtoOrderStatusForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteOtoOrderStatus',
				role: 'deleteOtoOrderStatus',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteOtoOrderStatus',
						subTitle: 'deleteOtoOrderStatusConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.STATUS_MANAGEMENT.DELETE_OTO_ORDER_STATUS, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteStatusGroup',
				role: 'deleteStatusGroup',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteStatusGroup',
						subTitle: 'deleteStatusGroupConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.STATUS_MANAGEMENT.DELETE_STATUS_GROUP, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteAllowedGroupStatusTransition',
				role: 'deleteAllowedGroupStatusTransition',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteAllowedGroupStatusTransition',
						subTitle: 'deleteAllowedGroupStatusTransitionConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.STATUS_MANAGEMENT.DELETE_ALLOWED_GROUP_STATUS_TRANSITION, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faEye,
				label: 'viewDCStatusMapping',
				role: 'viewDCStatusMapping',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(DCStatusMappingForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDCStatusMapping',
				role: 'editDCStatusMapping',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(DCStatusMappingForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDCStatusMapping',
				role: 'deleteDCStatusMapping',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDCStatusMapping',
						subTitle: 'deleteDCStatusMappingConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.STATUS_MANAGEMENT.DELETE_DC_STATUS_MAPPING, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response.data.code || error?.response.data.message
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'removeMissingDCStatusMapping',
				role: 'updateMissingDCStatusMapping',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['deliveryCompanyId', 'dcStatus'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'removeMissingDCStatusMapping',
						subTitle: 'removeMissingDCStatusMappingConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.STATUS_MANAGEMENT.UPDATE_MISSING_DC_STATUS_MAPPING, {
									deliveryCompanyId: row?.deliveryCompanyId ?? '',
									dcStatus: row?.dcStatus ?? '',
									mappingAction: 'removed'
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'showDCStatusHistory',
				role: 'showDCStatusHistory',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['otoOrderId'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(StatusChangeHistoryModal, {
						otoOrderId: row.otoOrderId
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateTranslation',
				role: 'updateTranslation',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(UpdateTranslationModal, {
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateSettings',
				role: 'updateNotificationThreshold',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(UpdateSettings, {
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'searchForAiError',
				role: 'searchForAiError',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					navigate(mergePaths(PATHS.CREATE_SHIPMENT_ERROR_MAPPING, PATHS.SHIPMENT_ERROR_LOGS_BY_AI), {
						state: {
							defaultFilters: [
								{
									column: {
										Header: 'aiErrorId',
										accessor: 'aiErrorId',
										filterType: FilterType.string
									},
									action: FilterAction.is,
									value: row.id
								}
							]
						}
					});
				}
			},
			{
				icon: faUser,
				label: 'userReferralBlock',
				role: 'userReferralBlock',
				group: 'tools',
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					(row.referralBlockedStatus === 'blocked' && 'errorMsg.notAllowedForThisState'),
				requiredFields: ['senderCompanyID', 'referralBlockedStatus'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'userReferralBlock',
						subTitle: 'userReferralBlock.modal.description',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.REFERRALS.BLOCK_REFERRAL, {
									companyIds: [row.senderCompanyID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBan,
				label: 'userReferralUnblock',
				role: 'userReferralUnblock',
				group: 'tools',
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					(row.referralBlockedStatus === 'noBlock' && 'errorMsg.notAllowedForThisState'),
				requiredFields: ['senderCompanyID', 'referralBlockedStatus'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'userReferralUnblock',
						subTitle: 'userReferralUnblock.modal.description',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.REFERRALS.UNBLOCK_REFERRAL, {
									companyIds: [row.senderCompanyID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faEye,
				label: 'viewAnnouncement',
				role: 'viewAnnouncement',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(HomePageAnnouncementForm, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faCopy,
				label: 'duplicateAnnouncement',
				role: 'duplicateAnnouncement',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				headerItem: true,
				action: (row) =>
					NiceModal.show(HomePageAnnouncementForm, {
						mode: 'duplicate',
						id: row.id
					})
			},
			{
				icon: faPenToSquare,
				label: 'editAnnouncement',
				role: 'editAnnouncement',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(HomePageAnnouncementForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteAnnouncement',
				role: 'deleteAnnouncement',
				group: 'tools',
				disabled: (row) => handleSelectOnlyOneItem(row),
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'removeHomePageAnnouncement',
						subTitle: 'removeHomePageAnnouncementConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADMIN.DELETE_ANNOUNCEMENT, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editSubscriptionTransactionInvoice',
				role: 'editSubscriptionTransactionInvoice',
				group: 'tools',
				requiredFields: [
					'ID',
					'companyID',
					'invoiceLink',
					'zohoInvoiceNo',
					'amount',
					'transactionDate',
					'clientName',
					'currency',
					'status'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(EditSubscriptionInvoiceModal, {
						transactionID: row.ID,
						companyID: row.companyID,
						invoiceLink: row.invoiceLink || '',
						invoiceNumber: row.zohoInvoiceNo || '',
						amount: row.amount || 0,
						transactionDate: row.transactionDate || '',
						clientName: row.clientName || '',
						currency: row.currency || '',
						paymentStatus: row.status || ''
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.PURCHASE_ORDER]: [
			{
				icon: faPenToSquare,
				label: 'editPurchaseOrder',
				role: 'editPurchaseOrder',
				group: 'order',
				requiredFields: ['id', 'status'],
				headerItem: true,
				disabled: (row) => {
					if (row.status !== 'new') {
						return 'errorMsg.statusIsNotCorrect';
					}
				},
				action: (row) => showModal('purchase-order-form', { mode: 'edit', id: row.id })
			},
			{
				icon: faEye,
				label: 'viewPurchaseOrder',
				role: 'viewPurchaseOrder',
				group: 'order',
				requiredFields: ['id'],
				headerItem: true,
				action: (row) => showModal('purchase-order-form', { mode: 'view', id: row.id })
			},
			{
				icon: faCircleCheck,
				label: 'submitPurchaseOrder',
				role: 'submitPurchaseOrder',
				group: 'order',
				requiredFields: ['id', 'status'],
				disabled: (rows) => {
					if (!isAllWithStatus(rows, 'new')) return 'errorMsg.notAllowedForThisStatus';
				},
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'submitPurchaseOrderTitle',
						subTitle: 'submitPurchaseOrderSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(PURCHASE_ORDER.UPDATE_PURCHASE_ORDER_STATUS, {
									ids: Array.isArray(rows) ? rows.map((rule) => rule.id) : [rows.id],
									status: 'submitted'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTruck,
				label: 'receivePurchaseOrder',
				role: 'receivePurchaseOrder',
				group: 'order',
				requiredFields: ['id', 'status'],
				disabled: (rows) => {
					if (!isAllWithStatus(rows, 'approved')) return 'errorMsg.notAllowedForThisStatus';
				},
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'receivePurchaseOrderTitle',
						subTitle: 'receivePurchaseOrderSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(PURCHASE_ORDER.UPDATE_PURCHASE_ORDER_STATUS, {
									ids: Array.isArray(rows) ? rows.map((rule) => rule.id) : [rows.id],
									status: 'completed'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'approvePurchaseOrder',
				role: 'approvePurchaseOrder',
				group: 'order',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (rows) => {
					if (!isAllWithStatus(rows, 'submitted')) return 'errorMsg.notAllowedForThisStatus';
				},
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'approvePurchaseOrderTitle',
						subTitle: 'approvePurchaseOrderSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(PURCHASE_ORDER.UPDATE_PURCHASE_ORDER_STATUS, {
									ids: Array.isArray(rows) ? rows.map((rule) => rule.id) : [rows.id],
									status: 'approved'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faXmark,
				label: 'cancelPurchaseOrder',
				role: 'cancelPurchaseOrder',
				group: 'order',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (rows) => {
					if (isThereAnyWithStatus(rows, 'cancelled') || isThereAnyWithStatus(rows, 'completed'))
						return 'errorMsg.notAllowedForThisStatus';
				},
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'cancelPurchaseOrderTitle',
						subTitle: 'cancelPurchaseOrderSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(PURCHASE_ORDER.UPDATE_PURCHASE_ORDER_STATUS, {
									ids: Array.isArray(rows) ? rows.map((rule) => rule.id) : [rows.id],
									status: 'cancelled'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
							}

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.SUPPORT]: [
			{
				icon: faRepeat,
				label: 'switchIntoClientAccount',
				role: 'switchInto',
				group: 'tools',
				requiredFields: [
					'clientUserId',
					'expiryDate',
					'SupportAccessRequest',
					'phone',
					'clientName',
					'fullName',
					'userType',
					'email',
					'expiredStatus'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) =>
					switchSupportClient({
						...row,
						status: row.expiredStatus
					})
			},
			{
				icon: faEye,
				label: 'viewCountry',
				role: 'viewCountry',
				group: 'tools',
				requiredFields: ['id', 'shortCode', 'hasState', 'hasNeighbourhood'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CountryForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCountry',
				role: 'deleteCountry',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['id'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCountryConfirmationTitle',
						subTitle: 'deleteCountryConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADDRESS.DELETE_COUNTRY, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCountry',
				role: 'editCountry',
				group: 'tools',
				requiredFields: ['id', 'shortCode', 'hasState', 'hasNeighbourhood'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CountryForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faEye,
				label: 'viewState',
				role: 'viewState',
				group: 'tools',
				requiredFields: ['id', 'name', 'countryID', 'countryName', 'shortCode'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(StateForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteState',
				role: 'deleteState',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['id'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteStateConfirmationTitle',
						subTitle: 'deleteStateConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADDRESS.DELETE_STATE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editState',
				role: 'editState',
				group: 'tools',
				requiredFields: ['id', 'name', 'countryID', 'countryName', 'shortCode'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(StateForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faEye,
				label: 'viewCity',
				role: 'viewCity',
				group: 'tools',
				requiredFields: ['id', 'name', 'countryID', 'stateID', 'countryName', 'stateName'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CityForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCity',
				role: 'deleteCity',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['id'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCityConfirmationTitle',
						subTitle: 'deleteCityConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADDRESS.DELETE_CITY, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCity',
				role: 'editCity',
				group: 'tools',
				requiredFields: ['id', 'name', 'countryID', 'stateID', 'countryName', 'stateName'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CityForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faEye,
				label: 'viewGovernorate',
				role: 'view-governorate',
				group: 'tools',
				requiredFields: ['id', 'name', 'countryID', 'stateID', 'countryName', 'stateName'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(GovernorateForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteGovernorate',
				role: 'delete-governorate',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['id'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteGovernorateConfirmationTitle',
						subTitle: 'deleteGovernorateConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADDRESS.DELETE_GOVERNORATE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editGovernorate',
				role: 'edit-governorate',
				group: 'tools',
				requiredFields: ['id', 'name', 'countryID', 'stateID', 'countryName', 'stateName'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(GovernorateForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faEye,
				label: 'viewDistrict',
				role: 'viewDistrict',
				group: 'tools',
				requiredFields: [
					'id',
					'name',
					'countryID',
					'stateID',
					'cityID',
					'countryName',
					'stateName',
					'cityName'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DistrictForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDistrict',
				role: 'deleteDistrict',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['id'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDistrictConfirmationTitle',
						subTitle: 'deleteDistrictConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADDRESS.DELETE_DISTRICT, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDistrict',
				role: 'editDistrict',
				group: 'tools',
				requiredFields: [
					'id',
					'name',
					'countryID',
					'stateID',
					'cityID',
					'countryName',
					'stateName',
					'cityName'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DistrictForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faEye,
				label: 'viewNeighbourhood',
				role: 'view-neighbourhood',
				group: 'tools',
				requiredFields: [
					'id',
					'name',
					'countryID',
					'stateID',
					'cityID',
					'countryName',
					'stateName',
					'cityName',
					'districtName',
					'districtID'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(NeighbourhoodForm, {
						mode: 'view',
						row
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteNeighbourhood',
				role: 'delete-neighbourhood',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['id'],
				footerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteNeighbourhoodConfirmationTitle',
						subTitle: 'deleteNeighbourhoodConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADDRESS.DELETE_NEIGHBORHOOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editNeighbourhood',
				role: 'edit-neighbourhood',
				group: 'tools',
				requiredFields: [
					'id',
					'name',
					'countryID',
					'stateID',
					'cityID',
					'countryName',
					'stateName',
					'cityName',
					'districtName',
					'districtID'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(NeighbourhoodForm, {
						mode: 'edit',
						row
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDCPickupLocation',
				role: 'editDCPickupLocation',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DCPickupLocationForm, {
						id: row?.id
					});
				}
			},
			{
				icon: faEnvelope,
				label: 'sendDCPickupLocationEmail',
				role: 'sendDCPickupLocationEmail',
				group: 'tools',
				requiredFields: ['id', 'dcPickupLocationId'],
				headerItem: true,
				action: (rows) => {
					NiceModal.show(SendDCPickupLocationEmailForm, {
						selectedDCPickupLocation: Array.isArray(rows) ? rows.map((row) => row.id) : [rows.id],
						dcPickupLocationIds: Array.isArray(rows)
							? rows.map((row) => row.dcPickupLocationId)
							: [rows.dcPickupLocationId]
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDCPickupLocation',
				role: 'deleteDCPickupLocation',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDCPickupLocation',
						subTitle: 'deleteDCPickupLocationConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_DC_PICKUP_LOCATION, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faEnvelope,
				label: 'generatePickUpId',
				role: 'generatePickUpId',
				group: 'tools',
				requiredFields: ['otoOrderID'],
				headerItem: true,
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'generatePickUpId',
						subTitle: 'generatePickUpIdConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(ORDER_ACTIONS.GENERATE_PICKUP_ID, {
									otoOrderIDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.otoOrderID)
										: [selectedOrders.otoOrderID]
								});
								if (response.status === 200) {
									NiceModal.show(GeneratedPickupOrderModal, {
										orders: response?.data?.orders
									});
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError({
									message: error?.message
								});
							} finally {
								dispatch(setLoading(false));
							}
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'toggleSLABreach',
				role: 'toggleSLABreach',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				action: (selectedRows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'toggleSLABreachConfirmationTitle',
						subTitle: 'toggleSLABreachConfirmationSubtitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(REPORT.SLA.UPDATE_SLA_BREACH_ON_SHIPMENT_STATUS_LIST, {
									IDs: Array.isArray(selectedRows)
										? selectedRows.map((order) => order.ID)
										: [selectedRows.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.CUSTOM_ROLE]: [
			{
				icon: faRepeat,
				label: 'updateRoleTemplates',
				role: 'updateRoleTemplates',
				group: 'roles',
				requiredFields: ['ID'],
				action: (row) =>
					showModal('role-templates-modal', {
						id: row.ID
					})
			},
			{
				icon: faTrashCan,
				label: 'deleteRoleTemplates',
				role: 'deleteRoleTemplates',
				group: 'tools',
				requiredFields: ['ID'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteRoleTemplates',
						subTitle: 'deleteRoleTemplatesConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(CUSTOM_ROLE.DELETE_ROLE_TEMPLATE, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],

		[ACTION_OBJECT_TYPES.SHIPPING_PARTNERS]: [
			{
				icon: faCircleCheck,
				label: 'activate',
				role: 'activateCityCoverage',
				group: 'tools',
				requiredFields: ['city', 'citySettingsID'],
				disabled: (row) => row.status === 'active',
				action: async (row) => {
					const deliveryCompSettingsID = state.settingsID;

					if (!deliveryCompSettingsID) return;

					dispatch(setLoading(true));

					await axiosPrivate
						.post(SHIPPING_PARTNERS.UPDATE_CITY_COVERAGE_LIST, {
							deliveryCompSettingsID,
							citySettingsID: Array.isArray(row)
								? row.map((city) => city.citySettingsID)[0]
								: row.citySettingsID,
							cities: Array.isArray(row)
								? row.map((city) => ({ cityName: city.city }))
								: [{ cityName: row.city }],
							action: 'activate'
						})
						.then((response) => {
							if (response?.status === 200) {
								showSuccess();
							} else {
								AppLogger.error(response);
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(updateDynamicListsInPage());
						});

					dispatch(setLoading(false));
				}
			},
			{
				icon: faCircleXmark,
				label: 'deactivate',
				role: 'deactivateCityCoverage',
				group: 'tools',
				requiredFields: ['city', 'citySettingsID'],
				disabled: (row) => row.status === 'passive',
				action: async (row) => {
					const deliveryCompSettingsID = state.settingsID;

					if (!deliveryCompSettingsID) return;

					dispatch(setLoading(true));

					await axiosPrivate
						.post(SHIPPING_PARTNERS.UPDATE_CITY_COVERAGE_LIST, {
							deliveryCompSettingsID,
							citySettingsID: Array.isArray(row)
								? row.map((city) => city.citySettingsID)[0]
								: row.citySettingsID,
							cities: Array.isArray(row)
								? row.map((city) => ({ cityName: city.city }))
								: [{ cityName: row.city }],
							action: 'deactivate'
						})
						.then((response) => {
							if (response?.status === 200) {
								showSuccess();
							} else {
								AppLogger.error(response);
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							dispatch(updateDynamicListsInPage());
						});

					dispatch(setLoading(false));
				}
			},
			{
				icon: faGauge,
				label: 'dailyCapacity',
				role: 'dailyCapacityCityCoverage',
				group: 'tools',
				requiredFields: ['city', 'cityId', 'citySettingsID'],
				action: (row) => {
					NiceModal.show(CapacityModal, {
						capacity: row.capacity,
						cities: Array.isArray(row) ? row.map((city) => city.city) : [row.city],
						handleSubmit: async (capacity) => {
							const deliveryCompSettingsID = state.settingsID;

							if (!deliveryCompSettingsID) return;

							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.UPDATE_CITY_COVERAGE_LIST, {
									deliveryCompSettingsID,
									citySettingsID: Array.isArray(row)
										? row.map((city) => city.citySettingsID)[0]
										: row.citySettingsID,
									cities: Array.isArray(row)
										? row.map((city) => ({ cityName: city.city }))
										: [{ cityName: row.city }],
									action: 'updateCapacity',
									capacity
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.OTHER]: [
			{
				icon: faPenToSquare,
				label: 'editWebhook',
				role: 'editWebhook',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddWebhookModal, {
						mode: 'edit',
						id: row.ID
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteWebhook',
				role: 'deleteWebhook',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteWebhook',
						subTitle: 'deleteWebhookConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(WEBHOOK.DELETE_WEBHOOK_SETTINGS, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOTOLink',
				role: 'editOTOLink',
				group: 'tools',
				requiredFields: ['id', 'linkExpiryDate'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row) || isExpired(row.linkExpiryDate),
				action: (row) => {
					NiceModal.show(CreateOtoLink, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faEye,
				label: 'viewOTOLink',
				role: 'viewOTOLink',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CreateOtoLink, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faCheck,
				label: 'activateOtoLink',
				role: 'activateOtoLink',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					handleStatusCheck(row, 'disabled', ['active'], 'errorMsg.linkAlreadyActive') ||
					isExpired(row.linkExpiryDate),
				action: (row) => {
					axiosPrivate
						.post(OTO_LINK.UPDATE_OTO_LINK_STATUS, {
							otoLinkId: row.id,
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faXmark,
				label: 'deactivateOtoLink',
				role: 'deactivateOtoLink',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					handleStatusCheck(row, 'disabled', ['inactive'], 'errorMsg.linkAlreadyDeactive') ||
					isExpired(row.linkExpiryDate),
				action: (row) => {
					axiosPrivate
						.post(OTO_LINK.UPDATE_OTO_LINK_STATUS, {
							otoLinkId: row.id,
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editFeasibilityRule',
				role: 'editFeasibilityRule',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('feasibility-rule-form', {
						mode: 'edit',
						id: row.ID
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'activateFeasibilityRule',
				role: 'activateFeasibilityRule',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'active')
						: selecteds?.status === 'active') && 'errorMsg.ruleCannotBeActivated',
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_FEASIBILITY_RULE_STATUS, {
							IDs: Array.isArray(selectedRules)
								? selectedRules.map((rule) => rule.ID)
								: [selectedRules.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivateFeasibilityRule',
				role: 'deactivateFeasibilityRule',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'inactive')
						: selecteds?.status === 'inactive') && 'errorMsg.ruleCannotBeDeactivated',
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_FEASIBILITY_RULE_STATUS, {
							IDs: Array.isArray(selectedRules)
								? selectedRules.map((rule) => rule.ID)
								: [selectedRules.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteFeasibilityRule',
				role: 'deleteFeasibilityRule',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteRule',
						subTitle: 'deleteRuleConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_AUTOMATION.DELETE_FEASIBILITY_RULE, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'activatePriorityRule',
				role: 'activatePriorityRule',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'active')
						: selecteds?.status === 'active') && 'errorMsg.ruleCannotBeActivated',
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_PRIORITY_RULE_STATUS, {
							IDs: Array.isArray(selectedRules)
								? selectedRules.map((rule) => rule.ID)
								: [selectedRules.ID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivatePriorityRule',
				role: 'deactivatePriorityRule',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'inactive')
						: selecteds?.status === 'inactive') && 'errorMsg.ruleCannotBeDeactivated',
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_PRIORITY_RULE_STATUS, {
							IDs: Array.isArray(selectedRules)
								? selectedRules.map((rule) => rule.ID)
								: [selectedRules.ID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editPriorityRule',
				role: 'editPriorityRule',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('priority-rules-form', { mode: 'edit', id: row?.ID });
				}
			},
			{
				icon: faTrashCan,
				label: 'deletePriorityRule',
				role: 'deletePriorityRule',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteRule',
						subTitle: 'deleteRuleConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_AUTOMATION.DELETE_PRIORITY_RULE, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOrderOtomationRule',
				role: 'editOrderOtomationRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('order-otomation-form', {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteOrderOtomationRule',
				role: 'deleteOrderOtomationRule',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteRule',
						subTitle: 'deleteRuleConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ORDER_AUTOMATION.DELETE_ORDER_AUTOMATION, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'activateOrderOtomationRule',
				role: 'activateOrderOtomationRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'active')
						: selecteds?.status === 'active') && 'errorMsg.ruleCannotBeActivated',
				action: (selectedRules) => {
					axiosPrivate
						.post(ORDER_AUTOMATION.UPDATE_ORDER_AUTOMATION_STATUS, {
							ids: Array.isArray(selectedRules)
								? selectedRules.map((rule) => rule.id)
								: [selectedRules.id],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivateOrderOtomationRule',
				role: 'deactivateOrderOtomationRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (selecteds) =>
					(Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'inactive')
						: selecteds?.status === 'inactive') && 'errorMsg.ruleCannotBeDeactivated',
				action: (selectedRules) => {
					axiosPrivate
						.post(ORDER_AUTOMATION.UPDATE_ORDER_AUTOMATION_STATUS, {
							ids: Array.isArray(selectedRules)
								? selectedRules.map((rule) => rule.id)
								: [selectedRules.id],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editReasonConfig',
				role: 'editReasonConfig',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('reason-config-form', { id: row.ID });
				}
			},
			{
				icon: faPenToSquare,
				label: 'editPredefinedMessage',
				role: 'editPredefinedMessage',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('predefined-message-form', { id: row.ID });
				}
			},
			{
				icon: faPenToSquare,
				label: 'viewPredefinedMessage',
				role: 'viewPredefinedMessage',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('predefined-message-form', { id: row.ID, mode: 'view' });
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBlacklistedCustomer',
				role: 'editBlacklistedCustomer',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('blacklisted-customer-form', { id: row.ID });
				}
			},
			// Price Configurations
			{
				icon: faPenToSquare,
				label: 'editShippingContract',
				role: 'editShippingContract',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ShippingContractForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},

			{
				icon: faTrashCan,
				label: 'deleteShippingContract',
				role: 'deleteShippingContract',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteShippingContract',
						subTitle: 'deleteShippingContractConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_SHIPPING_CONTRACT, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: 'errorMsg.pleaseMakeSureYouDontUseThisContract'
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDCWorkingHour',
				role: 'editDCWorkingHour',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DCWorkingHourForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faBoxesStacked,
				label: 'migrateConfiguration',
				role: 'migrateConfiguration',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ShippingMigrateConfigurationModal, {
						targetShippingContractId: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDCWorkingHour',
				role: 'deleteDCWorkingHour',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDCWorkingHour',
						subTitle: 'deleteDCWorkingHourConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_DC_WORKING_HOURS, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOTODeliveryPrice',
				role: 'editOTODeliveryPrice',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(OTODeliveryPriceForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteOTODeliveryPrice',
				role: 'deleteOTODeliveryPrice',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteOTODeliveryPrice',
						subTitle: 'deleteOTODeliveryPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_OTO_DELIVERY_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editSpecialContractTerm',
				role: 'editSpecialContractTerm',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SpecialContractTermForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteSpecialContractTerm',
				role: 'deleteSpecialContractTerm',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteSpecialContractTerm',
						subTitle: 'deleteSpecialContractTermConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_SPECIAL_CONTRACT_TERM, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editOTOMargin',
				role: 'editOTOMargin',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(OTOMarginForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteOTOMargin',
				role: 'deleteOTOMargin',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteOTOMargin',
						subTitle: 'deleteOTOMarginConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_OTO_MARGIN_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editTierBasedPrice',
				role: 'editTierBasedPrice',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(TierBasedPriceForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteTierBasedPrice',
				role: 'deleteTierBasedPrice',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteTierBasedPrice',
						subTitle: 'deleteTierBasedPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_TIER_BASED_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editWeightBasedPrice',
				role: 'editWeightBasedPrice',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(WeightBasedPriceForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteWeightBasedPrice',
				role: 'deleteWeightBasedPrice',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteWeightBasedPrice',
						subTitle: 'deleteWeightBasedPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_WEIGHT_BASED_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editWeightCountryBasedPrice',
				role: 'editWeightCountryBasedPrice',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(WeightCountryBasedPriceForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteWeightCountryBasedPrice',
				role: 'deleteWeightCountryBasedPrice',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteWeightCountryBasedPrice',
						subTitle: 'deleteWeightCountryBasedPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_WEIGHT_COUNTRY_BASED_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCityBasedPrice',
				role: 'editCityBasedPrice',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CityBasedPriceForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCityBasedPrice',
				role: 'deleteCityBasedPrice',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCityBasedPrice',
						subTitle: 'deleteCityBasedPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_CITY_BASED_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editKMBasedPrice',
				role: 'editKMBasedPrice',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(KMBasedPriceForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteKMBasedPrice',
				role: 'deleteKMBasedPrice',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteKMBasedPrice',
						subTitle: 'deleteKMBasedPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_KM_BASED_PRICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editKMBasedSLA',
				role: 'editKMBasedSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(KMBasedSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editKMBasedSLA',
				role: 'editUserKmBasedSla',
				group: 'tools',
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(KMBasedSLAForm, {
						mode: 'editConnectedPartner',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteKMBasedSLA',
				role: 'deleteKMBasedSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteKMBasedSLA',
						subTitle: 'deleteKMBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_KM_BASED_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteKMBasedSLA',
				role: 'deleteUserKmBasedSla',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteKMBasedSLA',
						subTitle: 'deleteKMBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_KM_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBulletKMSLA',
				role: 'editBulletKMSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(BulletKMSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBulletKMSLA',
				role: 'editUserBulletKMSLA',
				group: 'tools',
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(BulletKMSLAForm, {
						mode: 'editConnectedPartner',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBulletKMSLA',
				role: 'deleteBulletKMSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBulletKMSLA',
						subTitle: 'deleteBulletKMSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_BULLET_KM_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBulletKMSLA',
				role: 'deleteUserBulletKMSLA',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBulletKMSLA',
						subTitle: 'deleteBulletKMSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_BULLET_KM_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBulletDistrictSLA',
				role: 'editBulletDistrictSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(BulletDistrictSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBulletDistrictSLA',
				role: 'editBulletDistrictSLAUser',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(BulletDistrictSLAForm, {
						mode: 'edit',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBulletDistrictSLA',
				role: 'deleteBulletDistrictSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBulletDistrictSLA',
						subTitle: 'deleteBulletDistrictSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_BULLET_DISTRICT_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response?.data?.code || error?.response?.data?.message
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBulletDistrictSLA',
				role: 'deleteBulletDistrictSLAUser',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBulletDistrictSLA',
						subTitle: 'deleteBulletDistrictSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_BULLET_DISTRICT_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response?.data?.code || error?.response?.data?.message
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editTierBasedSLA',
				role: 'editTierBasedSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(TierBasedSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editTierBasedSLA',
				role: 'editUserTierBasedSla',
				group: 'tools',
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(TierBasedSLAForm, {
						mode: 'editConnectedPartner',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteTierBasedSLA',
				role: 'deleteTierBasedSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteTierBasedSLA',
						subTitle: 'deleteTierBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_TIER_BASED_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteTierBasedSLA',
				role: 'deleteUserTierBasedSla',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteTierBasedSLA',
						subTitle: 'deleteTierBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_TIER_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCountryBasedSLA',
				role: 'editCountryBasedSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CountryBasedSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCountryBasedSLA',
				role: 'editUserCountryBasedSla',
				group: 'tools',
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CountryBasedSLAForm, {
						mode: 'editConnectedPartner',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCountryBasedSLA',
				role: 'deleteCountryBasedSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCountryBasedSLA',
						subTitle: 'deleteCountryBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_COUNTRY_BASED_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCountryBasedSLA',
				role: 'deleteUserCountryBasedSla',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCountryBasedSLA',
						subTitle: 'deleteCountryBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_COUNTRY_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCityBasedSLA',
				role: 'editCityBasedSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CityBasedSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCityBasedSLA',
				role: 'editUserCityBasedSla',
				group: 'tools',
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CityBasedSLAForm, {
						mode: 'editConnectedPartner',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCityBasedSLA',
				role: 'deleteCityBasedSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCityBasedSLA',
						subTitle: 'deleteCityBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_CITY_BASED_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCityBasedSLA',
				role: 'deleteUserCityBasedSla',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCityBasedSLA',
						subTitle: 'deleteCityBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_CITY_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDistrictBasedSLA',
				role: 'editDistrictBasedSLA',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DistrictBasedSLAForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDistrictBasedSLA',
				role: 'editUserDistrictBasedSla',
				group: 'tools',
				requiredFields: ['id', 'deliveryCompanySettingsId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DistrictBasedSLAForm, {
						mode: 'editConnectedPartner',
						id: row.id,
						settingsID: row.deliveryCompanySettingsId
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDistrictBasedSLA',
				role: 'deleteDistrictBasedSLA',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDistrictBasedSLA',
						subTitle: 'deleteDistrictBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_DISTRICT_BASED_SLA, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteDistrictBasedSLA',
				role: 'deleteUserDistrictBasedSla',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteDistrictBasedSLA',
						subTitle: 'deleteDistrictBasedSLAConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PARTNERS.DELETE_DISTRICT_SLA_METHOD, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBlacklistedCustomer',
				role: 'deleteBlacklistedCustomer',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBlacklistedCustomer',
						subTitle: 'deleteBlacklistedCustomer',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(BLACKLISTED_CUSTOMER.DELETE_BLACKLISTED_CUSTOMER, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editAreaSetting',
				role: 'editAreaSetting',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('area-settings-form', {
						mode: 'edit',
						id: row.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewAreaSetting',
				role: 'viewAreaSetting',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('area-settings-form', {
						mode: 'view',
						id: row.ID
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteAreaSetting',
				role: 'deleteAreaSetting',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteAreaSetting',
						subTitle: 'deleteAreaSettingConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCED_OMS.DELETE_AREA_SETTING, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'assignAreaToDriver',
				role: 'assignAreaToDriver',
				group: 'tools',
				requiredFields: ['ID'],
				action: (area) => {
					NiceModal.show(AssingToDriverForm, {
						ids: [area.ID],
						customIDs: [area.ID],
						mode: 'area'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCityGroup',
				role: 'editCityGroup',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('city-group-form', {
						mode: 'edit',
						id: row.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewCityGroup',
				role: 'viewCityGroup',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('city-group-form', {
						mode: 'view',
						id: row.ID
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteCityGroup',
				role: 'deleteCityGroup',
				group: 'tools',
				className: 'red-item',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteCityGroup',
						subTitle: 'deleteCityGroupConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(ADVANCED_OMS.DELETE_CITY_GROUP, {
									ID: row.ID
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'editShippingPrice',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('shipping-price-form', {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faEye,
				label: 'view',
				role: 'viewShippingPrice',
				headerItem: true,
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('shipping-price-form', {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteShippingPrice',
				role: 'deleteShippingPrice',
				footerItem: true,
				className: 'red-item',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteShippingPrice',
						subTitle: 'deleteShippingPriceConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PRICES.DELETE_SHIPPING_PRICE_SPECIAL_SETTING, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editBrand',
				role: 'editBrands',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('brand-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'viewBrand',
				role: 'viewBrand',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('brand-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBrand',
				role: 'deleteBrand',
				group: 'tools',
				requiredFields: ['ID', 'storeName'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBrand',
						subTitle: t('confirmation.deleteBrand', {
							name: row.storeName
						}),
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(BRAND.DELETE_BRAND, {
									ID: row.ID
								});
								if (response?.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editEmailAccount',
				role: 'editEmailAccount',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('email-accounts-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faListCheck,
				label: 'viewEmailAccount',
				role: 'viewEmailAccount',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('email-accounts-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editEmailSetting',
				role: 'editEmailSetting',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('email-settings-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faListCheck,
				label: 'viewEmailSetting',
				role: 'viewEmailSetting',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('email-settings-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editSMSSettings',
				role: 'editSMSSetting',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					if (row.smsType === 'trackingSms' && isAuthorized('/modal/free-tracking-sms-form-modal')) {
						showModal('free-tracking-sms-form-modal', {
							id: row.id,
							mode: 'edit'
						});
					} else {
						showModal('sms-settings-form-modal', {
							id: row.id,
							mode: 'edit'
						});
					}
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteSMSSetting',
				role: 'deleteSMSSetting',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteSMSSettingTitle',
						subTitle: 'deleteSMSSettingConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							await axiosPrivate
								.post(SMS.DELETE_SMS_SETTING, {
									ids: Array.isArray(row) ? row.map((item) => item.id) : [row.id]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faEnvelope,
				label: 'sendTestEmail',
				role: 'sendTestEmail',
				group: 'tools',
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SendTestEmailModal, {
						id: row.ID
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCustomizablePages',
				role: 'editCustomizablePage',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('customizable-page-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			// SMS Settings
			{
				icon: faEnvelope,
				label: 'sendTestSMS',
				role: 'sendTestSMS',
				group: 'tools',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SendTestSMSForm, {
						smsSettingID: row.id
					});
				}
			},
			// Whatsapp Settings & Templates
			{
				icon: faPenToSquare,
				label: 'editWhatsappSetting',
				role: 'editWhatsappSetting',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('whatsapp-settings-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faListCheck,
				label: 'viewWhatsappSetting',
				role: 'viewWhatsappSetting',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('whatsapp-settings-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editWhatsappTemplate',
				role: 'editWhatsappTemplate',
				group: 'tools',
				requiredFields: ['ID', 'status'],
				headerItem: true,
				disabled: (row) =>
					handleStatusCheck(row, 'disabled', ['pending'], 'errorMsg.editPendingWhatsappTemplate') ||
					handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('whatsapp-template-form', {
						id: row.ID,
						mode: 'edit'
					});
				}
			},
			{
				icon: faListCheck,
				label: 'viewWhatsappTemplate',
				role: 'viewWhatsappTemplate',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('whatsapp-template-form', {
						id: row.ID,
						mode: 'view'
					});
				}
			},
			{
				icon: faEnvelope,
				label: 'sendWhatsappTestMessage',
				role: 'sendWhatsappTestMessage',
				group: 'tools',
				requiredFields: ['ID', 'messageType', 'connectionID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SendTestMessageForm, {
						row
					});
				}
			},
			{
				icon: faArrowRotateLeft,
				label: 'sendInvoiceToWaitingForApproval',
				role: 'sendInvoiceToWaitingForApproval',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (row) =>
					row.status === 'waitingForApproval'
						? 'errorMsg.youCanNotUseThisStatus'
						: row.status === 'approved'
							? 'errorMsg.youCanNotUseThisStatus'
							: undefined,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'sendInvoiceToWaitingForApproval',
						subTitle: 'sendInvoiceToWaitingForApprovalConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PRICES.SEND_INVOICE_TO_WAITING_APPROVAL, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faArrowRotateLeft,
				label: 'sendInvoiceToApproved',
				role: 'sendInvoiceToApproved',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (row) =>
					row.status !== 'waitingForApproval' ? 'errorMsg.onlyWorkingForWaitingForApproval' : undefined,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'sendInvoiceToApproved',
						subTitle: 'sendInvoiceToApprovedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PRICES.SEND_INVOICE_TO_APPROVED, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faArrowRotateLeft,
				label: 'sendInvoiceBackToDraft',
				role: 'sendInvoiceBackToDraft',
				group: 'tools',
				requiredFields: ['id', 'status'],
				disabled: (row) =>
					row.status !== 'waitingForApproval' ? 'errorMsg.onlyWorkingForWaitingForApproval' : undefined,
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'sendInvoiceBackToDraft',
						subTitle: 'sendInvoiceBackToDraftConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PRICES.SEND_INVOICE_BACK_TO_DRAFT, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteNewRecord',
				role: 'deleteNewRecord',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteNewRecord',
						subTitle: 'deleteNewRecordConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PRICES.DELETE_INVOICE_TRANSACTION, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									showError({
										message: error.response?.data.code || error.response?.data.message
									});
									AppLogger.error(error);
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteInvoice',
				role: 'deleteInvoice',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id', 'status'],
				disabled: (row) => (row.status !== 'draftInvoice' ? 'errorMsg.onlyWorkingForDraftInvoice' : undefined),
				action: async (row) => {
					let invoiceTotalRecords = 0;
					await axiosPrivate
						.post(SHIPPING_PRICES.GET_INVOICE_INFO_TO_BE_DELETED, {
							id: row.id
						})
						.then((response) => {
							if (response.status === 200) {
								invoiceTotalRecords = response.data.totalRecordsToSendToPending;
							} else {
								AppLogger.error(response);
							}
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						})
						.finally(() => {
							NiceModal.show(ConfirmationModal, {
								title: 'deleteInvoice',
								subTitle: t('deleteInvoiceConfirmationSubtitle', {
									invoiceTotalRecords
								}),
								yesAction: async () => {
									dispatch(setLoading(true));

									await axiosPrivate
										.post(SHIPPING_PRICES.DELETE_INVOICE, {
											id: row.id
										})
										.then((response) => {
											if (response?.status === 200) {
												showSuccess();
											} else {
												AppLogger.error(response);
											}
										})
										.catch((error) => {
											AppLogger.error(error);
											showError();
										})
										.finally(() => {
											dispatch(updateDynamicListsInPage());
										});

									dispatch(setLoading(false));
								}
							});
						});
				}
			},
			{
				icon: faEye,
				label: 'recordHistory',
				role: 'recordHistory',
				group: 'tools',
				requiredFields: ['invoiceID', 'shipmentID'],
				disabled: (selectedOrders) => handleSelectOnlyOneItem(selectedOrders),
				action: (selectedOrders) =>
					NiceModal.show(RecordHistory, {
						invoiceID: selectedOrders.invoiceID,
						shipmentID: selectedOrders.shipmentID
					})
			},
			{
				icon: faSend,
				label: 'sendToKafka',
				role: 'sendToKafka',
				group: 'tools',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'sendToKafkaForNotInvoicedOrders',
						subTitle: 'confirmation.sendToKafkaForNotInvoicedOrders',
						yesAction: async () => {
							dispatch(setLoading(true));

							try {
								const response = await axiosPrivate.post(
									SHIPPING_PRICES.SEND_NOT_INVOICED_ORDERS_TO_KAFKA,
									{
										otoOrderIDs: Array.isArray(selectedOrders)
											? selectedOrders.map((order) => order.ID)
											: [selectedOrders.ID]
									}
								);
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faListCheck,
				label: 'paidCODDetails',
				role: 'oldPaidCODDetails',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				disabled: (selectedOrders) => handleSelectOnlyOneItem(selectedOrders),
				action: (selectedOrder) => {
					NiceModal.show(PaidCODDetails, { ID: selectedOrder.ID });
				}
			},
			{
				icon: faEye,
				label: 'viewDetails',
				role: 'paidCODDetails',
				group: 'tools',
				headerItem: true,
				requiredFields: ['codReportId'],
				disabled: (selectedOrders) =>
					handleSelectOnlyOneItem(selectedOrders) || selectedOrders.codReportId === undefined
						? 'errorMsg.noCODReportIdOrMultipleSelected'
						: undefined,
				action: (selectedOrder) => {
					NiceModal.show(CODDetailsModal, { ID: selectedOrder.codReportId });
				}
			},
			{
				icon: faPlus,
				label: 'addPriceManually',
				role: 'addPriceManually',
				group: 'tools',
				headerItem: true,
				requiredFields: ['recordType', 'shipmentID', 'invoiceStatus', 'currency'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddPriceManuallyModal, {
						recordType: row?.recordType,
						shipmentNumber: row?.shipmentID,
						invoiceStatus: row?.invoiceStatus,
						currency: row?.currency
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deletePriceNotFoundOrder',
				role: 'deletePriceNotFoundOrder',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deletePriceNotFoundOrder',
						subTitle: 'deletePriceNotFoundOrderConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(SHIPPING_PRICES.DELETE_PRICE_NOT_FOUND_ORDERS, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faBolt,
				label: 'chargeCorrectionToClient',
				role: 'chargeCorrectionToClient',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'manuallyChargedCredit', 'orderID'],
				action: (row) => {
					const manuallyChargedCreditCheck = Array.isArray(row)
						? row.some((rows) => rows.manuallyChargedCredit > 0)
						: row.manuallyChargedCredit > 0;

					const manuallyChargedCredit = Array.isArray(row)
						? row.map((rows) => rows.manuallyChargedCredit)
						: [row.manuallyChargedCredit];

					NiceModal.show(ChargeCorrectionModal, {
						manuallyChargedCredit,
						manuallyChargedCreditCheck,
						orderNumbers: Array.isArray(row) ? row.map((rows) => rows.orderID) : [row.orderID],
						ids: Array.isArray(row) ? row.map((rows) => rows.ID) : [row.ID]
					});

					// NiceModal.show(ConfirmationModal, {
					// 	lottieName: manuallyChargedCreditCheck ? 'alert-warning-icon' : 'confirmation-modal',
					// 	title: 'chargeCorrectionToClient',
					// 	subTitle: manuallyChargedCreditCheck
					// 		? t('chargeCorrectionToClientManuallyBiggerThanZero', {
					// 				manuallyChargedCredit: Array.isArray(row)
					// 					? row.map((rows) => rows.manuallyChargedCredit)
					// 					: [row.manuallyChargedCredit]
					// 			})
					// 		: 'chargeCorrectionToClientConfirmation',
					// 	yesAction: async () => {
					// 		dispatch(setLoading(true));

					// 		await axiosPrivate
					// 			.post(PRICE_CONFIGURATIONS.CHARGE_CORRECTION, {
					// 				ids: Array.isArray(row) ? row.map((rows) => rows.ID) : [row.ID]
					// 			})
					// 			.then((response) => {
					// 				if (response?.status === 200) {
					// 					showSuccess();
					// 				} else {
					// 					AppLogger.error(response);
					// 				}
					// 			})
					// 			.catch((error) => {
					// 				AppLogger.error(error);
					// 				showError();
					// 			})
					// 			.finally(() => {
					// 				dispatch(updateReconciliation());
					// 			});

					// 		dispatch(setLoading(false));
					// 	}
					// });
				}
			},
			{
				icon: faBolt,
				label: 'chargeCorrectionFromCreditCard',
				role: 'chargeCorrectionFromCreditCard',
				group: 'tools',
				disabled: (row) =>
					handleSelectOnlyOneItem(row) ||
					(row.correctionStatus !== 'pendingUndercharge' && 'errorMsg.notAllowedForThisStatus'),
				headerItem: true,
				requiredFields: ['ID', 'manuallyChargedCredit', 'orderID', 'correctionStatus'],
				action: (row) => {
					const manuallyChargedCreditCheck = Array.isArray(row)
						? row.some((rows) => rows.manuallyChargedCredit > 0)
						: row.manuallyChargedCredit > 0;

					const manuallyChargedCredit = Array.isArray(row)
						? row.map((rows) => rows.manuallyChargedCredit)
						: [row.manuallyChargedCredit];

					NiceModal.show(ChargeCorrectionCreditCardModal, {
						manuallyChargedCredit,
						manuallyChargedCreditCheck,
						orderNumber: row.orderID,
						id: row.ID
					});
				}
			},
			{
				icon: faBoltSlash,
				label: 'dontChargeToClient',
				role: 'dontChargeToClient',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'dontChargeToClient',
						subTitle: 'dontChargeToClientConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DONT_CHARGE, {
									ids: Array.isArray(row) ? row.map((rule) => rule.ID) : [row.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editPromoCode',
				role: 'editPromoCode',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(PromoCodeForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faCopy,
				label: 'duplicatePromoCode',
				role: 'duplicatePromoCode',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(PromoCodeForm, {
						mode: 'create',
						id: row.id
					});
				}
			},
			{
				icon: faEye,
				label: 'viewPromoCode',
				role: 'viewPromoCode',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(PromoCodeForm, {
						mode: 'view',
						id: row.id
					});
				}
			},

			/* FINANCE USER ACTIONS */
			{
				icon: faPenToSquare,
				label: 'editBank',
				role: 'editBank',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(AddBankModal, {
						id: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteBank',
				role: 'deleteBank',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteBank',
						subTitle: 'deleteConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							await axiosPrivate
								.post(FINANCE.DELETE_BANK, {
									ids: Array.isArray(row) ? row.map((bank) => bank.id) : [row.id]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'approveBankTransfer',
				role: 'approveBankTransfer',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'clientName', 'clientID', 'amount', 'currency', 'status'],
				disabled: (rows) => handleSelectOnlyOneItem(rows) || rows.status !== 'pending',
				action: (selectedOrder) => {
					NiceModal.show(BankTransferModal, {
						ID: selectedOrder.ID,
						clientName: selectedOrder.clientName,
						clientID: selectedOrder.clientID,
						amount: selectedOrder.amount,
						currency: selectedOrder.currency,
						approveMode: true
					});
				}
			},
			{
				icon: faXmark,
				label: 'rejectBankTransfer',
				role: 'rejectBankTransfer',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'clientName', 'status'],
				disabled: (rows) => handleSelectOnlyOneItem(rows) || rows.status !== 'pending',
				action: (selectedOrder) => {
					NiceModal.show(BankTransferModal, {
						ID: selectedOrder.ID,
						clientName: selectedOrder.clientName,
						approveMode: false
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCODReceivable',
				role: 'editCODReceivable',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CODReceivableModal, {
						mode: 'edit',
						ID: row.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewCODReceivable',
				role: 'viewCODReceivable',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CODReceivableModal, {
						mode: 'view',
						ID: row.ID
					});
				}
			},
			{
				icon: faUpload,
				label: 'uploadPaymentSlip',
				role: 'uploadPaymentSlip',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(PaymentSlipModal, {
						ID: row.ID
					});
				}
			},
			{
				icon: faCheck,
				label: 'markReceivedCODReceivable',
				role: 'markReceivedCODReceivable',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markReceivedCODReceivableTitle',
						subTitle: 'markReceivedCODReceivableConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.RECEIVE_COD_RECEIVABLE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'reconcileCODReceivable',
				role: 'reconcileCODReceivable',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'reconcileCODReceivable',
						subTitle: 'reconcileCODReceivableConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.RECONCILE_COD_RECEIVABLE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'markReconciledCODReceivable',
				role: 'markReconciledCODReceivable',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'receiveStatus'],
				disabled: (rows) => {
					const data = Array.isArray(rows) ? rows : [rows];
					return !!data.find((row) => row.receiveStatus !== 'received');
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markReconciledCODReceivable',
						subTitle: 'markReconciledCODReceivableConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.MARK_RECONCILED_COD_RECEIVABLE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'markReceivedCODReceivableReport',
				role: 'markReceivedCODReceivableReport',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'receiveStatus'],
				disabled: (rows) => {
					const data = Array.isArray(rows) ? rows : [rows];
					return !!data.find((row) => row.receiveStatus !== 'noConflict');
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markReceivedCODReceivable',
						subTitle: 'markReceivedCODReceivableConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.MARK_RECEIVED_COD_RECEIVABLE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'markNoConflictCODReceivable',
				role: 'markNoConflictCODReceivable',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'receiveStatus'],
				disabled: (rows) => {
					const data = Array.isArray(rows) ? rows : [rows];
					return !!data.find((row) => row.receiveStatus !== 'new');
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markNoConflictCODReceivable',
						subTitle: 'markNoConflictCODReceivableConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.MARK_NO_CONFLICT_COD_RECEIVABLE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteNewCODReceivable',
				role: 'deleteNewCODReceivable',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteNewCODReceivable',
						subTitle: 'deleteNewCODReceivableConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.DELETE_COD_RECEIVABLE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editCODReportModal',
				role: 'editCODReportModal',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CODReportModal, {
						mode: 'edit',
						ID: row.ID
					});
				}
			},
			{
				icon: faEye,
				label: 'viewCODReportModal',
				role: 'viewCODReportModal',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(CODReportModal, {
						mode: 'view',
						ID: row.ID
					});
				}
			},
			{
				icon: faCircleCheck,
				label: 'activateAutomaticCODTransfer',
				role: 'activateAutoCODTransferInCashDeposit',
				group: 'tools',
				requiredFields: ['clientID', 'automaticCODTransfer'],
				footerItem: true,
				className: 'green-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return (
							selectedRows.some((row) => row.automaticCODTransfer === 'yes') && 'errorMsg.alreadyActive'
						);
					else return selectedRows?.automaticCODTransfer === 'yes' && 'errorMsg.alreadyActive';
				},
				action: (selected) => {
					axiosPrivate
						.post(FINANCE.UPDATE_CLIENT_AUTOMATIC_COD_TRANSFER, {
							IDs: Array.isArray(selected) ? selected.map((item) => item.clientID) : [selected.clientID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faXmark,
				label: 'deactivateAutomaticCODTransfer',
				role: 'deactivateAutoCODTransferInCashDeposit',
				group: 'tools',
				requiredFields: ['clientID', 'automaticCODTransfer'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return (
							selectedRows.some((row) => row.automaticCODTransfer === 'no') && 'errorMsg.alreadyInActive'
						);
					else return selectedRows?.automaticCODTransfer === 'no' && 'errorMsg.alreadyInActive';
				},
				action: (selected) => {
					axiosPrivate
						.post(FINANCE.UPDATE_CLIENT_AUTOMATIC_COD_TRANSFER, {
							IDs: Array.isArray(selected) ? selected.map((item) => item.clientID) : [selected.clientID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faEye,
				label: 'markAsWithdrawRequested',
				role: 'markAsWithdrawRequested',
				group: 'tools',
				headerItem: true,
				disabled: (row) => {
					if (Array.isArray(row)) {
						const markedItems = row.filter((i) => i?.isRequested !== 'no');
						if (markedItems.length > 0) {
							return t('errorMsg.withDrawRequested').concat(
								` (${markedItems.map((i) => i?.ID).join(', ')})`
							);
						}
					} else if (row?.isRequested !== 'no') {
						return t('errorMsg.withDrawRequested');
					}
					return false;
				},
				requiredFields: ['ID'],
				action: (selectedRows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsWithdrawRequestedConfirmTitle',
						subTitle: 'markAsWithdrawRequestedConfirmSubTitle',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.MARK_AS_WITHDRAW_REQUESTED, {
									ids: Array.isArray(selectedRows)
										? selectedRows.map((order) => order.ID)
										: [selectedRows.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response.data.code || error?.response.data.message
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faMoneyCheckDollarPen,
				label: 'payForCODReportModal',
				role: 'payForCODReportModal',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID', 'status'],
				disabled: (selectedRows) => {
					if (selectedRows.length) {
						return (
							selectedRows.some((row) => row.status === 'cashDeposited') && 'errorMsg.cod.cashDeposited'
						);
					} else {
						return selectedRows.status === 'cashDeposited' && 'errorMsg.cod.cashDeposited';
					}
				},
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'payForCODReportModal',
						subTitle: 'payForCODReportModalConfirm',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.UPDATE_COD_REPORT_CASH_DEPOSITED, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'approveCODReport',
				role: 'approveCODReport',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'approveCODReport',
						subTitle: 'approveCODReportConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.UPDATE_COD_REPORT_APPROVED, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCircleCheck,
				label: 'activateAutomaticCODTransfer',
				role: 'activateAutomaticCODTransfer',
				group: 'tools',
				requiredFields: ['clientID', 'automaticCODTransfer'],
				footerItem: true,
				className: 'green-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return (
							selectedRows.some((row) => row.automaticCODTransfer === 'yes') && 'errorMsg.alreadyActive'
						);
					else return selectedRows?.automaticCODTransfer === 'yes' && 'errorMsg.alreadyActive';
				},
				action: (selected) => {
					axiosPrivate
						.post(FINANCE.UPDATE_CLIENT_AUTOMATIC_COD_TRANSFER, {
							IDs: Array.isArray(selected) ? selected.map((item) => item.clientID) : [selected.clientID],
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faXmark,
				label: 'deactivateAutomaticCODTransfer',
				role: 'deactivateAutomaticCODTransfer',
				group: 'tools',
				requiredFields: ['clientID', 'automaticCODTransfer'],
				footerItem: true,
				className: 'red-item',
				disabled: (selectedRows) => {
					if (selectedRows.length)
						return (
							selectedRows.some((row) => row.automaticCODTransfer === 'no') && 'errorMsg.alreadyInActive'
						);
					else return selectedRows?.automaticCODTransfer === 'no' && 'errorMsg.alreadyInActive';
				},
				action: (selected) => {
					axiosPrivate
						.post(FINANCE.UPDATE_CLIENT_AUTOMATIC_COD_TRANSFER, {
							IDs: Array.isArray(selected) ? selected.map((item) => item.clientID) : [selected.clientID],
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteNewCODReport',
				role: 'deleteNewCODReport',
				group: 'tools',
				footerItem: true,
				className: 'red-item',
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteNewCODReport',
						subTitle: 'deleteNewCODReportConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.DELETE_COD_REPORT_BY_TYPE, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID],
									type: 'payable'
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faXmark,
				label: 'rejectCODReport',
				role: 'rejectCODReport',
				group: 'tools',
				headerItem: true,
				requiredFields: ['ID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'rejectCODReportModal',
						subTitle: 'rejectCODReportModalConfirm',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.UPDATE_COD_REPORT_NEW_REPORT, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.ID)
										: [selectedOrders.ID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faRotate,
				label: 'updateCODWalletTransactionStatus',
				role: 'updateCODWalletTransactionStatus',
				group: 'tools',
				headerItem: true,
				requiredFields: ['CODWalletTransactionID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'updateCODWalletTransactionStatusModal',
						subTitle: 'updateCODWalletTransactionStatusModalConfirm',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.UPDATE_COD_WALLET_TRANSACTION_STATUS, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.CODWalletTransactionID)
										: [selectedOrders.CODWalletTransactionID],
									status: 'manuallyCompleted'
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faMoneyCheckDollarPen,
				label: 'updateCODWalletTransactionDescription',
				role: 'updateCODWalletTransactionDescription',
				group: 'tools',
				headerItem: true,
				requiredFields: ['CODWalletTransactionID'],
				disabled: (row) => {
					const statusCheck = handleStatusCheck(
						row,
						'enabled',
						['pending', 'failed'],
						'errorMsg.onlyWorkingForPendingAndFailed'
					);
					return statusCheck;
				},

				action: (selectedOrders) => {
					NiceModal.show(UpdateCODStatusModal, {
						IDs: Array.isArray(selectedOrders)
							? selectedOrders.map((order) => order.CODWalletTransactionID)
							: [selectedOrders.CODWalletTransactionID],
						status: 'failed'
					});
				}
			},
			{
				icon: faRotateRight,
				label: 'fetchCODWalletTransactionBankInfos',
				role: 'fetchTransactionBankInformations',
				group: 'tools',
				headerItem: true,
				requiredFields: ['CODWalletTransactionID'],
				action: (selectedOrders) => {
					NiceModal.show(ConfirmationModal, {
						title: 'fetchCODWalletTransactionBankInfos',
						subTitle: 'fetchCODWalletTransactionBankInfosConfirm',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(FINANCE.FETCH_COD_WALLET_TRANSACTION_BANK_INFOS, {
									IDs: Array.isArray(selectedOrders)
										? selectedOrders.map((order) => order.CODWalletTransactionID)
										: [selectedOrders.CODWalletTransactionID]
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editSuccessInstruction',
				role: 'editSuccessInstruction',
				group: 'tools',
				headerItem: true,
				requiredFields: ['id'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(SuccessInstructionsForm, {
						mode: 'edit',
						ID: row.id
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteSuccessInstruction',
				role: 'deleteSuccessInstruction',
				group: 'tools',
				requiredFields: ['ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteSuccessInstruction',
						subTitle: 'deleteSuccessInstructionConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(PRICE_CONFIGURATIONS.DELETE_SUCCESS_INSTRUCTION, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'edit',
				role: 'editAutomationRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('automation-form', {
						id: row.id,
						mode: 'edit'
					});
				}
			},
			{
				icon: faEye,
				label: 'view',
				role: 'viewAutomationRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					showModal('automation-form', {
						id: row.id,
						mode: 'view'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateDescription',
				role: 'updateDescriptionZeroResultLog',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (rows) => {
					NiceModal.show(ZeroDcResultDetailedModal, {
						ids: Array.isArray(rows) ? rows.map((order) => order.id) : [rows.id]
					});
				}
			},
			{
				icon: faToggleOn,
				label: 'markAsSolved',
				role: 'markAsSolvedZeroResultLog',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsSolved',
						subTitle: 'markAsSolvedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							const ids = Array.isArray(rows) ? rows.map((order) => order.id) : [rows.id];
							await axiosPrivate
								.post(REPORT.DC.UPDATE_ZERO_DC_RESULT_LOG, {
									ids,
									status: 'solved'
								})
								.then((response) => {
									if (response?.status >= 200 && response?.status < 300) {
										showSuccess();
										dispatch(updateDynamicListsInPage());
									} else {
										AppLogger.error(response);
										showError();
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								});

							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faToggleOn,
				label: 'markAsUnsolved',
				role: 'markAsUnsolvedZeroResultLog',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsUnsolved',
						subTitle: 'markAsUnsolvedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							const ids = Array.isArray(rows) ? rows.map((order) => order.id) : [rows.id];
							await axiosPrivate
								.post(REPORT.DC.UPDATE_ZERO_DC_RESULT_LOG, {
									ids,
									status: 'unsolved'
								})
								.then((response) => {
									const { status } = response;

									if (status >= 200 && status < 300) {
										dispatch(updateDynamicListsInPage());
										showSuccess();
									} else {
										AppLogger.error(response);
										showError();
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response?.data?.code || error?.response?.data?.message
									});
								});
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faToggleOn,
				label: 'markAsOpen',
				role: 'markAsOpenZeroResultLog',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				action: (rows) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsOpen',
						subTitle: 'markAsOpenConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							const ids = Array.isArray(rows) ? rows.map((order) => order.id) : [rows.id];
							await axiosPrivate
								.post(REPORT.DC.UPDATE_ZERO_DC_RESULT_LOG, {
									ids,
									status: 'open'
								})
								.then((response) => {
									const { status } = response;

									if (status >= 200 && status < 300) {
										dispatch(updateDynamicListsInPage());
										showSuccess();
									} else {
										AppLogger.error(response);
										showError();
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error?.response?.data?.code || error?.response?.data?.message
									});
								});
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editExtraFreeShipmentCredit',
				role: 'editExtraFreeShipmentCredit',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ExtraCreditForEnterpriseForm, {
						mode: 'edit',
						id: row.ID
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editClientTransactionInvoice',
				role: 'editClientTransactionInvoice',
				group: 'tools',
				requiredFields: [
					'ID',
					'companyID',
					'invoiceNumber',
					'invoiceLink',
					'invoiceId',
					'amount',
					'transactionDate',
					'clientName',
					'currency',
					'status'
				],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(EditClientTransactionInvoiceModal, {
						transactionID: row.ID,
						companyID: row.companyID,
						invoiceLink: row.invoiceLink || '',
						invoiceNumber: row.invoiceNumber || '',
						invoiceID: row.invoiceId || '',
						clientName: row.clientName || '',
						transactionDate: row.transactionDate || '',
						amount: row.amount || 0,
						currency: row.currency || '',
						paymentStatus: row.status || ''
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editRule',
				role: 'editOtomaticRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(OtomationRuleForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'view',
				role: 'viewOtomaticRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(OtomationRuleForm, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'activateOtomationRule',
				role: 'activateOtomationRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selecteds) =>
					handleSelectOnlyOneItem(selecteds) ||
					((Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'active')
						: selecteds?.status === 'active') &&
						'errorMsg.ruleCannotBeActivated'),
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_AUTOMATION_RULE_STATUS, {
							ruleId: selectedRules.id,
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivateOtomationRule',
				role: 'deactivateOtomationRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selecteds) =>
					handleSelectOnlyOneItem(selecteds) ||
					((Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'inactive')
						: selecteds?.status === 'inactive') &&
						'errorMsg.ruleCannotBeDeactivated'),
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_AUTOMATION_RULE_STATUS, {
							ruleId: selectedRules.id,
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deleteOtomationRule',
				role: 'deleteOtomationRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.DELETE_AUTOMATION_RULE, {
							ruleId: selectedRules.id
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editDCFeasibilityRule',
				role: 'editDCFeasibilityRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DeliveryCompanyFeasibilitiesForm, {
						mode: 'edit',
						id: row.id
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'view',
				role: 'viewDCFeasibilityRule',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(DeliveryCompanyFeasibilitiesForm, {
						mode: 'view',
						id: row.id
					});
				}
			},
			{
				icon: faUserCheck,
				label: 'activateDCFeasibilityRule',
				role: 'activateDCFeasibilityRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'green-item',
				disabled: (selecteds) =>
					handleSelectOnlyOneItem(selecteds) ||
					((Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'active')
						: selecteds?.status === 'active') &&
						'errorMsg.ruleCannotBeActivated'),
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_DC_FEASIBILITY_STATUS, {
							dcFeasibilityId: selectedRules.id,
							status: 'active'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deactivateDCFeasibilityRule',
				role: 'deactivateDCFeasibilityRule',
				group: 'tools',
				requiredFields: ['id', 'status'],
				footerItem: true,
				className: 'red-item',
				disabled: (selecteds) =>
					handleSelectOnlyOneItem(selecteds) ||
					((Array.isArray(selecteds)
						? selecteds.some((rule) => rule?.status === 'inactive')
						: selecteds?.status === 'inactive') &&
						'errorMsg.ruleCannotBeDeactivated'),
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.UPDATE_DC_FEASIBILITY_STATUS, {
							dcFeasibilityId: selectedRules.id,
							status: 'inactive'
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faUserXmark,
				label: 'deleteDCFeasibilityRule',
				role: 'deleteDCFeasibilityRule',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (selectedRules) => {
					axiosPrivate
						.post(SHIPPING_AUTOMATION.DELETE_DC_FEASIBILITY_RULE, {
							dcFeasibilityId: selectedRules.id
						})
						.then(() => {
							dispatch(updateDynamicListsInPage());
							showSuccess();
						})
						.catch((error) => {
							AppLogger.error(error);
							showError();
						});
				}
			},
			{
				icon: faBackward,
				label: 'revertCreditTransaction',
				role: 'revertCreditTransaction',
				group: 'tools',
				requiredFields: ['ID', 'status', 'transactionType'],
				headerItem: true,
				disabled: (row) => {
					if (!['booked', 'canceled'].includes(row.status)) {
						return 'errorMsg.notAllowedForThisStatus';
					}
					if (row.transactionType !== 'dcFee') {
						return 'errorMsg.notAllowedForThisTransactionType';
					}
					return handleSelectOnlyOneItem(row);
				},
				action: (row) => {
					NiceModal.show(RevertCreditForm, {
						id: row.ID,
						transactionStatus: row.status
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateInvoiceStatus',
				role: 'updateInvoiceStatus',
				group: 'tools',
				requiredFields: ['ID', 'clientName', 'status'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(UpdateStatusModal, {
						id: row.ID,
						clientName: row.clientName,
						status: row.status
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'updateInvoiceCreatedInfo',
				role: 'updateInvoiceCreatedInfo',
				group: 'tools',
				requiredFields: ['ID', 'invoiceCreated'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'updateInvoiceCreatedInfo',
						subTitle: 'updateInvoiceCreatedInfoConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(FINANCE.UPDATE_INVOICE_CREATED_STATUS, {
									id: row.ID,
									invoiceCreated: row.invoiceCreated.toLowerCase() === 'no'
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'pushZatca',
				role: 'pushZatca',
				group: 'tools',
				headerItem: true,
				disabled: (row) => {
					if (handleSelectOnlyOneItem(row)) return handleSelectOnlyOneItem(row);
					if (row.provider !== 'wafeq') return 'errorMsg.notAllowedForThisProvider';
				},
				requiredFields: ['ID', 'endpointUrl', 'companyId', 'transactionId', 'paymentID', 'payload', 'provider'],
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'pushZatcaTitle',
						subTitle: 'pushZatcaConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							await axiosPrivate
								.post(FINANCE.PUSH_ZATCA, {
									ID: row.ID,
									endpointUrl: row.endpointUrl,
									companyId: row.companyId,
									transactionId: row.transactionId.toString(),
									paymentID: row.paymentID,
									payload: row.payload
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError();
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.USER_GROUP]: [
			{
				icon: faEye,
				label: 'viewUserGroup',
				role: 'viewUserGroup',
				group: 'tools',
				requiredFields: ['userGroupId'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(UserGroupForm, {
						mode: 'view',
						id: row.userGroupId
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'editUserGroup',
				role: 'editUserGroup',
				group: 'tools',
				requiredFields: ['userGroupId'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(UserGroupForm, {
						mode: 'edit',
						id: row.userGroupId
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.CLIENT]: [
			{
				icon: faBan,
				label: 'accountSuspendClient',
				role: 'accountSuspend',
				group: 'tools',
				requiredFields: ['connectedCompanyId'],
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'accountSuspendClient',
						subTitle: 'accountSuspendConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(ADMIN.CONFIGURATION.UPDATE_SUSPEND_CLIENTS, {
									companyID: row.connectedCompanyId
								});
								if (response.status === 200) {
									showSuccess();
									dispatch(updateDynamicListsInPage());
								} else {
									showError();
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faRepeat,
				label: 'moveSupportUser',
				role: 'moveSupportUser',
				group: 'tools',
				requiredFields: ['connectedCompanyId', 'clientName'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) =>
					NiceModal.show(UserSelectionModal, {
						companyID: row.connectedCompanyId,
						companyName: row.clientName
					})
			},
			{
				icon: faSend,
				label: 'send2faBackupCodes',
				role: 'send2faBackupCodes',
				group: 'tools',
				requiredFields: ['connectedCompanyId'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) =>
					NiceModal.show(Send2FACodeModal, {
						companyID: row.connectedCompanyId
					})
			},
			{
				icon: faPenToSquare,
				label: 'editClient',
				role: 'editClient',
				group: 'tools',
				requiredFields: ['ID'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(ClientForm, {
						id: row.ID,
						name: 'EditClientForm'
					});
				}
			},
			{
				icon: faCheck,
				label: 'markAsValidCRDocument',
				role: 'markAsValidCRDocument',
				group: 'tools',
				requiredFields: ['CRDocStatus', 'ID', 'connectedCompanyId'],
				footerItem: true,
				className: 'green-item',
				disabled: (row) => {
					if (Array.isArray(row)) {
						return 'errorMsg.selectOnlyOneItem';
					}
					return (
						!['UNVERIFIED', 'WAITING_VERIFICATION', 'VERIFICATION_FAILED'].includes(row.CRDocStatus) &&
						'errorMsg.notAllowedForThisStatus'
					);
				},
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsCRDocumentVerified',
						subTitle: 'markAsCRDocumentVerifiedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(COMPANY.UPDATE_CR_DOCUMENT_STATUS, {
									companyID: row.ID,
									connectedCompanyID: row.connectedCompanyId,
									status: 'VERIFIED'
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faXmark,
				label: 'markAsInvalidCRDocument',
				role: 'markAsInvalidCRDocument',
				group: 'tools',
				requiredFields: ['CRDocStatus', 'connectedCompanyId', 'ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => {
					if (Array.isArray(row)) {
						return 'errorMsg.selectOnlyOneItem';
					}
					return (
						!['VERIFIED', 'WAITING_VERIFICATION', 'VERIFICATION_FAILED'].includes(row.CRDocStatus) &&
						'errorMsg.notAllowedForThisStatus'
					);
				},
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsCRDocumentInvalid',
						subTitle: 'markAsCRDocumentInvalidConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(COMPANY.UPDATE_CR_DOCUMENT_STATUS, {
									companyID: row.ID,
									connectedCompanyID: row.connectedCompanyId,
									status: 'UNVERIFIED'
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'markAsValidFLDocument',
				role: 'markAsValidFLDocument',
				group: 'tools',
				requiredFields: ['FLDocStatus', 'ID', 'connectedCompanyId'],
				footerItem: true,
				className: 'green-item',
				disabled: (row) => {
					if (Array.isArray(row)) {
						return 'errorMsg.selectOnlyOneItem';
					}
					return (
						!['UNVERIFIED', 'WAITING_VERIFICATION', 'VERIFICATION_FAILED'].includes(row.FLDocStatus) &&
						'errorMsg.notAllowedForThisStatus'
					);
				},
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsFLDocumentVerified',
						subTitle: 'markAsFLDocumentVerifiedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(COMPANY.UPDATE_FL_DOCUMENT_STATUS, {
									companyID: row.ID,
									connectedCompanyID: row.connectedCompanyId,
									status: 'VERIFIED'
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faXmark,
				label: 'markAsInvalidFLDocument',
				role: 'markAsInvalidFLDocument',
				group: 'tools',
				requiredFields: ['FLDocStatus', 'connectedCompanyId', 'ID'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => {
					if (Array.isArray(row)) {
						return 'errorMsg.selectOnlyOneItem';
					}
					return (
						!['VERIFIED', 'WAITING_VERIFICATION', 'VERIFICATION_FAILED'].includes(row.FLDocStatus) &&
						'errorMsg.notAllowedForThisStatus'
					);
				},
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsFLDocumentInvalid',
						subTitle: 'markAsFLDocumentInvalidConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(COMPANY.UPDATE_FL_DOCUMENT_STATUS, {
									companyID: row.ID,
									connectedCompanyID: row.connectedCompanyId,
									status: 'UNVERIFIED'
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
							}
							dispatch(setLoading(false));
						}
					});
				}
			},
			{
				icon: faCheck,
				label: 'markAsRefundAllowed',
				role: 'markAsRefundAllowed',
				group: 'tools',
				requiredFields: ['connectedCompanyId'],
				footerItem: true,
				className: 'green-item',
				disabled: (row) => {
					if (Array.isArray(row)) {
						return 'errorMsg.selectOnlyOneItem';
					}
				},
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsRefundAllowedTitle',
						subTitle: 'markAsRefundAllowedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(COMPANY.UPDATE_CANCEL_REFUND_STATUS, {
									connectedCompanyIDs: [row.connectedCompanyId],
									status: 'allowed'
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
								dispatch(setLoading(false));
							}
						}
					});
				}
			},
			{
				icon: faBan,
				label: 'markAsBlacklisted',
				role: 'markAsBlacklisted',
				group: 'tools',
				requiredFields: ['connectedCompanyId'],
				footerItem: true,
				className: 'red-item',
				disabled: (row) => {
					if (Array.isArray(row)) {
						return 'errorMsg.selectOnlyOneItem';
					}
				},
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'markAsBlacklistedTitle',
						subTitle: 'markAsBlacklistedConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));
							try {
								const response = await axiosPrivate.post(COMPANY.UPDATE_CANCEL_REFUND_STATUS, {
									connectedCompanyIDs: [row.connectedCompanyId],
									status: 'blacklisted'
								});
								if (response?.status === 200) {
									showSuccess();
								} else {
									AppLogger.error(response);
									showError();
								}
							} catch (error) {
								AppLogger.error(error);
								showError();
							} finally {
								dispatch(updateDynamicListsInPage());
								dispatch(setLoading(false));
							}
						}
					});
				}
			}
		],
		[ACTION_OBJECT_TYPES.OTO_AI_CLIENT_CONFIGURATION]: [
			{
				icon: faPenToSquare,
				label: 'editQueryConfiguration',
				role: 'editQueryConfiguration',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(OTOAIClientConfigurationForm, {
						id: row.id,
						mode: 'edit'
					});
				}
			},
			{
				icon: faPenToSquare,
				label: 'viewQueryConfiguration',
				role: 'viewQueryConfiguration',
				group: 'tools',
				requiredFields: ['id'],
				headerItem: true,
				disabled: (row) => handleSelectOnlyOneItem(row),
				action: (row) => {
					NiceModal.show(OTOAIClientConfigurationForm, {
						id: row.id,
						mode: 'view'
					});
				}
			},
			{
				icon: faTrashCan,
				label: 'deleteQueryConfiguration',
				role: 'deleteQueryConfiguration',
				group: 'tools',
				requiredFields: ['id'],
				footerItem: true,
				className: 'red-item',
				action: (row) => {
					NiceModal.show(ConfirmationModal, {
						title: 'deleteQueryConfiguration',
						subTitle: 'deleteQueryConfigurationConfirmation',
						yesAction: async () => {
							dispatch(setLoading(true));

							await axiosPrivate
								.post(OTO_AI_CLIENT.DELETE_OTO_AI_SERVICE, {
									id: row.id
								})
								.then((response) => {
									if (response?.status === 200) {
										showSuccess();
									} else {
										AppLogger.error(response);
									}
								})
								.catch((error) => {
									AppLogger.error(error);
									showError({
										message: error.response?.data?.code || error.response?.data?.message
									});
								})
								.finally(() => {
									dispatch(updateDynamicListsInPage());
								});

							dispatch(setLoading(false));
						}
					});
				}
			}
		]
	};

	/**
	 * @description  Returns requested actions from library.
	 * @param actionRoles Array of action role's exp. ['cancelOrder','onHoldOrder']
	 * @param actionGroup Which action group is requested
	 * @param actionObjectType which type of action requested
	 */
	const getActions = (actionRoles, actionGroup, actionObjectType) =>
		actionsLibrary[actionObjectType].filter(
			(item) => actionRoles.includes(item.role) && item.group === actionGroup
		);

	return { getActions };
};

export default useActionsLibrary;
